// UI dictionaries (phase 1). The tvirt-v6 webui ships 33 locales with a delta/plural
// engine and per-section sidecar hashes; Imago phase 1 SIMPLIFIES to the two that
// matter — it-IT (the SOURCE language) and en-US — with a flat nested dictionary.
// The plumbing (LanguageProvider, useTranslation, the language cookie, the dropdown)
// is kept intact so more locales can be added later without touching call sites.
//
// `t(key)` returns the key itself when a string is missing, so a partial dictionary is
// always safe: the MFA self-service cards still carry their own Italian copy inline,
// while the chrome (nav, mode/user/language menus), the admin views, the profile and
// the login refusals read from here.

export const LOCALES = ['it-IT', 'en-US'] as const

export type Lang = (typeof LOCALES)[number]

// The source language: every other locale is a translation of the Italian strings.
export const SOURCE_LANG: Lang = 'it-IT'

export const isLang = (value: unknown): value is Lang =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value)

// Short codes possibly persisted before the switch to full BCP 47 tags.
const LEGACY_LANG_CODES: Record<string, Lang> = {
  it: 'it-IT',
  en: 'en-US'
}

// A persisted value → the locale it means today, or undefined when it means nothing.
export const resolvePersistedLang = (value: unknown): Lang | undefined => {
  if (isLang(value)) {
    return value
  }

  if (typeof value === 'string' && value in LEGACY_LANG_CODES) {
    return LEGACY_LANG_CODES[value]
  }

  return undefined
}

export const LANGUAGES: { code: Lang; name: string; flag: string }[] = [
  { code: 'it-IT', name: 'Italiano', flag: 'twemoji-flag-italy' },
  { code: 'en-US', name: 'English (US)', flag: 'twemoji-flag-united-states' }
]

const itIT = {
  login: {
    // The three shapes a login name can take, on every channel (Luca 2026-09-04): a bare name
    // is a user of the default realm; both qualified forms name their realm.
    usernameHelper: 'utente (realm «local»), utente@realm oppure realm\\utente',

    // The refusal of a login that owes an EXTERNAL factor the bridge reports as not
    // enrolled (`mfa_required` with `reason: externalEnrollmentRequired`, design §1.6,
    // A19): no challenge can be offered — the subject enrols on the external portal
    // first. `{name}` is the factor's label (`displayName`), `enrollmentUrl` where to enrol.
    externalEnrollmentRequired:
      'Il tuo account richiede l’MFA esterna «{name}», sulla quale non risulti registrato: registrati prima sul portale esterno, poi riprova.',
    externalEnrollmentLink: 'Vai alla registrazione',

    // The device code (round 5b, 2026-09-07 — Luca 2026-08-21: Imago is ALWAYS the broker,
    // so RFC 8628 lives on OUR OIDC surface): a user holding a code from a PBA gate, a
    // Credential Provider or the `imago` CLI enters it on the broker's verification page —
    // `/oidc/device`, SERVER-RENDERED by httpd (the provider's route), not a page of this
    // SPA — sees WHO asked (realm, username, channel, computer, when, from where) and signs
    // in as that subject through the realm's normal login. A browser never gets an
    // identity round itself (it redirects): the link is the way in for whoever holds a code.
    deviceCodeLink: 'Ho un codice dispositivo',

    // The federated step-up (a realm with `mfaExempt: false` — `mfaFromUpstream: stepUp`
    // until 2026-09-06): the same picker as the login, its own refusals.
    stepUpRejected: 'Il codice non è stato accettato.',
    stepUpFido2Failed: 'Verifica con la chiave di sicurezza annullata o non riuscita. Inserisci un codice.',

    // The page's own words (Login.tsx): the welcome and the headings, the fields and the
    // buttons of the username / password / picker / code steps, and the refusals that are
    // the client's own — a passkey or security-key ceremony that did not finish, a
    // federated landing that came back denied or expired (no problem+json to speak).
    welcome: 'Benvenuto in Trantor Imago',
    heading: 'Accedi al tuo account.',
    headingOtp: 'Inserisci il codice del secondo fattore.',
    username: 'Nome utente',
    usernamePlaceholder: 'utente, utente@realm o realm\\utente',
    continueButton: 'Continua',
    passkeyButton: 'Accedi con una passkey',
    changeUser: 'Cambia utente',
    password: 'Password',
    submit: 'Accedi',
    securityKeyButton: 'Accedi con security key',
    chooseFactor: 'Questo account ha più metodi di verifica. Scegli come continuare.',
    otpLabel: 'Codice',
    otpHelpTotp: 'Inserisci un codice TOTP o un codice di recupero.',
    otpHelpFactor: 'Inserisci il codice di «{name}».',
    verify: 'Verifica',
    forgotPasswordLink: 'Password dimenticata?',
    passkeyFailed: 'Accesso con passkey annullato o non riuscito. Inserisci il nome utente.',
    securityKeyFailed: 'Accesso con security key annullato o non riuscito. Usa la password.',
    noSecurityKey: 'Nessuna security key disponibile per questo utente. Usa la password.',
    federationExpired: 'La sessione federata è scaduta, riprova.',
    federationFailed: 'Accesso federato non riuscito.',

    // The built-in factors as the picker and the code field name them; an external
    // factor shows its configured displayName verbatim.
    factors: {
      totp: 'App di autenticazione (TOTP)',
      fido2: 'Carta o token FIDO2'
    },

    // The ONE interactive round (round 5a — A30, A32 items 3, 4, 16): a `401
    // round_required` carries the `round` — who produced it (`credential`: the realm's
    // RADIUS server asks one more credential step, A21 «un passo come la password»,
    // never a factor; `factor`: an external MFA bridge's round after the password) and
    // what it asks (`answer`: type and resend `transactionId` + `answer`; `wait`: act
    // elsewhere — approve the push, enter the code at the verification page — while the
    // page polls the transaction alone, never faster than `intervalSeconds`, until
    // accepted, refused or `expiresAt`). ONE screen, ONE state, whoever produced it;
    // `hints.actions` are the producer's shortcut buttons (a bridge's «resend»). A
    // refused answer or a rejected/expired wait is the uniform 401 (the reason in the
    // register): the login restarts from the password, the step-up from its picker.
    // RETIRED (loss said): the separate credential step (`credential_challenge`) and
    // the bridge's prompt inside the OTP field (`mfa_required.challenge.prompt`).
    round: {
      headingCredential: 'Il server di autenticazione chiede un ulteriore passo.',
      headingFactor: 'Rispondi alla richiesta dell’MFA esterna.',
      headingWait: 'Conferma la richiesta dell’MFA esterna.',
      fromServer: 'Richiesta del server',
      fromFactor: 'Richiesta di «{name}»',
      step: 'passo {round}',
      answer: 'Risposta',
      answerHelpCredential:
        'Rispondi come indicato qui sopra. Non è un secondo fattore: è un passo della verifica della password, deciso dal server del realm.',
      answerHelpFactor: 'Rispondi come indicato qui sopra: la risposta va a «{name}».',
      submit: 'Continua',
      openLink: 'Apri la pagina di verifica',
      userCode: 'Codice da inserire',
      copyCode: 'Copia il codice',
      copied: 'Copiato',
      waiting: 'In attesa della conferma…',
      waitingCadence: 'Verifichiamo ogni {seconds} s.',
      waitingDeadline: 'La richiesta scade alle {at}.',
      expired: 'La richiesta è scaduta senza una conferma: nulla è stato giudicato, nessun colpo di lockout.',
      retry: 'Riprova',
      rejectedCredential: 'La risposta non è stata accettata dal server del realm. Ripeti l’accesso dalla password.',
      rejectedFactor:
        'La richiesta non è stata accettata da «{name}» (risposta rifiutata, o round scaduto). Ripeti l’accesso dalla password.',
      rejectedStepUp:
        'La richiesta non è stata accettata da «{name}» (risposta rifiutata, o round scaduto). Scegli di nuovo come verificare.'
    }
  },

  // The login page's refusals (Login.tsx describeError) and the anonymous lost-password
  // dialog (ForgotPasswordDialog). The uniform answer of the lost-password flow is the
  // same whatever happened: revealing anything would be an enumeration oracle.
  auth: {
    errors: {
      badCredentials: 'Credenziali non valide.',
      rateLimited: 'Troppi tentativi. Riprova più tardi.',
      directoryUnavailable: 'Directory non disponibile. Riprova più tardi.',
      unavailable: 'Servizio non disponibile: la directory o l’MFA esterna non rispondono. Riprova più tardi.',

      // `503 mfa_unavailable` (phase 3): an owed external MFA that cannot be served —
      // bridge down, timeout, 5xx, or the resource disabled while a policy still owes it.
      // Fail-closed, retryable, never a credential failure, no lockout hit.
      mfaUnavailable:
        'L’MFA esterna che il tuo account deve non è servibile in questo momento ({detail}). Non è un errore di credenziali: riprova tra poco.',
      mfaRequired:
        'Il tuo account richiede un secondo fattore che non può essere verificato da qui (nessun fattore utilizzabile su questo canale, o un fattore esterno non registrato).',

      // The MFA-fatigue cap (core EXTERNAL_ROUND_BUDGET, 3 fresh factor rounds per login,
      // round 5a `roundBudgetExhausted`): the open round is still answerable.
      roundBudgetExhausted:
        'Troppi round dell’MFA esterna avviati per questo accesso: rispondi alla richiesta già aperta oppure riparti dalla password.',
      generic: 'Impossibile completare l’accesso.'
    },

    // The two anonymous recovery flows (recovery.yaml): the lost PASSWORD (start → code +
    // new password) and the lost AUTHENTICATOR (start → code; the enrollment is cleared,
    // never re-seeded). Both steps speak uniformly: the start never says whether the
    // account exists, the completion never says WHY a code was refused.
    forgot: {
      title: 'Password dimenticata',
      titleOtp: 'Autenticatore perso',
      linkOtp: 'Ho perso l’autenticatore',
      username: 'Nome utente',
      usernamePlaceholder: 'utente (realm «local»), utente@realm oppure realm\\utente',
      usernameHelp:
        'Tre forme: «utente» (realm «local»), «utente@realm» oppure «realm\\utente» — le due forme qualificate indicano il realm.',
      uniform:
        'Se l’account esiste, riceverai un’e-mail con un codice monouso: inseriscilo qui sotto insieme alla nuova password.',
      uniformOtp:
        'Se l’account esiste, riceverai un’e-mail con un codice monouso: inseriscilo qui sotto per rimuovere l’autenticatore.',
      otpConsequence:
        'L’autenticatore attuale viene rimosso: al prossimo accesso ti verrà chiesto di registrarne uno nuovo. Se hai ancora un codice di recupero, usalo al posto del secondo fattore invece di questa procedura.',
      haveCode: 'Ho già un codice',
      code: 'Codice ricevuto',
      codePlaceholder: 'timrc_…',
      codeHelp: 'Il codice monouso ricevuto via e-mail: vale pochi minuti e un solo tentativo, giusto o sbagliato.',
      newPassword: 'Nuova password',
      confirmPassword: 'Conferma nuova password',
      mismatch: 'Le password non coincidono.',
      complete: 'Conferma',
      donePassword: 'Password reimpostata. Ora puoi accedere con la nuova password.',
      doneOtp: 'Autenticatore rimosso. Al prossimo accesso registrerai un nuovo secondo fattore.',
      codeRejected: 'Codice non valido, scaduto o già usato. Richiedi un nuovo codice.',
      completeError: 'Impossibile completare il recupero.',
      back: 'Indietro',
      close: 'Chiudi',
      submit: 'Invia'
    }
  },
  nav: {
    home: 'Home',
    profile: 'Profilo',
    connectors: 'Connettori',
    connectorsServices: 'Servizi',
    connectorsApplications: 'Applicazioni',
    connectorsDot1x: '802.1X',
    realms: 'Realm',
    mfa: 'MFA',
    legacy: 'Imago 4Legacy',
    biocards: 'Imago BioCard',
    computers: 'Computer',
    system: 'Sistema',
    policy: 'Policy',
    audit: 'Audit',
    events: 'Eventi di sistema',
    localUsers: 'Utenti locali',
    localGroups: 'Gruppi locali',
    enumeration: 'Enumerazione',
    fidoMetadata: 'FIDO Metadata Service',
    rbacGrants: 'Ruoli e grant',
    duress: 'Anti-coercizione',
    node: 'Nodo',
    about: 'Informazioni'
  },
  sectionSummary: {
    home: 'Il tuo account e le azioni rapide',
    profile: 'Le tue credenziali e i tuoi fattori di autenticazione',
    connectors: 'Servizi, applicazioni e 802.1X esposti agli utenti',
    realms: 'Realm nativi, directory LDAP/AD, IdP federati e server RADIUS',
    mfa: 'Policy di attuazione MFA e fattori esterni',
    legacy: 'App legacy dietro il proxy: login Imago, password effimera, vhost nginx generati',
    biocards: 'Gestione delle smart card biometriche',
    computers: 'I computer con PBA e Credential Provider: registrazione, abilitazioni, abbinamenti',
    policy: 'Regole di accesso e di sicurezza',
    audit: 'Il registro completo: accessi, fattori, recuperi e modifiche di configurazione, su ogni canale',
    events: 'Ciò che il sistema e gli amministratori hanno fatto: configurazione, apply, sincronizzazioni, probe',
    localUsers: 'Gli utenti propri di Imago nel realm nativo «local»',
    localGroups: 'I gruppi degli utenti locali, riconosciuti da grant, policy MFA e regole VLAN',
    enumeration:
      'La sorgente di enumerazione di ogni realm e il censimento che ne deriva: soggetti e gruppi con id stabili',
    fidoMetadata: 'I modelli di autenticatore FIDO noti al server (il BLOB del FIDO Metadata Service e gli statement locali), con stato e livello di certificazione',
    rbacGrants:
      'Chi amministra Imago: ruoli (di sistema e personalizzati) e grant a gruppi o utenti dei realm, con bersaglio e delega',
    duress: 'Suffisso segreto da digitare sotto coercizione: login normale, allarme silenzioso',
    node: 'La rete di gestione, il mirror degli aggiornamenti e il frontend 802.1X di questo nodo',
    about: 'Versione, nodo e controlli di salute'
  },
  common: {
    contractError: 'Risposta del server non conforme al contratto: {field} = {value}',
    cancel: 'Annulla',
    create: 'Crea',
    save: 'Salva',
    delete: 'Elimina',
    all: 'Tutti',
    previous: 'Precedente',
    next: 'Successivo',
    actions: 'Azioni',
    close: 'Chiudi',
    sudoPassword: 'Conferma con la tua password',
    remove: 'Rimuovi',
    sudoWhy: 'Ogni scrittura è confermata con la tua password e registrata in audit a tuo nome.',
    actionFailed: 'Operazione non riuscita.',
    howItWorks: 'Come funziona',

    // The problems the contract names by code, framed in the operator's language
    // (utils/apiError describeProblem); the server's own detail rides verbatim.
    problems: {
      immutableField: 'Il campo «{field}» è immutabile: non si modifica dopo la creazione.',
      validation: 'Valore non accettato — {fields}',
      validationField: '{field}: {message}',
      inUseCount: 'Eliminazione rifiutata: l’oggetto è ancora in uso ({count} riferimenti). {detail}',
      inUse: 'Eliminazione rifiutata: l’oggetto è ancora in uso. {detail}'
    }
  },

  // The chip-list input (ChipsField): the gesture it explains when the caller gives no
  // helper of its own. The comma is an ordinary character (a group DN carries several).
  chips: {
    hint: 'Invio per aggiungere una voce; incollando più righe se ne aggiunge una per riga.'
  },
  noPermission: {
    title: 'Permesso mancante',
    body: 'La tua sessione non ha il permesso per vedere questa pagina. I ruoli si risolvono al login: se ti è stato assegnato un ruolo da poco, esci e rientra.',
    required: 'Permesso richiesto: {permission}'
  },

  // Sistema > Audit (AuditView): the register, its filters and the human names of the
  // closed catalogs — channels, events and outcomes come from the api-js enums (the
  // contract's), the target kinds and the detail reasons from the writers in httpd.
  audit: {
    subheader:
      'Il registro di audit di Trantor Imago: accessi, MFA, registrazioni dei fattori, recuperi e modifiche di configurazione, su ogni canale.',
    filterChannel: 'Canale',
    filterEvent: 'Evento',
    filterOutcome: 'Esito',
    filterTargetKind: 'Oggetto',
    filterUsername: 'Utente',
    filterUsernamePlaceholder: 'Cerca nel nome utente…',
    filterRealm: 'Realm',
    filterRealmPlaceholder: 'Cerca nel nome del realm…',
    filterActor: 'Amministratore (attore)',
    filterActorPlaceholder: 'Cerca nel nome dell’amministratore…',
    filterSince: 'Dal',
    filterUntil: 'Al',
    filterRangeHelp: 'Ora locale del browser.',
    advanced: 'Filtri avanzati',
    filterExternalUuid: 'UUID esterno',
    filterExternalUuidPlaceholder: 'objectGUID / entryUUID esatto',
    filterClientIp: 'IP client',
    filterClientIpPlaceholder: 'Indirizzo esatto, es. 10.0.0.7',
    filterNode: 'Nodo',
    filterNodePlaceholder: 'Nome esatto del nodo',
    filterActorRealm: 'Realm dell’amministratore',
    filterActorRealmPlaceholder: 'Nome esatto del realm',
    filterTargetId: 'ID oggetto',
    filterTargetIdPlaceholder: 'Identificativo esatto dell’oggetto',
    filterTargetName: 'Nome oggetto',
    filterTargetNamePlaceholder: 'Cerca nel nome dell’oggetto…',
    colAt: 'Data e ora',
    colChannel: 'Canale',
    colEvent: 'Evento',
    colOutcome: 'Esito',
    colUsername: 'Utente',
    colRealm: 'Realm',
    colIp: 'IP',
    empty: 'Nessun record di audit.',
    emptyFiltered: 'Nessun record di audit per i filtri selezionati.',
    loadError: 'Impossibile caricare il registro di audit.',
    node: 'Nodo',
    actor: 'Attore',
    sudo: 'sudo',
    target: 'Oggetto',
    reason: 'Motivo',
    declaredRealm: 'realm dichiarato: «{realm}»',
    revokedCount: '{count} sessioni revocate',
    detail: 'Dettaglio',
    channels: {
      portal: 'Portale (API nativa)',
      oidc: 'OIDC',
      saml: 'SAML',
      pi: 'privacyIDEA',
      radius: 'RADIUS',
      ldap: 'LDAP',
      proxy: 'Proxy identity-aware',
      pba: 'PBA (gate pre-boot)',
      cp: 'Credential Provider (Windows)',
      eap: '802.1X (EAP)',
      system: 'Sistema',
      scim: 'SCIM (export delle app)'
    },
    events: {
      login: 'Accesso riuscito',
      loginFailed: 'Accesso rifiutato',
      mfaChallenge: 'Secondo fattore richiesto',
      mfaAccepted: 'Secondo fattore verificato',
      mfaFailed: 'Secondo fattore rifiutato',
      lockoutTripped: 'Lockout scattato',
      sudoElevated: 'Elevazione sudo concessa',
      sudoFailed: 'Elevazione sudo rifiutata',
      sessionRevoked: 'Sessione revocata',
      enrollmentStarted: 'Registrazione del fattore avviata',
      enrollmentConfirmed: 'Registrazione del fattore confermata',
      enrollmentCleared: 'Registrazione del fattore rimossa',
      recoveryStarted: 'Recupero avviato',
      recoveryCompleted: 'Recupero completato',
      recoveryFailed: 'Recupero rifiutato',
      passwordChanged: 'Password cambiata dall’utente',
      passwordResetPerformed: 'Password impostata per l’utente (recupero o reset amministrativo)',
      configCreated: 'Configurazione creata',
      configUpdated: 'Configurazione modificata',
      configDeleted: 'Configurazione eliminata',
      externalMfaProbe: 'Salute dell’MFA esterna cambiata',
      computerActivated: 'Computer attivato',
      eapCaInitialized: 'CA di Imago inizializzata',
      eapCertIssued: 'Certificato EAP-TLS emesso',
      eapCertRevoked: 'Certificato EAP-TLS revocato',

      // Imago 4Legacy (2026-09-09): the profile of a legacy app and its generated ephemeral
      // consumer, the nginx site the driver wrote (or refused), the proxy session the
      // callback minted on the app host, the ephemeral credential refused on the verify.
      legacyAppCreated: 'App legacy creata (col suo consumer effimero)',
      legacyAppUpdated: 'App legacy modificata',
      legacyAppDeleted: 'App legacy eliminata (col consumer, le sessioni proxy e le credenziali)',
      legacyNginxApplied: 'Sito nginx 4Legacy scritto (e ricaricato in modo apply)',
      legacyNginxFailed: 'Sito nginx 4Legacy rifiutato (file precedenti ripristinati)',
      legacyLogin: 'Accesso a un’app legacy (sessione proxy coniata)',
      legacyCredentialRefused: 'Credenziale effimera rifiutata sul verify effimero (LDAP o RADIUS)',

      // The node configuration channel (design docs/design-node-channel-2026-11-04.md §3:
      // a verb of the root helper — network, network-confirm, update-mirror, update-check,
      // eapd — succeeded, or was refused/failed with the helper's own words) and the two
      // maintenance sweeps of 2026-11-04 (audit retention, abandoned TOTP enrollments).
      nodeApplied: 'Configurazione del nodo applicata (rete, mirror, 802.1X)',
      nodeApplyFailed: 'Configurazione del nodo rifiutata o fallita (le parole dell’helper nel dettaglio)',
      auditSwept: 'Registro di audit ripulito (retention)',
      totpEnrollmentExpired: 'Registrazione TOTP mai confermata scaduta ed eliminata',
      enrollmentRefused: 'Registrazione di un fattore rifiutata: rete non ammessa (policy enrollment.allowedCidrs)',
      externalMfaEnrollmentStarted: 'Registrazione su un MFA esterno avviata dal profilo (il bridge ha risposto)',

      // The device code of a federated realm on the Imago broker (round 5b, 2026-09-07):
      // the identity round opened (the code minted — never written), the approval on the
      // verification page by the subject the device asked for, the page's refusals
      // (wrong, expired or spent code, throttle, another subject, «non sono io»), the
      // round past its deadline before an approval (met on the device's poll).
      deviceCodeStarted: 'Codice dispositivo emesso (round identity aperto)',
      deviceCodeVerified: 'Codice dispositivo approvato dal soggetto',
      deviceCodeRefused: 'Codice dispositivo rifiutato sulla pagina di verifica',
      deviceCodeExpired: 'Codice dispositivo scaduto senza approvazione',
      windowsCodeIssued: 'Codice monouso per Windows emesso',
      duressLogin: 'Accesso sotto coercizione (allarme)',
      samlAssertionIssued: 'Asserzione SAML emessa al service provider',
      backchannelRequested: 'Richiesta di accesso backchannel (CIBA) ricevuta',
      backchannelApproved: 'Richiesta backchannel approvata dall’utente',
      backchannelDenied: 'Richiesta backchannel rifiutata dall’utente',
      enumerationSynced: 'Enumerazione sincronizzata (censimento promosso)',
      enumerationSyncFailed: 'Enumerazione fallita',
      scimTokenCreated: 'Token SCIM creato',
      scimTokenRevoked: 'Token SCIM revocato',
      scimAccessDenied: 'Accesso SCIM rifiutato',
      subjectLinked: 'Identità legata al soggetto stabile',
      fidoMetadataRefreshed: 'Metadata FIDO aggiornati',
      fidoMetadataRefreshFailed: 'Aggiornamento metadata FIDO fallito'
    },
    outcomes: {
      succeeded: 'Riuscito',
      denied: 'Negato',
      rejected: 'Rifiutato',
      failed: 'Fallito',
      accepted: 'Accettato'
    },

    // The facts a `detail` carries beside its reason since the named MFA policies
    // (phase 2, design §1.5) and the external MFA resource (phase 3, §1.6): the owed
    // factors still missing and the policies enforcing them (mfaObligedUnenrolled), the
    // owed factors and the channel that cannot carry the held ones
    // (noOwedFactorOnChannel), the external MFA not enrolled on (externalEnrollmentRequired:
    // name, label, where to enrol) or not served (externalUnavailable: name, detail), the
    // method and round of an external challenge — and, on an accepted login, the factor
    // that verified.
    missingFactors: 'Fattori mancanti: {factors}',
    viaPolicies: 'Policy: {policies}',
    owedFactors: 'Fattori dovuti: {factors}',
    onChannel: 'Canale: {channel}',
    externalMfa: 'MFA esterna: {name}',
    externalDisplayName: 'etichetta: «{name}»',
    enrollmentUrl: 'registrazione: {url}',
    externalDetail: 'dettaglio: {detail}',
    challengeMethod: 'Metodo',
    challengeRound: 'giro {round}',
    secondFactor: 'Secondo fattore',
    mfaNone: 'nessuno (sola password: nulla di dovuto)',
    mfaPending: 'in sospeso: la sessione è al gate di registrazione',

    // The facts of the ONE interactive round (round 5a): every line about a round says
    // its `producer` (credential: the realm's RADIUS server; factor: the external MFA
    // bridge; identity: the device-code, reserved) and its `kind` (answer / wait), the
    // round number, the method of a factor round, the ROUND's own transaction (the
    // prefix of the Idempotency-Key the bridge saw — it correlates the register with
    // the bridge's log), the budget met (fresh factor rounds opened, credential steps
    // taken), the channel that could not carry it (roundNotCarried) and, on a
    // `methodNotOffered` with `roundRequired`, the code sent for a method whose round
    // was never opened. `credentialRounds` on a `login`/`badCredentials` line of a
    // `radius` realm: how many Access-Challenge steps preceded the Accept/Reject.
    roundLabel: 'Round interattivo',
    roundProducer: 'produttore: {producer}',
    roundKind: 'genere: {kind}',
    roundNumber: 'round {round}',
    roundTransaction: 'transazione del round: {id}',
    roundOpened: 'round freschi aperti: {opened} su {budget}',
    roundCredentialBudget: 'passi di credenziale: {rounds} su {budget}',
    roundRequiredHint:
      'codice inviato per un metodo esterno senza round aperto: la prova raggiunge il bridge solo attraverso il suo round',
    producers: {
      credential: 'credenziale (il server RADIUS del realm)',
      factor: 'fattore (il bridge MFA esterno)',
      identity: 'identità (il device-code del broker Imago, realm federato)'
    },
    kinds: {
      answer: 'answer (risposta digitata)',
      wait: 'wait (conferma altrove, poll)'
    },
    credentialRounds: 'Passi di credenziale (RADIUS): {rounds}',
    realmKind: 'tipo di realm: {kind}',
    refusalError: 'dettaglio: {error}',

    // The DEVICE-CODE facts (round 5b, 2026-09-07): every `deviceCode*` line names the
    // subject the device asked for (`requested`, `user@realm`; on the `deviceCodeStarted`
    // line the row's own username/realm), the device's channel and computer (the page's
    // lines are on channel `oidc` — the device's channel rides in `detail`), the deadline
    // of the code (`expiresAt`), the round's transaction; a `deviceCodeVerified` says
    // WHO approved (`subject`) and HOW (`approvedBy`: the login completed on the page, or
    // the IdP session already authenticated as that subject); a `subjectMismatch` refusal
    // names the stranger; a `throttled` refusal says which window tripped (5/min per IP
    // and per code) and the wait. The user code itself is NEVER in the register. On a
    // `login` line that followed an identity round, `identity: deviceCode`.
    deviceCode: 'Codice dispositivo',
    deviceRequested: 'soggetto chiesto: {subject}',
    deviceApprovedAs: 'soggetto che ha risposto: {subject}',
    deviceApprovedBy: 'approvazione: {how}',
    deviceApprovals: {
      login: 'login completato sulla pagina di verifica',
      session: 'sessione IdP già autenticata come il soggetto (nessun login chiesto)'
    },
    deviceChannel: 'canale del dispositivo: {channel}',
    deviceComputer: 'computer: {computer}',
    deviceExpiresAt: 'scadenza del codice: {at}',
    deviceThrottlePerIp: 'finestra per IP esaurita',
    deviceThrottlePerCode: 'finestra per codice esaurita',
    deviceRetryAfter: 'riprova tra {seconds} s',
    identityLabel: 'Identità',
    identities: {
      deviceCode: 'device code approvato sulla pagina di verifica del broker (round identity)'
    },

    // The HEALTH facts of an external MFA (A32 item 2): the `externalMfaProbe` line —
    // written only when the health state changes — carries the transition, its trigger,
    // the probe's verdict and the last ok; an `externalUnavailable` refusal says when the
    // bridge was stale (the effective disable); a configuration line says when the
    // observations were cleared, an immediate probe scheduled, a verdict discarded.
    healthTransition: 'Salute',
    probeTrigger: 'innesco: {trigger}',
    probeVerdict: 'verdetto del probe: {probe}',
    lastOkAt: 'ultimo probe ok: {at}',
    lastOkNever: 'ultimo probe ok: mai',
    staleBridge: 'bridge stantio: disabilitato a livello effettivo (la configurazione dice ancora «abilitato»)',
    observationsCleared: 'capacità e ultimo probe azzerati (endpoint, segreto o timeout cambiati)',
    probeScheduled: 'probe immediato programmato',
    probeDiscarded: 'verdetto scartato: {reason}',

    // The per-METHOD reset of A33 (2026-09-07): an `enrollmentCleared` line written by
    // the domain carries the subject, the method as the request named it (`totp`,
    // `fido2:<credentialId>`, `all`), what went (`cleared {totp, fido2[]}` — the Biocard
    // serial of each credential registered against a card, whose inventory row stays)
    // and whether the §5.1 e-mail reached the mailer. The lens' refusals of the same
    // act carry none of these facts (their `method` is the HTTP verb).
    factorReset: 'Azzeramento',
    factorResetSubject: 'soggetto: {subject}',
    factorResetTotp: 'TOTP rimosso (segreto e codici di recupero)',
    factorResetFido2: '{count} credenziali FIDO2 rimosse',
    factorResetBiocards: 'BioCard S/N {serials} (l’inventario resta)',
    factorResetNothing: 'nulla rimosso',
    factorResetNotified: 'e-mail di notifica inviata',
    factorResetNotNotified: 'nessuna e-mail inviata',
    factorMethods: {
      totp: 'TOTP',
      all: 'tutti i metodi',
      fido2: 'credenziale FIDO2',
      external: 'MFA esterna'
    },

    // The Imago 4Legacy facts (2026-09-09): on every `legacy*` line the app (or the
    // apps a site apply rendered), the login mode and the credential the callback
    // minted (session-bound, one-shot, none), the engine that ran and whether its
    // replay held or fell back, the generated consumer, the fields a patch carried,
    // the nginx driver's mode, directory and files (and the reload), the stderr it
    // refused with; on the `sessionRevoked` of a portal logout the proxy sessions the
    // single logout ended and the apps they were on.
    legacyLabel: 'Imago 4Legacy',
    legacyApp: 'app: {app}',
    legacyMode: 'modo di login: {mode}',
    legacyCredential: 'credenziale: {credential}',
    legacyEngine: 'motore: {engine}',
    legacyReplay: 'replay del form: {replay}',
    legacyFallback: 'ripiego: {fallback}',
    legacyConsumer: 'consumer effimero: {consumer}',
    legacyFields: 'campi modificati: {fields}',
    legacyNginxMode: 'modo del driver nginx: {mode}',
    legacyNginxDir: 'directory: {dir}',
    legacyNginxFiles: 'file: {files}',
    legacyNginxApps: 'app rese: {apps}',
    legacyNginxReloaded: 'nginx validato e ricaricato',
    legacyNginxStderr: 'stderr di nginx',
    legacyProxySessions: '{count} sessioni proxy chiuse',
    legacyLogoutApps: 'app legacy: {apps}',
    legacyModes: {
      formReplay: 'replay del form (lato server)',
      injectPrefill: 'prefill iniettato (script nella pagina di login)',
      httpBasic: 'HTTP Basic',
      trustedHeader: 'trusted header (nessuna credenziale)'
    },
    legacyCredentials: {
      session: 'legata alla sessione proxy',
      oneShot: 'monouso',
      none: 'nessuna'
    },
    legacyReplays: {
      ok: 'riuscito',
      failed: 'fallito'
    },
    healthStates: {
      ok: 'in salute',
      stale: 'stantio',
      unreachable: 'non raggiungibile',
      unauthorized: 'segreto rifiutato',
      invalid: 'risposta non valida',
      unprobed: 'mai provato'
    },
    probeTriggers: {
      periodic: 'probe periodico',
      immediate: 'probe immediato (creazione, modifica o riabilitazione)',
      manual: 'probe manuale',
      clock: 'orologio: finestra di stantiezza chiusa'
    },
    probeOutcomes: {
      ok: 'risponde',
      unauthorized: 'segreto rifiutato',
      unreachable: 'non raggiungibile',
      invalid: 'risposta non valida'
    },
    targetKinds: {
      realm: 'Realm',
      localUser: 'Utente locale',
      localGroup: 'Gruppo locale',
      policy: 'Policy',
      roleMapping: 'Mappatura di ruolo (storica)',
      rbacGrant: 'Grant di ruolo',
      rbacRole: 'Ruolo',
      app: 'Applicazione',
      computer: 'Computer',
      computerPairing: 'Abbinamento computer',
      enrollment: 'Registrazione MFA',
      frontend: 'Servizio',
      biocard: 'BioCard',
      session: 'Sessione',
      eapCa: 'CA di Imago',
      eapCert: 'Certificato EAP-TLS',
      mfaPolicy: 'Policy MFA',
      externalMfa: 'MFA esterna',
      legacyApp: 'App legacy (Imago 4Legacy)'
    },

    // The reasons whose code means something ELSE on one event than in the shared
    // catalog below (the view asks audit.eventReasons.<event>.<code> first): the
    // refusals of an ephemeral credential on the verify (Imago 4Legacy, D9), the
    // failures of the nginx driver (D6).
    eventReasons: {
      legacyCredentialRefused: {
        sourceNotEphemeral:
          'Credenziale effimera presentata da un indirizzo che non è di nessun consumer effimero: rifiutata senza passare dalla catena dei realm (nessun colpo di lockout)',
        audienceMismatch: 'Credenziale coniata per un’altra app legacy (audience diversa dal consumer chiamante)',
        expired: 'Credenziale effimera scaduta (oltre il TTL)',
        consumed: 'Credenziale monouso già spesa',
        usernameMismatch: 'Bind con uno username diverso da quello per cui la credenziale è stata coniata',
        notEphemeralCredential:
          'Password reale presentata a un consumer effimero: mai verificata sulla directory (solo la credenziale effimera è ammessa)',
        disabled: 'App legacy disabilitata: ogni credenziale è rifiutata'
      },
      legacyNginxFailed: {
        nginxTestFailed: '`nginx -t` ha rifiutato il sito reso (stderr sotto)',
        nginxReloadFailed: '`nginx -s reload` non è riuscito (stderr sotto)',
        writeFailed: 'Scrittura dei file nella directory nginx non riuscita',
        renderFailed: 'Un profilo salvato non si rende più (riconciliazione all’avvio)'
      }
    },
    reasons: {
      badCredentials: 'Credenziali errate',
      rateLimited: 'Limite di tentativi superato (throttling)',
      unresolvedSubject: 'Soggetto non risolto: il nome utente non identifica nessun utente nel realm',
      unknownSubject: 'Soggetto sconosciuto',
      unknownRealm: 'Realm sconosciuto',
      realmMissing: 'Realm mancante: il canale richiede un realm esplicito',
      realmKindRefused: 'Tipo di realm non ammesso su questo canale',
      federatedRealm: 'Realm federato: Imago non custodisce la password',
      mfaRequired: 'Secondo fattore richiesto',
      mfaRejected: 'Secondo fattore rifiutato',
      mfaObligedUnenrolled: 'MFA dovuta ma un fattore dovuto non è registrato (canale senza gate di registrazione)',
      noOwedFactorOnChannel: 'I fattori posseduti non viaggiano su questo canale mentre l’MFA è dovuta',
      externalEnrollmentRequired: 'MFA esterna dovuta ma non registrata sul bridge',
      externalUnavailable:
        'MFA esterna dovuta ma non servibile (bridge giù, timeout, 5xx o risorsa disabilitata): rifiuto chiuso, nessun colpo di lockout',

      // Round 5a: the three reasons of the ONE interactive round (they fold the retired
      // credentialChallengeNotCarried, credentialRoundBudgetExhausted,
      // externalRoundBudgetExhausted and externalRoundExpired; externalRoundNotOpened is
      // now methodNotOffered with `roundRequired`).
      roundNotCarried:
        'Round interattivo aperto su un canale che non può portarlo: rifiuto, nulla giudicato, nessun colpo di lockout',
      roundBudgetExhausted:
        'Budget dei round di questo accesso esaurito (3 round freschi del fattore, 5 passi di credenziale): rifiuto, nessun colpo di lockout, il round aperto resta rispondibile',
      roundExpired:
        'Round chiuso senza giudizio: la sessione del bridge è scaduta o il round wait ha superato la scadenza; nessun colpo di lockout',
      methodNotOffered: 'Metodo scelto non tra quelli offerti',
      stepUpChallengeUnknownOrReplayed: 'Challenge FIDO2 dello step-up sconosciuta o già spesa',
      notEnrolled: 'Nessuna registrazione',
      stepUpRejected: 'Step-up rifiutato',
      lockedOut: 'Account bloccato (soglia di lockout raggiunta)',
      disabled: 'Utente o realm disabilitato',
      revoked: 'Revocato',
      mustChangePassword: 'Cambio password obbligatorio in sospeso',
      mustChangeForced: 'Cambio password imposto dall’amministratore: sessioni revocate',
      passwordReset: 'Password reimpostata dall’amministratore: sessioni revocate',
      passwordRecovered: 'Password recuperata dall’utente: sessioni revocate',
      localUserDisabled: 'Utente locale disabilitato: sessioni revocate',
      localUserDeleted: 'Utente locale eliminato: sessioni revocate',
      adSsoRequired: 'Logon Windows non configurato sul realm (dominio AD assente)',
      vscNotProvisioned: 'Virtual Smart Card non provisionata',
      notInInventory: 'Card non presente nell’inventario BioCard',
      assignedToOther: 'Card già assegnata a un altro soggetto',
      pairingDenied: 'Abbinamento negato',
      noStableId: 'Nessun identificativo stabile (UUID) per il soggetto',
      samlAcsFailed: 'Response SAML rifiutata sull’ACS',
      federationCallbackFailed: 'Callback di federazione non riuscito',
      policyRefused: 'Rifiutato dalla policy',
      directoryUnavailable: 'Directory non raggiungibile',

      // Round 5b (2026-09-07): the reasons of a `deviceCodeRefused` line of the broker's
      // verification page (channel `oidc`) — a code nobody minted, a code past its 300 s,
      // a code already approved or burned, the 5/min throttle per IP and per code, a login
      // as ANOTHER subject than the one the device asked for (the code is burned), the
      // user's own «non sono io» — and, on a `loginFailed` line met on the device's poll,
      // the denial (the uniform refusal, no lockout hit) and a broker that could not serve
      // the round. RETIRED with them (loss said): `federatedLoginNotOnDevice` — a federated
      // realm logs on to a device with the identity round at the place of the password.
      wrongCode: 'Codice dispositivo sconosciuto (mai emesso, o digitato male)',
      expired: 'Codice dispositivo scaduto (300 s dall’emissione)',
      alreadyUsed: 'Codice dispositivo già speso (approvato o bruciato)',
      throttled: 'Troppi tentativi sul codice dispositivo (5 al minuto per IP e per codice)',
      subjectMismatch:
        'Login sulla pagina di verifica come un soggetto diverso da quello chiesto dal dispositivo: codice bruciato',
      denied: 'Il soggetto ha negato sulla pagina di verifica («non sono io»)',
      deviceCodeDenied:
        'Codice dispositivo negato o bruciato sulla pagina di verifica, incontrato al poll del dispositivo: rifiuto uniforme, nessun colpo di lockout',
      deviceCodeUnavailable:
        'Il broker non ha potuto servire il round identity (provider OIDC assente o in errore): nulla giudicato, nessun colpo di lockout'
    }
  },

  // Applicazioni (AppsView, NewAppDialog, EditAppDialog): the fields the 2026-09-05
  // parity review found writable from the CLI only — the operator notes, the `enabled`
  // switch of the create — and, since Imago 4Legacy (2026-09-09), the source of an LDAP
  // consumer and the read-only badge of the ephemeral consumer a legacy app generated.
  // The kind `proxyApp` and its login-form descriptor are RETIRED (loss said): a legacy
  // app is its own resource under /4legacy. Since 2026-11-04 the whole Connettori area —
  // the catalog, the two dialogs, the kinds — reads from here (apps.*), like the Servizi
  // (frontends.*) and 802.1X (eap.*) pages below.
  apps: {
    list: {
      generated: 'generato da 4Legacy',
      generatedHelp:
        'Consumer effimero generato dall’app legacy «{app}»: vive e muore con lei, non si modifica né si elimina da solo.',
      generatedActions: 'Si gestisce dall’app legacy che lo ha generato (Imago 4Legacy)',
      title: 'Applicazioni',
      subheader: 'Il catalogo delle applicazioni: ogni app si integra con un servizio (canale) di Trantor Imago.',
      create: 'Nuova app',
      empty: 'Nessuna applicazione registrata.',
      loadError: 'Impossibile caricare le applicazioni.',
      colName: 'Nome',
      colKind: 'Tipo',
      colFrontend: 'Frontend',
      colState: 'Stato',
      active: 'Attiva',
      inactive: 'Disattivata',
      scimExport: 'Export SCIM',
      edit: 'Modifica',
      deleteTitle: 'Elimina applicazione',
      deleteHint:
        'Stai per eliminare l’applicazione «{name}». Le sue credenziali e le sessioni OIDC attive muoiono con essa.'
    },

    // The four kinds of `App.kind` (appMeta KIND_META), as the catalog and the dialogs name them.
    kinds: {
      oidcClient: 'Client OIDC',
      radiusClient: 'Client RADIUS',
      samlServiceProvider: 'Service provider SAML',
      ldapConsumer: 'Consumer LDAP'
    },
    dialog: {
      notes: 'Note (opzionale)',
      notesHelp: 'Note libere dell’operatore: che cos’è l’app, chi la gestisce.',
      enabled: 'Abilitata',
      enabledHelp:
        'Un’app disabilitata rifiuta ogni flusso di autenticazione; la voce di catalogo e la configurazione restano.',
      ldapSourceKind: 'Sorgente (opzionale)',
      ldapSourceIp: 'Indirizzo IP',
      ldapSourceCidrLabel: 'Intervallo sorgente',
      ldapSourceIpLabel: 'Indirizzo sorgente',
      ldapSourceHelp:
        'L’indirizzo da cui il consumer fa il bind su imago-ldapd, come lo vede il servizio LDAP. Descrittivo su un consumer scritto a mano; su un consumer effimero è copiato dall’app legacy.',
      tabs: {
        general: 'Generale',
        protocol: 'Protocollo',
        groups: 'Gruppi e claim'
      },
      nameRequired: 'Serve il nome.',
      redirectUrisRequired: 'Serve almeno un redirect URI.',
      radiusSourceRequired: 'Serve la sorgente del NAS (CIDR o indirizzo IP).',
      radiusSecretRequired: 'Serve lo shared secret.',
      samlEntityIdRequired: 'Serve l’Entity ID del service provider.',
      samlAcsUrlRequired: 'Serve l’ACS URL.',
      createTitle: 'Nuova app',
      editTitle: 'Modifica app',
      createError: 'Impossibile creare l’applicazione.',
      updateError: 'Impossibile aggiornare l’applicazione.',
      kind: 'Tipo',
      kindHelp: 'Il tipo determina il frontend integrato ed è immutabile dopo la creazione.',
      name: 'Nome',
      namePlaceholder: 'portale-clienti',
      nameHelp: 'Unico nel catalogo.',

      // the one-time credential reveal after the create of an oidcClient
      created: {
        title: 'Applicazione creata',
        warning:
          'Il client secret è mostrato una sola volta e non è più recuperabile: copialo ora. Se lo perdi, elimina e ricrea l’applicazione.',
        clientId: 'Client ID',
        clientSecret: 'Client secret',
        close: 'Ho copiato, chiudi'
      },

      // Protocollo: the fields of each kind
      oidc: {
        clientId: 'Client ID',
        clientIdHelp: 'Coniato alla creazione, stabile per tutta la vita dell’app (sola lettura).',
        redirectUris: 'Redirect URI',
        redirectUrisHelp: 'Uno per riga. Solo https:// (con la concessione di sviluppo http://localhost).',
        postLogoutRedirectUris: 'Post-logout redirect URI (opzionale)',
        postLogoutRedirectUrisHelp: 'Uno per riga, stesse regole dei redirect URI.',
        grantTypes: 'Grant type',
        scopes: 'Scope',
        scopesHelp: 'Invio per aggiungere uno scope.'
      },
      radius: {
        source: 'Sorgente',
        sourceIp: 'Indirizzo IP',
        sourceCidrLabel: 'Intervallo sorgente',
        sourceIpLabel: 'Indirizzo sorgente',
        sourceHelp: 'Il NAS/concentratore è riconosciuto dal suo indirizzo di origine.',
        sharedSecret: 'Shared secret',
        sharedSecretHelp: 'Scritto una sola volta e sigillato nel keystore: non viene mai mostrato di nuovo.',
        newSharedSecret: 'Nuovo shared secret (lascia vuoto per mantenere)',
        vlanByGroup: 'VLAN dinamica per gruppo (opzionale)',
        vlanByGroupHelp:
          'Una regola per riga: gruppo => VLAN. Vince la prima regola il cui gruppo l’utente possiede (RFC 3580).',
        vlanRules: 'VLAN dinamica per soggetto (opzionale, valutata per prima)',
        vlanRulesHelp:
          'Una regola per riga: user:<nome>@<realm>, group:<gruppo>@<realm> o @<realm> => VLAN. In ordine: vince la prima che combacia, poi la mappa per gruppo.',
        vlanRulesInvalid:
          'Riga non valida: {line} — serve <chip> => <vlan> con chip user:<nome>@<realm>, group:<gruppo>@<realm> o @<realm>'
      },
      saml: {
        note: 'L’IdP SAML di Imago firma sempre asserzione e Response; gli attributi rilasciati sono username, realm, displayName, email e i gruppi scelti qui sotto. Metadati e URL SSO sono nel frontend SAML.',
        entityId: 'Entity ID',
        acsUrl: 'ACS URL',
        acsUrlHelp: 'Assertion Consumer Service URL (binding HTTP-POST).',
        nameIdFormat: 'Formato del NameID',
        spCertificate: 'Certificato di firma del service provider (PEM, facoltativo)',
        spCertificateHelp:
          'Con un certificato ogni AuthnRequest del service provider deve essere firmata (con questa chiave); senza, le richieste viaggiano in chiaro e l’ACS registrato fa da garanzia.',

        // the NameID formats (appMeta SAML_NAME_ID_META): the menu label and its helper
        nameId: {
          unspecified: {
            label: 'unspecified — username@realm',
            help: 'Il NameID è «username@realm», stabile e leggibile (default).'
          },
          emailAddress: {
            label: 'emailAddress — e-mail del soggetto',
            help: 'Il NameID è l’e-mail del soggetto: un utente senza e-mail viene rifiutato con un errore parlante.'
          },
          persistent: {
            label: 'persistent — identificatore opaco stabile',
            help: 'Un identificatore opaco, stabile per coppia soggetto/SP e insensibile ai rinomini.'
          },
          transient: {
            label: 'transient — identificatore per sessione',
            help: 'Un identificatore diverso a ogni asserzione.'
          }
        }
      },
      ldap: {
        noteCreate:
          'Autentica i propri utenti via il frontend LDAP (imago-ldapd), che delega ogni verdetto al core. Non viene coniato alcun segreto.',
        noteEdit: 'Autentica via il frontend LDAP (imago-ldapd).',
        description: 'Descrizione (opzionale)',
        descriptionEdit: 'Descrizione',
        descriptionHelp: 'Che cos’è questo consumer e come effettua il bind.'
      },

      // Gruppi e claim
      groups: {
        label: 'Gruppi da trasmettere',
        helpOidc: 'Gruppi rilasciati all’app (claim groups). L’app deve anche richiedere lo scope “groups”.',
        helpOther: 'Gruppi dell’utente rilasciati a questa app (solo questi, verbatim). Vuoto = nessun gruppo.'
      }
    }
  },

  // Connettori › Servizi (FrontendsView, FrontendConfigDialog): the four protocol
  // frontends, their status chip, the enable/disable confirmation and the
  // Generale/Avanzate editor.
  frontends: {
    list: {
      title: 'Servizi',
      subheader: 'I quattro servizi di protocollo di Trantor Imago: OIDC, LDAP, RADIUS e SAML.',
      loadError: 'Impossibile caricare i frontend.',
      colService: 'Servizio',
      colProtocol: 'Protocollo',
      colState: 'Stato',
      colEnabled: 'Abilitato',
      configure: 'Configura',
      enableTitle: 'Abilita il servizio',
      disableTitle: 'Disabilita il servizio',
      enableHint:
        'Stai per abilitare il servizio «{name}». La modifica ha effetto al prossimo riavvio del daemon (o build del provider OIDC).',
      disableHint:
        'Stai per disabilitare il servizio «{name}». La modifica ha effetto al prossimo riavvio del daemon (o build del provider OIDC).',
      enable: 'Abilita',
      disable: 'Disabilita'
    },

    // the status chip (FrontendStatus)
    status: {
      up: 'Attivo',
      unknown: 'Sconosciuto',
      notBuilt: 'Non compilato',
      down: 'Spento'
    },
    dialog: {
      saveError: 'Impossibile salvare la configurazione del servizio.',
      tabs: {
        general: 'Generale',
        advanced: 'Avanzate'
      },

      // the per-frontend "effect takes hold when" subtitle
      effect: {
        oidc: 'Le modifiche hanno effetto alla prossima ricostruzione del provider OIDC in httpd.',
        ldap: 'Le modifiche hanno effetto al prossimo riavvio del daemon LDAP (imago-ldapd).',
        radius: 'Le modifiche hanno effetto al prossimo riavvio del daemon RADIUS (imago-radiusd).',
        saml: 'L’interruttore ha effetto immediato: a servizio spento ogni richiesta SAML riceve 404 e i metadati non sono pubblicati.'
      },
      notBuilt:
        'Non ancora disponibile (fase 2): il servizio SAML non è compreso in questa build, quindi non espone impostazioni modificabili.',
      state: 'Stato:',
      build: 'Build:',
      buildPresent: 'presente',
      buildMissing: 'non compilato',
      enabled: 'Abilitato su questo canale',
      issuer: 'Issuer',
      issuerHelp: 'Origine pubblica del provider (deploy-level, sola lettura).',
      entityId: 'Entity ID dell’IdP',
      entityIdHelp: 'Derivato dall’origine pubblica (deploy-level, sola lettura).',
      ssoUrl: 'URL SSO',
      ssoUrlHelp: 'Binding HTTP-Redirect e HTTP-POST sullo stesso URL (sola lettura).',
      metadataUrl: 'URL dei metadati',
      metadataUrlHelp: 'Da importare nel service provider: contiene il certificato di firma dell’IdP (sola lettura).',
      listenerAddress: 'Indirizzo di ascolto',
      listenerAddressHelp: 'Socket del daemon (deploy-level, dall’ambiente — sola lettura).',
      servedRealms: 'Realm serviti',
      servedRealmsHelp:
        'Nessuno = tutti i realm. Un login su un realm non servito è rifiutato come un realm inesistente (l’audit lo distingue).',
      defaultRealm: 'Realm di default del servizio',
      defaultRealmHelp: 'Il realm di uno username NUDO su questo servizio; vuoto = la regola del portale (local).',
      defaultRealmPortal: '— regola del portale —',
      totpMode: 'Modo TOTP nella credenziale combinata',
      totpModeHelp:
        'suffix = ultime 6 cifre (come ieri), prefix = prime 6, none = la password è tutta password (chi è obbligato al TOTP non entra da qui).',
      accessTokenTtl: 'TTL access token (secondi)',
      idTokenTtl: 'TTL id token (secondi)',
      authCodeTtl: 'TTL authorization code (secondi)',
      ttlRangeToken: 'Tra 60 e 86400.',
      ttlRangeCode: 'Tra 30 e 3600.',
      scopes: 'Scope pubblicati',
      scopesHelp: 'Invio per aggiungere uno scope.',
      dedupTtl: 'Finestra di dedup (secondi)',
      dedupTtlHelp: 'Finestra anti-ritrasmissione UDP: tra 1 e 3600.',
      papOnly: 'PAP soltanto:',
      papOnlyValue: 'attivo (const)'
    }
  },

  // Connettori › 802.1X (EapView): the Imago CA and its EAP-TLS client certificates —
  // the list, the CA bootstrap, the issue (the private key shown once), the revoke and
  // the CA certificate / CRL viewer.
  eap: {
    list: {
      title: 'Imago CA — certificati EAP-TLS',
      loadError: 'Impossibile caricare i certificati EAP.',
      caCert: 'Certificato CA',
      crl: 'CRL',
      initCa: 'Inizializza CA',
      issue: 'Emetti certificato',
      colSerial: 'Seriale',
      colSubject: 'Soggetto (CN)',
      colSan: 'SAN',
      colExpiry: 'Scadenza',
      colState: 'Stato',
      revoked: 'revocato',
      valid: 'valido',
      revoke: 'Revoca',
      empty: 'Nessun certificato emesso.'
    },
    viewer: {
      caCertTitle: 'Certificato della CA di Imago (trust anchor)',
      caTitle: 'CA di Imago',
      crlTitle: 'CRL (revoca)',
      crlShortTitle: 'CRL',
      notInitialized: 'La CA non è ancora inizializzata.'
    },
    init: {
      title: 'Inizializza la CA di Imago',
      hint: 'Genera la chiave della CA (sigillata nel keystore) e il certificato radice. Idempotente.',
      confirm: 'Inizializza'
    },
    issue: {
      title: 'Emetti un certificato client EAP-TLS',
      commonName: 'Common Name',
      sanUpn: 'SAN UPN (utente, es. user@dominio)',
      sanDns: 'SAN DNS (macchina)',
      days: 'Validità (giorni, default 365)',
      note: 'Serve almeno un SAN (UPN o DNS). La chiave privata è mostrata una sola volta.',
      continue: 'Continua (sudo)',
      confirmTitle: 'Conferma emissione',
      confirmHint: 'Emetti un certificato per {name}.',
      confirm: 'Emetti'
    },
    issued: {
      title: 'Certificato emesso — serial {serial}',
      warning: 'Salva ORA la chiave privata: non sarà più mostrata.',
      certPem: 'Certificato (PEM)',
      keyPem: 'Chiave privata (PEM)'
    },
    revoke: {
      title: 'Revoca il certificato {serial}…',
      hint: 'Il certificato sarà rifiutato dall’autenticazione EAP e finirà nella CRL.',
      confirm: 'Revoca'
    }
  },

  // Computer (ComputersView and its dialogs): the registry of the machines enrolled with
  // Imago and their enablements (PBA, Credential Provider) — the list, «Nuovo computer»,
  // «Modifica» (the four writable fields of patchComputer, contract computers.yaml),
  // «Abilita»/«Rigenera token» (the one-time registration token, shown once), the revoke
  // and delete confirmations and the PBA login pairings dialog (PairingsDialog).
  computers: {
    // The one placeholder the create and the edit dialog both show on the IP whitelist.
    ipWhitelistPlaceholder: '10.0.0.5 oppure 192.168.0.0/24',
    list: {
      title: 'Computer',
      subheader:
        'I computer registrati in Trantor Imago e le loro abilitazioni (PBA, Credential Provider). L’attivazione la completa il PBA Manager o il CP Manager sul computer col token monouso; qui li registri, li abiliti e li revochi.',
      pairings: 'Pairing di login',
      new: 'Nuovo computer',
      loadError: 'Impossibile caricare i computer.',
      empty: 'Nessun computer registrato.',
      name: 'Nome',
      enablements: 'Abilitazioni',
      directoryDn: 'DN directory',
      notes: 'Note',
      enablementRevoked: 'Revocata',
      enablementHint: 'Clic: rigenera token · X: revoca',
      edit: 'Modifica',
      enable: 'Abilita PBA / Credential Provider',

      // The activation state of an enablement, beside its kind on the chip («PBA · attivo»).
      status: {
        pending: 'in attivazione',
        active: 'attivo',
        revoked: 'revocato'
      }
    },
    revokeDialog: {
      title: 'Revoca abilitazione',
      hint: 'Stai per revocare l’abilitazione «{kind}» di «{name}». PBA: rifiutata al pre-boot. CP: il token permanente è invalidato.',
      confirm: 'Revoca'
    },
    deleteDialog: {
      title: 'Elimina computer',
      hint: 'Stai per eliminare «{name}» e tutte le sue abilitazioni. I token permanenti degli agent smettono di essere serviti.'
    },
    newDialog: {
      title: 'Nuovo computer',
      nameRequired: 'Il nome è obbligatorio.',
      createError: 'Impossibile creare il computer.',
      name: 'Nome',
      namePlaceholder: 'ws-segreteria-01',
      nameHelp: 'Immutabile dopo la creazione.',
      directoryDn: 'DN directory (opzionale)',
      directoryDnHelp: 'Per risolvere i gruppi del computer via LDAP.',
      realm: 'Realm dei gruppi (opzionale)',
      realmHelp: 'Il realm il cui bind di servizio risolve i gruppi del computer via LDAP. Vuoto = quello dell’utente.',
      realmUser: 'Realm dell’utente',
      ipWhitelist: 'Indirizzi ammessi (IP/CIDR, opzionale)',
      ipWhitelistHelp: 'IP singoli o CIDR. Vincola le chiamate degli agent (PBA/CP) a queste origini.',
      notes: 'Note (opzionale)'
    },
    editDialog: {
      title: 'Modifica computer',
      name: 'Nome',
      nameNote: 'Immutabile: è stampato in ogni riga di audit.',
      realm: 'Realm dei gruppi',
      realmHelp:
        'Il realm il cui bind di servizio risolve i gruppi del computer via LDAP. «— nessuno —» = il realm dell’utente che si autentica.',
      realmNone: '— nessuno —',
      realmLoadError: 'Impossibile caricare i realm: la scelta resta quella attuale.',
      realmUnknown: 'Il realm selezionato non esiste più: ricarica la pagina e riprova.',
      directoryDn: 'DN directory',
      directoryDnHelp:
        'Il DN dell’oggetto computer nella directory, per risolverne i gruppi via LDAP. Vuoto = nessuno.',
      ipWhitelist: 'Indirizzi ammessi (IP/CIDR)',
      ipWhitelistHelp:
        'IP singoli o CIDR. Vincola le chiamate degli agent (PBA/CP) a queste origini. Vuota = nessun vincolo.',
      notes: 'Note',
      notesHelp: 'Note libere dell’operatore.',
      sudoNote: 'La modifica di «{name}» richiede la conferma della password.',
      updateError: 'Impossibile modificare il computer.'
    },

    // «Abilita» (a new enablement) and «Rigenera token» (an existing one): the same
    // dialog, then the one-time registration token shown exactly once.
    enableDialog: {
      titleEnable: 'Abilita — «{name}»',
      titleReissue: 'Rigenera token — «{name}»',
      enable: 'Abilita',
      reissue: 'Rigenera',
      kind: 'Abilitazione',
      enableNote: 'Viene generato un token monouso (vale 15 minuti) da inserire nell’agent.',
      reissueNote: 'Il vecchio token viene invalidato e ne viene generato uno nuovo.',
      actionError: 'Impossibile completare l’operazione.',
      tokenTitle: 'Token monouso — {kind}',
      tokenWarning:
        'Copia ora questo token: è mostrato una sola volta. Va inserito nell’agent (PBA ISO / Credential Provider) di «{name}», che lo scambia con un token permanente. Imago ne conserva solo l’hash.',
      done: 'Fatto'
    },

    // The PBA login pairings (computer|group ↔ user|group): list, add form, delete.
    pairingsDialog: {
      title: 'Abbinamenti di login PBA',
      subtitle:
        'Chi può fare login su quali computer. Un lato è un computer o un gruppo di computer, l’altro un utente o un gruppo di utenti. I gruppi vengono sempre dalla directory.',
      loadError: 'Impossibile caricare gli abbinamenti.',
      createError: 'Impossibile creare l’abbinamento.',
      chooseComputer: 'Scegli il computer.',
      computerGroupRequired: 'Serve il DN del gruppo di computer.',
      userRequired: 'Serve l’utente (username@realm).',
      userGroupRequired: 'Serve il DN del gruppo di utenti.',
      add: 'Aggiungi',
      computer: 'Computer',
      subject: 'Soggetto',
      empty: 'Nessun abbinamento.',
      addSection: 'Aggiungi un abbinamento',
      computerSide: 'Lato computer',
      kindComputer: 'computer',
      kindGroup: 'gruppo',
      kindUser: 'utente',
      noComputers: 'Nessun computer registrato',
      computerGroupDn: 'DN gruppo di computer',
      subjectSide: 'Lato soggetto',
      userGroupDn: 'DN gruppo di utenti',
      deleteTitle: 'Elimina abbinamento',
      deleteHint: 'Stai per eliminare l’abbinamento «{computer} → {subject}».'
    }
  },

  // Imago BioCard (BiocardsView and its dialogs): the inventory of the corporate FIDO2
  // cards — one row per physical card, censused before it may enroll — with the status and
  // model filters, «Nuova card», «Modifica» (status/model/notes), the revoke and delete
  // confirmations. `status.*` names the three inventory states (biocardMeta).
  biocards: {
    status: {
      issued: 'In inventario',
      assigned: 'Assegnata',
      revoked: 'Revocata'
    },
    list: {
      title: 'BioCard',
      subheader: 'L’inventario delle Imago BioCard aziendali: una card FIDO2 è censita qui prima di potersi registrare.',
      new: 'Nuova card',
      loadError: 'Impossibile caricare le card.',
      status: 'Stato',
      model: 'Modello',
      empty: 'Nessuna card nell’inventario.',
      serial: 'Seriale',
      assignedTo: 'Assegnata a',
      notes: 'Note',
      revoke: 'Revoca',
      edit: 'Modifica'
    },
    revokeDialog: {
      title: 'Revoca card',
      hint: 'Stai per revocare la card «{serial}». Una card revocata è rifiutata all’enrollment.',
      confirm: 'Revoca'
    },
    deleteDialog: {
      title: 'Elimina card',
      hint: 'Stai per eliminare la card «{serial}» dall’inventario. Una credenziale già registrata continua a funzionare, ma la card non è più abbinabile a una nuova registrazione.'
    },
    newDialog: {
      title: 'Nuova card',
      serialRequired: 'Il serial è obbligatorio.',
      createError: 'Impossibile creare la card.',
      serial: 'Serial',
      serialHelp: 'Chiave fisica univoca della card. Immutabile dopo la creazione.',
      aaguid: 'AAGUID (opzionale)',
      aaguidHelp: 'GUID del modello di autenticatore. Immutabile dopo la creazione.',
      model: 'Modello (opzionale)',
      notes: 'Note (opzionale)'
    },
    editDialog: {
      title: 'Modifica card',
      updateError: 'Impossibile aggiornare la card.',
      serial: 'Serial',
      serialHelp: 'Identità fisica della card (sola lettura).',
      aaguid: 'AAGUID',
      aaguidHelp: 'Modello di autenticatore (sola lettura).',
      status: 'Stato',
      model: 'Modello',
      notes: 'Note'
    }
  },
  rbacGrants: {
    subheader:
      'Chi amministra Trantor Imago: ogni grant assegna un ruolo ai membri di un gruppo di un realm, oppure a un singolo utente del realm, su un bersaglio (tutto il deployment o un solo realm), con o senza facoltà di delega.',
    help: 'Un grant è immutabile: per correggerlo, eliminalo e ricrealo. I ruoli si applicano al prossimo login.',
    helpDetail:
      'Un grant su un solo realm vale sulle operazioni di quel realm (utenti locali, spazio di identità, enumerazione); «può delegare» permette al titolare di creare grant dentro al proprio e di revocare quelli che ha creato. Le sessioni già aperte conservano i ruoli risolti all’autenticazione.',
    tabMappings: 'Grant',
    tabRoles: 'Ruoli',
    new: 'Nuovo grant',
    colTarget: 'Bersaglio',
    colDelegate: 'Delega',
    colCreatedBy: 'Creato da',
    targetAll: 'tutto',
    targetRealm: 'realm {realm}',
    delegable: 'può delegare',
    filterRealm: 'Realm',
    filterGroup: 'Gruppo del realm',
    filterGroupPlaceholder: 'Cerca nel nome del gruppo…',
    filterUser: 'Utente',
    filterUserPlaceholder: 'Cerca nel nome utente…',
    filterRole: 'Ruolo',
    colRealm: 'Realm',
    colSubject: 'Soggetto',
    colRole: 'Ruolo',
    colCreatedAt: 'Creato il',
    subjectGroup: 'gruppo',
    subjectUser: 'utente',
    empty:
      'Nessun grant: oggi amministra solo il break-glass «admin» del realm «local». Crea un grant per concedere un ruolo a un gruppo di un realm o a un singolo utente.',
    emptyFiltered: 'Nessun grant per i filtri selezionati.',
    loadError: 'Impossibile caricare i grant.',
    delete: 'Elimina',
    deleteTitle: 'Elimina grant',
    deleteHint:
      'Stai per revocare il ruolo «{role}» ai membri di «{group}» nel realm «{realm}». Le sessioni già aperte conservano il ruolo fino al prossimo login.',
    deleteHintUser:
      'Stai per revocare il ruolo «{role}» all’utente «{user}» nel realm «{realm}». Le sessioni già aperte conservano il ruolo fino al prossimo login.',
    roles: {
      admin: 'Tutto: ogni lettura e ogni scrittura.',
      helpdesk:
        'Azzera i fattori MFA; legge realm, utenti e gruppi locali, applicazioni, policy e policy MFA, grant, enumerazione e censimento, metadata FIDO, audit. Nessuna scrittura di configurazione.',
      auditor: 'Ogni lettura, registro di audit incluso. Nessuna scrittura.'
    },
    dialog: {
      title: 'Nuovo grant',
      realm: 'Realm',
      realmHelp: 'Il realm a cui appartiene il gruppo o l’utente.',
      target: 'Bersaglio',
      targetAll: 'Tutto il deployment',
      targetRealm: 'Un solo realm',
      targetRealmPick: 'Realm bersaglio',
      targetRealmHelp:
        'Il ruolo vale solo sulle operazioni di questo realm (il realm stesso, i suoi utenti locali, lo spazio di identità, l’enumerazione).',
      canDelegate: 'Può delegare',
      canDelegateHelp:
        'Il titolare potrà creare grant dentro a questo (stesso ruolo o un ruolo con meno permessi, stesso realm o più stretto) e revocare quelli che ha creato.',
      realmEmpty: 'Nessun realm disponibile: crealo prima.',
      subjectKind: 'Concedi a',
      subjectGroup: 'Gruppo',
      subjectUser: 'Utente',
      group: 'Gruppo del realm',
      groupHelp:
        'Il nome o DN del gruppo come lo riporta la directory (memberOf/inChain); su un realm nativo, il nome di un gruppo locale esistente. Confronto senza distinzione di maiuscole.',
      user: 'Nome utente',
      userHelp:
        'Il nome di login di un solo utente, così come lo riporta la directory (lo stesso nome dell’obbligo MFA per utente). Confronto senza distinzione di maiuscole.',
      role: 'Ruolo',
      roleEmpty: 'Nessun ruolo disponibile.',
      roleSystem: 'di sistema',
      roleCustom: 'ruolo personalizzato',
      realmRequired: 'Scegli il realm.',
      groupRequired: 'Serve il gruppo del realm.',
      userRequired: 'Serve il nome utente.',
      roleRequired: 'Scegli il ruolo.',
      targetRealmRequired: 'Scegli il realm bersaglio.',
      sudoNote: 'La creazione di un grant richiede la conferma della password.',
      conflict: 'Esiste già un grant identico (stesso realm, stesso gruppo, stesso ruolo e stesso bersaglio).',
      conflictUser:
        'Esiste già un grant identico (stesso realm, stesso utente — senza distinzione di maiuscole — stesso ruolo e stesso bersaglio).',
      danglingRealm: 'Il realm selezionato non esiste più: ricarica la pagina e riprova.',
      danglingRole: 'Il ruolo selezionato non esiste più: ricarica la pagina e riprova.',
      nativeNoGroups: 'Realm nativo: il gruppo deve esistere in Sistema › Gruppi locali.',
      nativeGroupRefused: 'Nessun gruppo locale con questo nome nel realm: crealo prima in Sistema › Gruppi locali.',
      createError: 'Impossibile creare il grant.'
    }
  },
  rbacRoles: {
    title: 'Ruoli',
    subheader:
      'I ruoli che un grant può concedere: i tre di sistema (fissi nel codice) e quelli personalizzati, composti dal catalogo dei permessi.',
    help: 'Un ruolo è un insieme di permessi: i ruoli di sistema sono immutabili, un ruolo personalizzato ha i permessi che scegli.',
    helpDetail:
      'Il prefisso «imago-» è riservato e il nome non si cambia. Le sessioni che portano un ruolo personalizzato sentono una modifica alla richiesta successiva (su questo nodo subito, sugli altri entro 30 s); un ruolo eliminato non concede più nulla.',
    new: 'Nuovo ruolo',
    colName: 'Nome',
    colKind: 'Tipo',
    colDescription: 'Descrizione',
    colPermissions: 'Permessi',
    kindSystem: 'di sistema',
    kindCustom: 'personalizzato',
    noPermissions: 'nessun permesso',
    morePermissions: '+{count}',
    empty: 'Nessun ruolo.',
    loadError: 'Impossibile caricare i ruoli.',
    catalogError: 'Impossibile caricare il catalogo dei permessi: la creazione e la modifica dei ruoli sono sospese.',
    edit: 'Modifica',
    delete: 'Elimina',
    deleteTitle: 'Elimina ruolo',
    deleteHint:
      'Stai per eliminare il ruolo «{role}». Le sessioni che lo portano non otterranno più nulla da questo ruolo. Se un grant lo nomina ancora, l’eliminazione viene rifiutata.',
    deleteGranted: 'Il ruolo «{role}» ha {count} grant: revoca prima i grant che lo nominano.',
    systemLocked: 'I ruoli di sistema sono fissi nel codice: non si modificano né si eliminano.',
    dialog: {
      createTitle: 'Nuovo ruolo',
      editTitle: 'Modifica ruolo',
      name: 'Nome',
      nameHelp:
        'Minuscole, cifre e «-», 1–64 caratteri; immutabile dopo la creazione. Il prefisso «imago-» è riservato ai ruoli di sistema.',
      nameInvalid:
        'Nome non valido: minuscole, cifre e «-», 1–64 caratteri, che inizia e finisce con una lettera o una cifra.',
      nameReserved: 'Il prefisso «imago-» è riservato ai ruoli di sistema.',
      nameRequired: 'Serve il nome.',
      nameImmutable: 'Il nome non si cambia: è stampato in ogni grant, sessione e riga di audit.',
      description: 'Descrizione',
      permissions: 'Permessi',
      permissionsHelp:
        'Il catalogo è quello del contratto servito, raggruppato per risorsa: ogni permesso elenca (al passaggio del mouse) le operazioni che sblocca. Un ruolo senza permessi è lecito: non concede nulla.',
      selectedCount: '{count} selezionati',
      sudoNote: 'La creazione o la modifica di un ruolo richiede la conferma della password.',
      conflict: 'Esiste già un ruolo con questo nome.',
      createError: 'Impossibile creare il ruolo.',
      updateError: 'Impossibile modificare il ruolo.'
    }
  },
  localGroups: {
    subheader:
      'I gruppi del realm nativo «{realm}»: insiemi nominati dei suoi utenti locali. Un membro porta il nome del gruppo nella sua identità: grant di ruolo, policy MFA, regole VLAN e allowlist delle app lo riconoscono come un gruppo di AD.',
    help: 'Il nome del gruppo è immutabile e confrontato senza distinzione di maiuscole; i gruppi sono piatti (un membro è un utente). Un cambio di appartenenza vale dal prossimo login del membro. I ruoli per gruppo si concedono in',
    helpGrantsLink: 'Ruoli e grant',
    new: 'Nuovo gruppo',
    usersLink: 'Utenti del realm',
    realmSelectorHelp: 'I gruppi sono per realm: lo stesso nome in due realm nativi è due gruppi diversi.',
    realmLookupDenied:
      'La tua sessione può leggere i gruppi locali ma non i realm (permesso realm.read): senza l’elenco dei realm nativi la pagina non sa quale realm mostrare. Chiedi il permesso realm.read.',
    filterName: 'Nome del gruppo',
    filterNamePlaceholder: 'Cerca nel nome…',
    colName: 'Nome',
    colDescription: 'Descrizione',
    colMembers: 'Membri',
    noMembers: 'nessun membro',
    empty: 'Nessun gruppo locale: crea un gruppo.',
    emptyFiltered: 'Nessun gruppo per il filtro selezionato.',
    loadError: 'Impossibile caricare i gruppi locali.',
    edit: 'Modifica',
    delete: 'Elimina',
    deleteTitle: 'Elimina gruppo locale',
    deleteHint:
      'Stai per eliminare «{group}». Le appartenenze vengono rimosse; i grant di ruolo, le policy MFA e le regole che nominano il gruppo restano inerti e vanno revocati a mano. I membri vedono il cambio dal prossimo login.',
    dialog: {
      title: 'Nuovo gruppo in «{realm}»',
      name: 'Nome del gruppo',
      nameHelp:
        'Lettere, cifre, «.», «_», «-», iniziale alfanumerica, senza spazi. Immutabile dopo la creazione; confronto senza distinzione di maiuscole.',
      nameInvalid:
        'Nome non valido: lettere, cifre, «.», «_», «-», iniziale alfanumerica, 1–255 caratteri, senza spazi.',
      nameRequired: 'Il nome del gruppo è obbligatorio.',
      nameInvalidBrief: 'Nome del gruppo non valido.',
      description: 'Descrizione',
      descriptionHelp: 'Testo libero per l’amministratore; non viene confrontato con nulla.',
      members: 'Membri',
      membersHelp: 'Gli utenti del realm che fanno parte del gruppo: l’elenco completo, il salvataggio lo sostituisce.',
      membersTruncated: 'Il realm ha più utenti di quanti il selettore ne elenchi: gli altri si aggiungono dalla CLI.',
      membersLoadError: 'Impossibile caricare gli utenti del realm (serve il permesso localUser.read).',
      memberUnknown: 'Un membro non è un utente del realm: {detail}',
      conflictName:
        'Il gruppo «{group}» esiste già nel realm «{realm}» (il confronto non distingue le maiuscole): scegli un altro nome.',
      createError: 'Impossibile creare il gruppo.'
    },
    editDialog: {
      title: 'Modifica gruppo locale',
      nameHelp:
        'Il nome è immutabile: è citato dai grant di ruolo, dalle policy MFA, dalle regole VLAN e dalle allowlist delle app.',
      updateError: 'Impossibile aggiornare il gruppo.'
    }
  },
  localUsers: {
    subheader:
      'Gli utenti del realm nativo «{realm}»: gli account che Imago possiede in proprio, con password argon2id. Per il resto nessuna differenza da un utente di un realm esterno: stessi flussi di login, obbligo MFA, policy e audit.',
    help: 'Il nome utente è immutabile e confrontato senza distinzione di maiuscole. Nel realm «local», «admin» è il break-glass: non si elimina, non si disabilita, non si rinomina. I ruoli si concedono per utente o per gruppo locale in',
    helpRolesLink: 'Ruoli e grant',
    helpGroupsLink: 'Gruppi locali',
    new: 'Nuovo utente',
    filterUsername: 'Nome utente',
    filterUsernamePlaceholder: 'Cerca nel nome utente…',
    filterEnabled: 'Stato',
    enabledOnly: 'Solo abilitati',
    disabledOnly: 'Solo disabilitati',
    filterDisplayName: 'Nome visualizzato',
    filterDisplayNamePlaceholder: 'Cerca nel nome visualizzato…',
    filterMail: 'E-mail',
    filterMailPlaceholder: 'Cerca nell’e-mail…',
    filterProtected: 'Break-glass',
    protectedOnly: 'Solo il break-glass',
    unprotectedOnly: 'Senza il break-glass',
    filterMustChange: 'Password',
    mustChangeOnly: 'Solo da cambiare al login',
    mustChangeOffOnly: 'Solo definitive',
    colUsername: 'Nome utente',
    colDisplayName: 'Nome visualizzato',
    colMail: 'E-mail',
    colEnabled: 'Stato',
    colProtected: 'Break-glass',
    colMustChange: 'Password',
    colCreatedAt: 'Creato il',
    enabled: 'Abilitato',
    disabled: 'Disabilitato',
    breakGlass: 'break-glass',
    mustChange: 'da cambiare al login',
    empty: 'Nessun utente in questo realm: crea il primo.',
    emptyFiltered: 'Nessun utente per i filtri selezionati.',
    loadError: 'Impossibile caricare gli utenti locali.',
    realmMissing: 'Nessun realm nativo trovato: il realm «local» è integrato e dovrebbe esistere sempre.',
    realmSelector: 'Realm nativo',
    realmSelectorHelp: 'Gli utenti sono per realm: lo stesso nome utente in due realm nativi è due utenti diversi.',
    realmDefault: 'predefinito',
    realmDisabled: 'disabilitato',
    realmUnknown: 'Il realm indicato nel link non è un realm nativo: mostro «{realm}».',
    realmLookupDenied:
      'La tua sessione può leggere gli utenti locali ma non i realm (permesso realm.read): senza l’elenco dei realm nativi la pagina non sa quale realm mostrare. Chiedi il permesso realm.read.',
    edit: 'Modifica',
    reset: 'Reimposta password',
    delete: 'Elimina',
    deleteProtected: 'Il break-glass non si elimina: è l’ultima via d’accesso.',
    resetProtected:
      'La password del break-glass si reimposta solo dall’host, con imago-httpd-admin init-admin --reset.',
    deleteTitle: 'Elimina utente locale',
    deleteHint:
      'Stai per eliminare «{user}». I suoi fattori MFA (TOTP e credenziali FIDO2) vengono cancellati, le sue BioCard tornano disponibili. I grant di ruolo e gli abbinamenti che nominano il login restano e vanno revocati a mano. Le sue sessioni aperte vengono revocate subito.',
    dialog: {
      title: 'Nuovo utente in «{realm}»',
      username: 'Nome utente',
      usernameHelp:
        'Lettere, cifre, «.», «_», «-», iniziale alfanumerica, senza spazi. Immutabile dopo la creazione; confronto senza distinzione di maiuscole.',
      usernameInvalid:
        'Nome utente non valido: lettere, cifre, «.», «_», «-», iniziale alfanumerica, 1–255 caratteri, senza spazi.',
      usernameRequired: 'Il nome utente è obbligatorio.',
      usernameInvalidBrief: 'Nome utente non valido.',
      password: 'Password',
      passwordConfirm: 'Conferma password',
      passwordMin: 'Almeno {min} caratteri (policy).',
      passwordMismatch: 'Le password non coincidono.',
      passwordTooShort: 'La password è più corta del minimo di policy: {min} caratteri.',
      passwordRequired: 'La password è obbligatoria.',
      displayName: 'Nome visualizzato',
      displayNameHelp: 'Con cui l’utente viene salutato; vuoto = il nome utente.',
      mail: 'E-mail',
      mailHelp: 'Per i codici di recupero e le notifiche; vuoto = nessun contatto (recupero non disponibile).',
      mustChange: 'Deve cambiare la password al primo login',
      mustChangeOnHelp:
        'Credenziale di avvio: l’utente la sostituisce al primo login; fino ad allora RADIUS e LDAP rifiutano il login.',
      mustChangeOffHelp: 'La password è definitiva: nessun cambio forzato al primo login.',
      enabled: 'Abilitato',
      conflictUsername:
        'Il nome utente «{user}» è già in uso nel realm «{realm}» (il confronto non distingue le maiuscole): scegline un altro.',
      createError: 'Impossibile creare l’utente.'
    },
    editDialog: {
      title: 'Modifica utente locale',
      usernameHelp:
        'Il nome utente è immutabile: è scritto in ogni sessione, grant di ruolo, obbligo MFA e riga di audit.',
      enabledLocked: 'Il break-glass non si disabilita: è l’ultima via d’accesso.',
      protectedConflict: 'Il break-glass non si disabilita: è l’ultima via d’accesso.',
      disableRevokes:
        'Disabilitando l’utente, le sue sessioni aperte vengono revocate subito (e con loro le elevazioni sudo): ogni chiamata successiva risponde 401.',
      mustChangeRevokes:
        'Imponendo il cambio password, le sessioni aperte dell’utente vengono revocate subito: l’obbligo scatta al prossimo login.',
      updateError: 'Impossibile aggiornare l’utente.'
    },
    resetDialog: {
      title: 'Reimposta password',
      note: 'Stai impostando una password temporanea per «{user}»: dovrà cambiarla al prossimo login. Le sue sessioni aperte vengono revocate subito.',
      password: 'Nuova password',
      confirm: 'Reimposta',
      resetError: 'Impossibile reimpostare la password.'
    }
  },
  realms: {
    manageUsers: 'Gestisci utenti',
    manageGroups: 'Gestisci gruppi',
    enumeration: 'Enumerazione e censimento del realm',
    edit: 'Modifica',
    delete: 'Elimina',
    deleteBuiltIn: 'Il realm «local» integrato non si elimina: è il realm predefinito e contiene il break-glass.',

    // The list (RealmsView): header, columns, chips, tooltips.
    list: {
      subheader:
        'I realm di autenticazione di Trantor Imago: realm nativi (utenti gestiti da Imago), directory LDAP/AD, IdP federati OIDC e SAML e server RADIUS.',
      new: 'Nuovo realm',
      empty: 'Nessun realm configurato.',
      loadError: 'Impossibile caricare i realm.',
      colName: 'Nome',
      colBackend: 'Backend',
      colEndpoint: 'Endpoint',
      colBaseDn: 'Base DN',
      colStatus: 'Stato',
      colDefault: 'Predefinito',
      colActions: 'Azioni',
      active: 'Abilitato',
      disabled: 'Disabilitato',
      default: 'Predefinito',
      probeBuiltIn: 'Il realm «local» non ha un backend da provare',

      // The «Prova» action is KIND-AWARE (MFA design phase 4): the tooltip says what the
      // server will probe on this row — one per kind, no kind left out.
      probeByKind: {
        ldap: 'Prova la directory LDAP: URL, cifratura, connessione, bind di servizio, filtro utente',
        oidc: 'Prova la discovery OIDC dell’issuer',
        saml: 'Verifica il materiale SAML dell’IdP: URL SSO e certificato di firma',
        radius: 'Prova il server RADIUS: Status-Server o Access-Request sintetica con risposta firmata',
        local: 'Verifica lo stato del realm nativo (nessun backend da raggiungere)'
      }
    },
    kinds: {
      ldap: 'LDAP',
      oidc: 'OIDC',
      saml: 'SAML',
      local: 'Nativo',
      radius: 'RADIUS'
    },
    newDialog: {
      title: 'Nuovo realm',
      kindLocal: 'Nativo (utenti di Imago)',
      kindLdap: 'LDAP / Active Directory',
      kindOidc: 'OIDC (IdP federato)',
      kindSaml: 'SAML (IdP federato)',
      kindRadius: 'RADIUS (server esterno)',
      localInfo:
        'Un realm nativo non si appoggia a nessuna directory: gli utenti sono di Imago (password argon2id) e si gestiscono in Sistema › Utenti locali. Nessun logon Windows tramite il Credential Provider; il realm «local» resta il predefinito.',
      sudoNote: 'La creazione di un realm richiede la conferma della password.',
      createError: 'Impossibile creare il realm.'
    },
    deleteDialog: {
      title: 'Elimina realm',
      hintExternal:
        'Stai per eliminare il realm «{realm}». I grant di ruolo vengono rimossi; i fattori MFA registrati diventano orfani, non cancellati.',
      hintNative:
        'Stai per eliminare il realm nativo «{realm}». Si elimina solo se non ha più utenti (eliminali prima, uno per uno); i grant di ruolo vengono rimossi, gli abbinamenti che nominano un login restano.',
      hasUsers: 'Il realm «{realm}» ha ancora {count} utenti: eliminali prima, poi riprova.'
    },
    editDialog: {
      title: 'Modifica realm nativo',
      name: 'Nome',
      localNote: 'Il realm «local» è integrato: nome, backend, stato e predefinito non si modificano.',
      nativeNote: 'Realm nativo: nome e backend non si modificano; lo stato sì.',
      enabled: 'Abilitato',
      enabledHelp: 'Un realm disabilitato rifiuta ogni login dei suoi utenti, su ogni canale.',
      notes: 'Note',
      notesHelp: 'Annotazioni libere su questo realm.',
      sudoNote: 'La modifica richiede la conferma della password e viene registrata in audit a tuo nome.',
      updateError: 'Impossibile aggiornare il realm.'
    },
    editExternalDialog: {
      title: 'Modifica realm',
      nameNote: 'Nome e backend non si modificano; tutto il resto sì. Vengono inviati solo i campi cambiati.',
      notExternal: 'Questo realm è nativo: si modifica dalla finestra del realm nativo.',
      loadError: 'Impossibile caricare il realm.',
      updateError: 'Impossibile aggiornare il realm.'
    },

    // The kind-aware connection probe (RealmProbeDialog): what it does per kind, the
    // outcome as the contract means `reachable` (an answering backend; on SAML the
    // coherence of the static material; on a native realm «nothing to probe») and a
    // label per check name of contract RealmProbe.checks[].name — the server's own
    // detail rides verbatim under it.
    probeDialog: {
      title: 'Prova realm: {realm}',
      hints: {
        ldap: 'Una prova di configurazione della directory, mai un’autenticazione: forma degli URL, cifratura, connessione, bind di servizio e filtro utente.',
        oidc: 'Scarica il documento di discovery dell’issuer (/.well-known/openid-configuration) e verifica che dichiari l’issuer configurato.',
        saml: 'Verifica il materiale dell’IdP senza rete: l’URL SSO deve essere https:// e il certificato di firma deve leggersi ed essere in validità.',
        radius:
          'Status-Server (RFC 5997) sui server in ordine, poi un’Access-Request sintetica che deve tornare come Access-Reject FIRMATA: raggiungibilità e segreto condiviso provati insieme. Nessuna risposta = server irraggiungibile O segreto sbagliato (un server con un altro segreto scarta la richiesta in silenzio), mai «timeout».',
        local:
          'Un realm nativo non ha backend: l’unica verifica è lo stato (abilitato).'
      },
      username: 'Utente di prova (opzionale)',
      usernameHelp: 'Verifica il filtro utente; senza, ne controlla solo la forma.',
      outcome: 'Esito:',
      reachable: 'Raggiungibile',
      unreachable: 'Non raggiungibile',
      coherent: 'Materiale coerente',
      incoherent: 'Materiale incompleto',
      nativeNothingToProbe: 'Nessun backend da provare',
      run: 'Prova',
      error: 'Impossibile eseguire la prova di connessione.',
      checks: {
        urls: 'URL configurati',
        encryption: 'Cifratura del trasporto',
        enabled: 'Realm abilitato',
        connect: 'Connessione TCP/TLS',
        bind: 'Bind di servizio',
        userFilter: 'Filtro utente',
        servers: 'Server RADIUS configurati',
        statusServer: 'Status-Server (RFC 5997)',
        accessRequest: 'Access-Request sintetica (Access-Reject firmata)',
        messageAuthenticator: 'Message-Authenticator nelle risposte del server',
        discovery: 'Discovery OIDC',
        idpSsoUrl: 'URL SSO dell’IdP',
        idpCertificate: 'Certificato di firma dell’IdP'
      }
    },

    // The per-kind forms shared by the create and edit dialogs (ConnectionFields,
    // TlsFields, AttributeFields, LdapLimitsFields, OidcFields, SamlFields) and the
    // client-side mirrors of the server's shape rules (realms.form.*Required/*Shape).
    form: {
      tabs: {
        general: 'Generale',
        connection: 'Connessione',
        access: 'MFA e Windows'
      },
      name: 'Nome',
      namePlaceholder: 'corp',
      nameHelp: 'Minuscolo in stile DNS (corp, example.com), immutabile dopo la creazione.',
      nameShape: 'Solo lettere minuscole, cifre, trattini e punti, in stile DNS (es. corp, example.com).',
      nameRequired: 'Il nome è obbligatorio.',
      kind: 'Backend',
      kindHelp: 'Immutabile dopo la creazione.',
      advanced: 'Avanzate',
      tlsSection: 'TLS',
      attributesSection: 'Attributi della directory',
      limitsSection: 'Limiti',
      urls: 'Server (URL)',
      urlsHelp: 'Uno per riga (ldaps://host:636 o ldap://host), provati in ordine di failover.',
      baseDn: 'Base DN',
      baseDnHelp: 'Il sottoalbero da cui parte la ricerca dell’utente.',
      bindDn: 'Bind DN',
      bindDnHelp: 'Account di servizio per la ricerca dell’utente. Vuoto = bind anonimo di ricerca (rifiutato da AD).',
      bindPassword: 'Bind password',
      bindPasswordHelp: 'Cifrata nel keystore, mai mostrata.',
      bindPasswordKeepHelp: 'Vuota = invariata; scrivila per ruotarla. Non viene mai mostrata.',
      clearBindPassword: 'Rimuovi la bind password',
      clearBindPasswordHelp: 'Cancella il segreto dal keystore: la ricerca passa al bind anonimo.',
      userFilter: 'Filtro utente',
      userFilterHelp:
        '%u è sostituito dalla parte locale del login (RFC 4515-escaped); deve selezionare esattamente un utente. Directory non-AD: (&(objectClass=inetOrgPerson)(uid=%u)).',
      membershipMode: 'Risoluzione dei gruppi',
      membershipInChain: 'inChain (gruppi annidati, AD)',
      membershipMemberOf: 'memberOf (appartenenza diretta)',
      startTls: 'StartTLS',
      startTlsHelp:
        'Cifra una connessione ldap:// prima di qualsiasi bind; ignorato su ldaps://. Senza ldaps:// né StartTLS la bind password viaggia in chiaro.',
      verifyCertificate: 'Verifica il certificato',
      verifyCertificateHelp: 'Il certificato della directory è verificato nella stretta di mano TLS.',
      verifyCertificateOffHelp:
        'Spento: una sessione TLS non verificata non autentica nessuno — la prova di connessione lo segnala.',
      caCertificate: 'CA (PEM)',
      caCertificateHelp: 'Bundle CA in PEM fissato per questa directory. Vuoto = trust store di sistema.',
      uuidAttribute: 'Attributo UUID',
      uuidAttributeHelp:
        'L’UUID immutabile dell’utente: objectGUID su AD, entryUUID su OpenLDAP. Chiave degli enrollment MFA e di ogni riga di audit.',
      displayNameAttribute: 'Attributo display name',
      displayNameAttributeHelp: 'Con cui salutare l’utente; in mancanza, il nome di login.',
      mailAttribute: 'Attributo mail',
      mailAttributeHelp:
        'Dove arrivano i codici di recupero e le notifiche di registrazione e azzeramento dei fattori.',
      phoneAttribute: 'Attributo telefono',
      phoneAttributeHelp: 'Numero mobile, conservato per un futuro recupero via SMS (non ancora servito).',
      memberOfAttribute: 'Attributo memberOf',
      memberOfAttributeHelp: 'Letto al login per fotografare i gruppi quando la risoluzione è memberOf.',
      directoryLockoutThreshold: 'Soglia di lockout della directory',
      directoryLockoutThresholdHelp:
        'La soglia di blocco account della directory stessa. La soglia di Imago deve restare strettamente sotto: Imago deve scattare per primo. Vuoto = non dichiarata.',
      connectTimeoutSeconds: 'Timeout di connessione (s)',
      connectTimeoutSecondsHelp:
        'Scadenza di connessione e richiesta per ogni URL (1–60 s); scaduta, si passa all’URL successivo.',
      oidcInfo:
        'Login federato via redirect all’IdP: nessuna password raggiunge Imago. La fotografia d’identità è costruita dai claim dell’id_token secondo le mappature.',
      issuer: 'Issuer',
      issuerHelp: 'Base della discovery OIDC (Imago legge /.well-known/openid-configuration).',
      clientId: 'Client ID',
      clientSecret: 'Client secret',
      clientSecretHelp: 'Assente = client pubblico (solo PKCE). Cifrato nel keystore, mai mostrato.',
      clientSecretKeepHelp: 'Vuoto = invariato; scrivilo per ruotarlo. Non viene mai mostrato.',
      clearClientSecret: 'Rimuovi il client secret',
      clearClientSecretHelp: 'Il client torna pubblico (solo PKCE).',
      claimsSection: 'Scope e mappature dei claim',
      scopes: 'Scopes',
      scopesHelp: 'Gli scope richiesti all’IdP (openid è sempre incluso).',
      usernameClaim: 'Claim username',
      uuidClaim: 'Claim UUID (stabile)',
      uuidClaimHelp: 'Claim rename-proof mappato sull’uuid esterno (default sub).',
      displayNameClaim: 'Claim display name',
      mailClaim: 'Claim mail',
      groupsClaim: 'Claim gruppi',
      groupsClaimHelp: 'Un array di gruppi, usato come un memberOf LDAP.',
      samlInfo:
        'Imago è il Service Provider: login via redirect all’IdP (AuthnRequest), la Response firmata torna sull’ACS di Imago e la fotografia d’identità è costruita dagli attributi dell’asserzione. I metadata SP del realm sono pubblicati da Imago.',
      idpEntityId: 'Entity ID dell’IdP',
      idpEntityIdHelp: 'L’entityID SAML dell’IdP.',
      idpSsoUrl: 'URL SSO dell’IdP',
      idpSsoUrlHelp: 'Il SingleSignOnService dell’IdP (binding HTTP-Redirect).',
      idpCertificate: 'Certificato di firma dell’IdP',
      idpCertificateHelp:
        'PEM o base64 nudo. Ogni firma di asserzione è verificata contro questo certificato: è l’ancora di fiducia dell’intero flusso.',
      samlMappingsSection: 'Mappature degli attributi',
      usernameAttribute: 'Attributo username',
      usernameAttributeHelp: 'Vuoto = il NameID dell’asserzione.',
      samlUuidAttribute: 'Attributo UUID (stabile)',
      samlUuidAttributeHelp: 'Vuoto = il NameID (rename-proof se l’IdP emette un NameID persistent).',
      samlDisplayNameAttribute: 'Attributo display name',
      samlMailAttribute: 'Attributo mail',
      groupsAttribute: 'Attributo gruppi',
      groupsAttributeHelp: 'Attributo multivalore con i gruppi, usato come un memberOf LDAP.',
      urlsRequired: 'Serve almeno un URL della directory.',
      urlShape: 'Ogni URL deve avere la forma ldaps://host[:porta] o ldap://host[:porta], senza percorso.',
      baseDnRequired: 'Il Base DN è obbligatorio.',
      connectTimeoutRange: 'Il timeout di connessione è un intero tra 1 e 60 secondi.',
      lockoutThresholdShape: 'La soglia di lockout della directory è un intero ≥ 1, oppure vuota.',
      issuerRequired: 'L’issuer è obbligatorio.',
      clientIdRequired: 'Il client ID è obbligatorio.',
      idpEntityIdRequired: 'L’entity ID dell’IdP è obbligatorio.',
      idpSsoUrlRequired: 'L’URL SSO dell’IdP è obbligatorio.',
      idpCertificateRequired: 'Il certificato di firma dell’IdP è obbligatorio: è l’ancora di fiducia.',
      bindPasswordClearAndTyped:
        'Hai attivato «Rimuovi la bind password» e digitato anche una nuova password: scegli una delle due.',
      clientSecretClearAndTyped:
        'Hai attivato «Rimuovi il client secret» e digitato anche un nuovo secret: scegli una delle due.',

      // The `radius` block (RadiusFields — MFA-policy design §1.8, A21, phase 4): Imago is
      // the PAP client of an upstream RADIUS server; the Access-Challenge is a credential
      // step, never a factor; no directory, no enumeration, the shared secret write-only.
      radiusInfo:
        'Imago è il client PAP di un server RADIUS esterno: la password è verificata con una Access-Request (RFC 2865, Message-Authenticator sempre). Una Access-Challenge del server è un passo di credenziale in più (PIN, token), non un secondo fattore: i fattori di Imago seguono DOPO l’accept. Il server non ha una directory: l’identità è il solo nome utente (un rinominato a monte è un soggetto nuovo), i gruppi arrivano solo dall’accept, nessuna enumerazione; il cambio password lo tiene il server RADIUS.',
      radiusServers: 'Server RADIUS',
      radiusServersHelp:
        'Da 1 a 4 server host[:porta] (porta 1812 se omessa), provati NELL’ORDINE elencato finché uno risponde: failover ordinato, mai bilanciamento. Le frecce cambiano l’ordine.',
      radiusServersEmpty: 'Nessun server: aggiungine almeno uno.',
      radiusServer: 'Aggiungi server (host[:porta])',
      radiusServerHelp: 'radius1.example.com, 10.0.0.5:1812 o [fd00::5]:1812 — Invio o «Aggiungi» lo accoda in fondo.',
      radiusServerAdd: 'Aggiungi',
      radiusServerShape:
        'Ogni server ha la forma host[:porta]: nome DNS, IPv4 o IPv6 tra parentesi quadre, porta opzionale.',
      radiusServersRequired: 'Serve almeno un server RADIUS.',
      radiusServersMax: 'Al massimo 4 server RADIUS.',
      radiusSecret: 'Segreto condiviso',
      radiusSecretHelp:
        'Il segreto condiviso con i server (uno per tutta la lista), almeno 8 caratteri. Cifrato nel keystore, mai mostrato.',
      radiusSecretKeepHelp:
        'Vuoto = invariato; scrivilo per ruotarlo (almeno 8 caratteri). Non viene mai mostrato e non si può rimuovere.',
      radiusSecretRequired: 'Il segreto condiviso è obbligatorio.',
      radiusSecretShort:
        'Il segreto condiviso deve avere almeno 8 caratteri: uno più corto rende l’offuscamento RFC 2865 un giocattolo.',
      radiusTimeoutMs: 'Timeout (ms)',
      radiusTimeoutMsHelp:
        'Attesa di una risposta prima di ritrasmettere (o passare al server successivo): 500–10000 ms.',
      radiusTimeoutRange: 'Il timeout è un intero tra 500 e 10000 millisecondi.',
      radiusRetries: 'Ritrasmissioni',
      radiusRetriesHelp:
        'Ritrasmissioni della stessa richiesta per server dopo il primo invio (0–5), poi il server successivo.',
      radiusRetriesRange: 'Le ritrasmissioni sono un intero tra 0 e 5.',
      radiusNasIdentifier: 'NAS-Identifier',
      radiusNasIdentifierHelp:
        'Inviato su ogni richiesta se non vuoto (alcuni server vi legano le policy). Vuoto = non inviato.',
      radiusGroupsFrom: 'Gruppi dall’accept',
      radiusGroupsFromHelp:
        'Da dove leggere i gruppi del soggetto sulla Access-Accept; usati come un memberOf LDAP (grant di ruolo, ambito delle policy MFA).',
      radiusGroupsNone: 'nessuno (gruppi vuoti)',
      radiusGroupsClass: 'Class (ogni attributo Class, come testo)',
      radiusGroupsFilterId: 'Filter-Id (ogni attributo Filter-Id)',

      // `radius.requireMessageAuthenticator` (contract default true — the Blast-RADIUS
      // posture, CVE-2024-3596): the switch says the loss of «off» in so many words.
      radiusRequireMessageAuthenticator: 'Richiedi Message-Authenticator nelle risposte',
      radiusRequireMessageAuthenticatorHelp:
        'Una risposta è creduta solo se porta un Message-Authenticator valido (RFC 2869 §5.14) oltre al Response Authenticator: la postura Blast-RADIUS (CVE-2024-3596). Un server che non firma le risposte non autentica nessuno e la prova ne dice il motivo.',
      radiusRequireMessageAuthenticatorOff:
        'Spento: ammetti CONSAPEVOLMENTE un server RADIUS che non firma le risposte (FreeRADIUS prima della 3.2.5, NPS senza KB5040268): il solo Response Authenticator è falsificabile (CVE-2024-3596). Tieni quel tratto su un segmento segregato o in un tunnel RadSec e riaccendi il controllo appena il server è aggiornato.'
    },

    // The refusals the dialogs know how to speak (describeRealmWriteError).
    errors: {
      immutableField: 'Il campo «{field}» è immutabile: non si modifica dopo la creazione.',
      notOnNative: 'Realm nativo: i campi {fields} non si applicano (nessuna directory, federazione o AD SSO).',
      policyRefused: 'Rifiutato dalla guardia di lockout: {detail}',
      nameReserved: 'Il nome «local» è riservato al realm nativo integrato, in qualsiasi combinazione di maiuscole.',
      nameTaken: 'Esiste già un realm con questo nome.'
    },

    // The AD SSO of an external realm (Luca 2026-09-04): `adDomain` is THE Windows-logon
    // switch, `certLogon` the TPM Virtual Smart Card certificate logon.
    // The «Esente dall'MFA di Imago» switch of the common section (MfaExemptField) — the
    // root field `mfaExempt` of every EXTERNAL realm (Luca 2026-09-06): the label, the
    // help, the per-kind hint (what «its own MFA» is there and which way the default
    // goes) and the list's chip. A native realm has no such switch.
    mfaExempt: {
      section: 'MFA di Imago',
      label: 'Esente dall’MFA di Imago',
      help: 'Il realm fa già la sua MFA: a chi lo usa Imago non chiede nessun fattore, né locale né esterno; le policy MFA che lo coprono restano inerti.',
      hint: {
        oidc: 'Default acceso su un realm OIDC: l’IdP è il gate. Spegnilo se Imago deve aggiungere il suo secondo fattore dopo il login federato (step-up).',
        saml: 'Default acceso su un realm SAML: l’IdP è il gate. Spegnilo se Imago deve aggiungere il suo secondo fattore dopo il login federato (step-up).',
        ldap: 'Default spento su un realm LDAP: la directory verifica solo la password. Accendilo se davanti alla directory c’è già un’MFA.',
        radius:
          'Default spento su un realm RADIUS: Imago aggiunge i suoi fattori dopo l’Access-Accept. Accendilo se il server RADIUS fa già la sua MFA (challenge a token).'
      },
      chip: 'MFA esente',
      chipTooltip: 'Il realm fa la sua MFA: Imago non chiede nessun fattore ai suoi utenti.'
    },
    adSso: {
      column: 'AD SSO',
      none: '—',
      byPassword: 'password',
      byCert: 'cert',
      section: 'AD SSO (logon Windows)',
      adDomain: 'Dominio AD',
      adDomainHelp:
        'Dominio Windows degli utenti del realm (dominio DNS AD / realm Kerberos, es. example.com). OBBLIGATORIO per qualsiasi logon Windows (CP, SSO PBA→CP); vuoto = nessun logon Windows. È il dominio, non il nome del realm.',
      adDomainShape:
        'Dominio DNS o nome NetBIOS: lettere, cifre e trattini, etichette unite da punti (es. example.com o ACME), senza spazi.',
      certLogon: 'Logon a certificato (VSC)',
      certLogonHelp:
        'Certificato smartcard sulla Virtual Smart Card TPM, firmato dalla CA di Imago (il DC deve fidarsi della CA di Imago: configurazione manuale una tantum). Spento = logon con password.',
      federatedHelp:
        'Sui realm federati il logon Windows è solo a certificato: con un dominio AD il logon a certificato è acceso.',
      certLogonNeedsDomain:
        'Il logon a certificato richiede il dominio AD: compila «Dominio AD» oppure spegni il logon a certificato.',
      federatedNeedsCertLogon:
        'Un realm federato entra in Windows solo con certificato: accendi il logon a certificato o svuota il dominio AD.',

      // The RADIUS rule (design §1.8, Q11): the password is the RADIUS server's and never
      // reaches LSA — Windows logon by certificate only, like a federated realm.
      radiusHelp:
        'Su un realm RADIUS il logon Windows è solo a certificato: la password è del server RADIUS e non arriva mai a LSA. Con un dominio AD il logon a certificato è acceso; senza, il Credential Provider rifiuta il realm e il gate PBA sblocca senza handoff.',
      radiusNeedsCertLogon:
        'Un realm RADIUS entra in Windows solo con certificato (la password non arriva mai a LSA): accendi il logon a certificato o svuota il dominio AD.',
      sidNoteLdap:
        'Active Directory 2025+ in modalità enforcement (KB5014754) richiede il SID dell’utente nel certificato: Imago lo legge da objectSid e lo inserisce in ogni certificato che firma.',
      sidNoteNonLdap:
        'Se su un realm non-LDAP vuoi supportare Active Directory 2025+ in modalità enforcement, serve che la CA legga il SID dalla sorgente di enumerazione LDAP del realm (ADR-13, non ancora costruito): oggi su un realm non-LDAP funziona solo con Samba DC o Active Directory non in modalità enforcement.'
    }
  },

  // Home (HomeView): the portal landing page — who you are, the state of your second
  // factor, and the shortcuts to the self-service actions of the profile.
  home: {
    welcome: 'Benvenuto, {name}',
    loadError: 'Impossibile contattare il backend Imago. Riprova più tardi.',
    accountTitle: 'Il tuo account',
    secondFactor: 'Secondo fattore:',
    roles: 'Ruoli:',
    email: 'E-mail: {mail}',
    quickActionsTitle: 'Azioni rapide',
    manageProfile: 'Gestisci profilo e MFA',
    manageProfileHelp:
      'Configura il secondo fattore (MFA), rigenera i codici di recupero e cambia la password dal tuo profilo.'
  },

  // Sistema › Nodo (NodeView and the three cards of components/node): the node
  // configuration channel of the appliance — the management network with its
  // checkpointed apply, the update mirror, the 802.1X frontend — and the calm notice
  // where the channel is off (every node that is not the appliance).
  node: {
    channel: {
      off: 'Questo nodo non ha un canale di configurazione (IMAGO_NODE_APPLY_MODE=off): rete di gestione, mirror degli aggiornamenti e 802.1X si configurano solo sull’appliance Imago Server.',
      offDetail: 'Il server dice: {detail}',
      loadError: 'Impossibile leggere lo stato del nodo.'
    },
    readOnly: 'La tua sessione legge il nodo ma non lo modifica: serve il permesso node.manage.',
    problems: {
      validation: 'Il nodo ha rifiutato il modulo — {detail}',
      checkpointExpired: 'Conferma arrivata troppo tardi: {detail}',
      applyFailed: 'Il nodo non ha applicato la modifica: {detail}'
    },
    network: {
      title: 'Rete di gestione',
      subheader:
        'Interfaccia, indirizzi, DNS e rotte statiche con cui il nodo è raggiunto. Ogni apply vale 90 secondi: senza conferma il nodo torna da solo alla rete precedente.',
      pending: 'In attesa di conferma',
      notConfigured:
        'Questo nodo non ha ancora una rete scritta nell’identità (installato prima di questo canale): il primo apply confermato la scrive. Il modulo parte vuoto; lo stato attuale è qui sotto.',
      refusedTitle: 'Il server ha rifiutato il modulo',
      interface: 'Interfaccia',
      interfaceHelp:
        'Il nome come si vede ora (ens192) o l’indirizzo MAC; più porte separate da virgola formano un bond.',
      vlan: 'VLAN',
      vlanHelp: 'Vuota = non taggata; altrimenti un intero tra {min} e {max}.',
      ip4Mode: 'IPv4',
      ip6Mode: 'IPv6',
      mode: { off: 'Non configurato', dhcp: 'DHCP', auto: 'Automatico (SLAAC/DHCPv6)', static: 'Statico' },
      address4: 'Indirizzo IPv4',
      address6: 'Indirizzo IPv6',
      prefix: 'Prefisso',
      gw4: 'Gateway IPv4',
      gw6: 'Gateway IPv6',
      gwHelp: 'La rotta di default vive qui, mai tra le rotte statiche.',
      dns: 'Server DNS',
      dnsHelp: 'Invio per aggiungere un server; una voce IPv4 richiede IPv4 configurato, una IPv6 richiede IPv6.',
      routes: {
        title: 'Rotte statiche',
        colDestination: 'Destinazione',
        colGateway: 'Gateway',
        colMetric: 'Metrica',
        empty: 'Nessuna rotta statica.',
        add: 'Aggiungi rotta',
        hint: 'Destinazione in forma CIDR (10.9.0.0/24, fd01::/64) e gateway della stessa famiglia, dentro la rete dell’interfaccia; metrica vuota = quella del dispositivo (100 su ethernet). La rotta di default è il gateway, non una rotta.',
        duplicate: 'La destinazione «{destination}» è già in tabella.'
      },
      issues: {
        interfaceRequired: 'Serve l’interfaccia.',
        vlanRange: 'La VLAN è un intero tra 1 e 4094.',
        address4Required: 'Serve l’indirizzo IPv4.',
        prefix4Range: 'Il prefisso IPv4 è un intero tra 1 e 32.',
        address6Required: 'Serve l’indirizzo IPv6.',
        prefix6Range: 'Il prefisso IPv6 è un intero tra 1 e 128.',
        familyRequired: 'Serve almeno una famiglia: IPv4 o IPv6.',
        routeDestinationRequired: 'Serve la destinazione.',
        routeGatewayRequired: 'Serve il gateway.',
        routeMetricRange: 'La metrica è un intero da 0 in su.'
      },
      apply: 'Applica',
      applyHelp: 'Scrive i profili e apre un checkpoint di {seconds} secondi: la rete nuova resta solo se la confermi.',
      applyDialog: {
        title: 'Applica la rete di gestione',
        hint: 'Il nodo scrive i profili e tiene la rete precedente in un checkpoint per {seconds} secondi: se non confermi entro la scadenza torna da solo a quella. Se l’indirizzo cambia, riapri la console al nuovo indirizzo e conferma da lì (o con «imago confirm-network»).',
        confirm: 'Applica'
      },
      inFlight: {
        title: 'Rete applicata, in attesa di conferma',
        countdown: 'Senza conferma il nodo torna alla rete precedente tra {seconds} s (alle {deadline}).',
        help: 'Se questa pagina non raggiunge più il server, il nodo torna da solo: nessun indirizzo sbagliato ti chiude fuori. Conferma solo se la console risponde dalla rete nuova.',
        profiles: 'Profili scritti: {profiles}',
        confirm: 'Conferma ({seconds} s)'
      },
      stillPending:
        'La scadenza è passata ma il nodo segnala ancora una rete in attesa: puoi tentare la conferma, il nodo risponde se il checkpoint è già sparito.',
      confirmDialog: {
        title: 'Conferma la rete',
        hint: 'Il checkpoint viene distrutto e la rete nuova entra nell’identità del nodo: da qui in poi è quella con cui si avvia.',
        confirm: 'Conferma'
      },
      confirmed: 'Rete confermata: è ora quella dell’identità del nodo.',
      reverted:
        'Il nodo è tornato alla rete precedente: la conferma non è arrivata in tempo e il modulo in sospeso è stato scartato. Il modulo mostra di nuovo la rete confermata.',
      live: {
        title: 'Stato attuale',
        device: 'Dispositivo',
        addresses: 'Indirizzi',
        gateway4: 'Gateway IPv4',
        gateway6: 'Gateway IPv6',
        dns: 'DNS',
        routes: 'Rotte',
        none: '—',
        noRoutes: 'Nessuna rotta.'
      }
    },
    update: {
      title: 'Aggiornamenti',
      subheader:
        'La versione in esecuzione, il mirror da cui il nodo scarica i bundle A/B e l’esito dell’ultimo controllo.',
      running: 'Versione in esecuzione',
      mirror: 'Mirror degli aggiornamenti',
      mirrorHelp: 'https://… con un host (il nodo aggiunge la barra finale); vuoto = nessun mirror.',
      mirrorNone: 'Nessun mirror configurato: il nodo non controlla gli aggiornamenti.',
      save: {
        title: 'Salva il mirror',
        hint: 'Il nodo registra il mirror nella sua identità e lo usa dal prossimo controllo.',
        confirm: 'Salva'
      },
      saved: 'Mirror salvato.',
      check: 'Controlla ora',
      checkDialog: {
        title: 'Controlla gli aggiornamenti',
        hint: 'Il nodo legge l’indice del mirror e registra ciò che trova; nessun bundle viene scaricato né installato.',
        confirm: 'Controlla'
      },
      checked: 'Controllo eseguito.',
      availableTitle: 'Ultimo controllo',
      neverChecked: 'Nessun controllo ancora eseguito.',
      nothingNewer: 'Nessuna versione più recente di quella in esecuzione.',

      // The facts of the updater's `available.json`, by path (`.` written as `_`):
      // a path missing here is still shown, by its own name.
      facts: {
        checkedAt: 'Controllato il',
        mirror: 'Mirror interrogato',
        running: 'In esecuzione al controllo',
        available: 'Disponibile',
        available_version: 'Versione disponibile',
        available_bundle: 'Bundle',
        available_sha256: 'SHA-256',
        available_size: 'Dimensione',
        available_notes: 'Note'
      }
    },
    eap: {
      title: '802.1X (imago-eapd)',
      subheader:
        'Il frontend EAP di questo nodo: la postura all’avvio, il servizio e il certificato server che presenta.',
      enabled: 'Abilitato all’avvio',
      disabled: 'Disabilitato all’avvio',
      active: 'In esecuzione',
      inactive: 'Fermo',
      certificate: 'Certificato server',
      certificateNone: 'Nessun certificato installato.',
      subject: 'Soggetto',
      notAfter: 'Scade il',
      enableSwitch: 'Abilitato',
      enableHelp:
        'Abilitare senza un certificato installato è rifiutato dal nodo: incolla o carica certificato e chiave qui sotto.',
      pemHelp:
        'Certificato e chiave insieme, o nessuno dei due: il nodo verifica la coppia con openssl prima di toccare i file. La chiave resta in questa pagina finché non la invii, non viene mai riletta né registrata.',
      pairRequired: 'Certificato e chiave vanno insieme: manca {missing}.',
      certPem: 'Certificato server (PEM)',
      keyPem: 'Chiave privata (PEM)',
      fromFile: 'Da file',
      reveal: 'Mostra la chiave',
      hide: 'Nascondi la chiave',
      save: {
        title: 'Salva 802.1X',
        hint: 'La postura e, se forniti, certificato e chiave vengono scritti nell’identità del nodo; il servizio viene avviato o fermato subito.',
        confirm: 'Salva'
      },
      saved: 'Impostazioni 802.1X salvate; i campi PEM sono stati svuotati.'
    }
  },
  mode: {
    tooltip: 'Tema',
    light: 'Chiaro',
    dark: 'Scuro',
    system: 'Sistema'
  },
  user: {
    profile: { menu: 'Il mio profilo' },
    logout: 'Esci'
  },

  // Profilo (ProfileView, ChangePasswordDialog): the self-service password change is
  // offered to the realms that hold a password at Imago — a federated session (OIDC/SAML)
  // changes it at its identity provider.
  profile: {
    changePassword: 'Cambia password',
    passwordFederated:
      'Il realm «{realm}» è federato ({kind}): la password è custodita dall’identity provider, non da Imago. Cambiala lì.',
    loadError: 'Impossibile caricare il profilo dal backend Imago.',
    sessionTitle: 'Sessione',
    sessionSubheader: 'La sessione con cui stai usando la console.',

    // The mustChangePassword gate at login (ForcePasswordChangeDialog): the change is
    // forced BEFORE entering the portal; fields and refusals reuse changePasswordDialog.
    forcedChange: {
      title: 'Cambio password obbligatorio',
      hint: 'Devi impostare una nuova password prima di continuare.'
    },
    changePasswordDialog: {
      title: 'Cambia password',
      current: 'Password attuale',
      next: 'Nuova password',
      confirm: 'Conferma nuova password',
      mismatch: 'Le password non coincidono.',
      federated: 'Il tuo realm è federato: la password è custodita dall’identity provider, non da Imago — cambiala lì.',
      error: 'Impossibile cambiare la password.'
    }
  },

  // Sistema › Policy (PolicyView, ScopeChipsField, ChannelMatrix): the policy TREE of
  // contract/policy.yaml (design 2026-09-04 §1.7, ratified 2026-09-05) — the labels of
  // the leaves keyed by their dotted path, the scope chips' gestures and refusals, the
  // capability matrix. Retired with the tree: the allUsers switches, the OTP-length
  // select and the hand-written method matrix.
  policy: {
    subtitle: 'Le regole globali di autenticazione, sessione e rate limiting di Trantor Imago.',
    loadError: 'Impossibile caricare la policy.',
    saved: 'Policy aggiornata.',
    readOnly: 'La tua sessione può leggere la policy ma non modificarla: serve il permesso policy.manage.',
    range: 'Tra {min} e {max}.',
    updatedAt: 'Ultima modifica: {when}',
    tabs: {
      general: 'Sessione e sicurezza',
      fido2: 'FIDO2',
      frontend: 'Servizi e canali'
    },
    fields: {
      session: {
        maxIdleMinutes: 'Timeout di inattività della sessione (minuti)',
        maxAgeHours: 'Durata massima della sessione (ore)',
        maxConcurrent: 'Sessioni contemporanee per utente (0 = senza limite)'
      },
      password: {
        minLength: 'Lunghezza minima della password'
      },
      lockout: {
        threshold: 'Soglia di lockout (tentativi falliti)',
        durationMinutes: 'Durata del lockout (minuti)'
      },
      throttle: {
        perIpPerMinute: 'Limite tentativi per IP al minuto',
        perUserPerMinute: 'Limite tentativi per utente al minuto'
      },
      mfa: {
        totpPendingMinutes: 'Validità di una configurazione TOTP non confermata (minuti)'
      },
      audit: {
        retentionDays: 'Conservazione del registro di audit (giorni)'
      }
    },
    fido2: {
      devicesTitle: 'FIDO2 — dispositivi consentiti',
      devicePolicy: 'Politica dei dispositivi FIDO2',
      devicePolicyHelp: 'Vale sia per la registrazione sia per l’autenticazione FIDO2.',
      biocardOnly: 'Solo le Imago BioCard censite nell’inventario',
      anyDevice: 'Qualsiasi dispositivo FIDO2, per i soggetti in elenco',
      devicesAllow: 'Chi può usare qualsiasi dispositivo FIDO2',
      devicesAllowHelp:
        'Utenti, gruppi o interi realm: gli altri restano vincolati alle BioCard. Ogni modifica è immediata e richiede la conferma della password.',
      devicesAllowInert:
        'Utenti, gruppi o interi realm — voci inerti finché la policy è «solo BioCard» (biocardOnly): restano rimovibili da qui senza cambiare la politica. Ogni modifica è immediata e richiede la conferma della password.',
      passwordlessTitle: 'FIDO2 — accesso passwordless',
      passwordlessHelp:
        'Chi può accedere con la sola chiave FIDO2, senza password, sui canali che lo supportano (vedi la matrice dei canali). L’accesso passwordless richiede SEMPRE la user verification (PIN/biometria). Predefinito: nessuno.',
      passwordlessEnabled: 'Abilita l’accesso passwordless per i soggetti in elenco',
      passwordlessAllow: 'Chi può accedere passwordless',
      passwordlessAllowHelp:
        'Utenti, gruppi o interi realm. Ogni modifica è immediata e richiede la conferma della password.',
      passwordlessAllowClosed:
        'L’interruttore è spento: l’elenco è conservato ma nessuno accede passwordless finché non lo accendi e salvi.',
      blacklistTitle: 'FIDO2 — auto-registrazione vietata',
      blacklist: 'Chi NON può registrare da sé una credenziale FIDO2',
      blacklistHelp:
        'La registrazione dal profilo è rifiutata a questi soggetti; l’helpdesk può ancora registrare per loro. Ogni modifica è immediata e richiede la conferma della password.',
      metadataTitle: 'FIDO2 — metadata e certificazione',
      metadataHelp:
        'Regole sui modelli di autenticatore noti al FIDO Metadata Service. Un modello REVOCATO o COMPROMESSO è rifiutato sempre, alla registrazione e all’accesso: non è una scelta.',
      requireKnownAaguid: 'Registra solo autenticatori con un modello noto (AAGUID nei metadata)',
      minCertificationLevel: 'Livello minimo di certificazione FIDO alla registrazione',
      minCertificationLevelHelp: '«Nessuno» accetta anche modelli non certificati; un modello ignoto non soddisfa alcun livello.',
      levelNone: 'Nessuno',
      allowedAaguids: 'AAGUID ammessi (uno per riga; vuoto = tutti i modelli)',
      allowedAaguidsHelp: 'Vale alla registrazione E all’accesso: una chiave di un modello tolto dall’elenco smette di autenticare.',
      allowedAaguidsBad: 'Riga non valida: «{bad}» non è un AAGUID.',
      sourceUrl: 'URL del BLOB del FIDO Metadata Service',
      sourceUrlHelp:
        'Vuoto = l’URL dell’ambiente (IMAGO_FIDO_MDS_URL). Se impostato vince e vale dal prossimo aggiornamento: https obbligatorio, http solo verso un host di loopback.',
      sourceUrlBad: {
        form: 'Serve un URL assoluto, senza credenziali, query o frammento.',
        scheme: 'Schema non ammesso: serve https (http solo verso un host di loopback).',
        host: 'http è ammesso solo verso un host di loopback (127.0.0.0/8, ::1, localhost): questo host richiede https.'
      },
      sourceUrlFromPolicy: 'dalla policy',
      sourceUrlFromEnvironment: 'dall’ambiente (IMAGO_FIDO_MDS_URL)'
    },
    enrollment: {
      title: 'Registrazione dei fattori — reti ammesse',
      allowedCidrs: 'Reti da cui si può registrare un fattore (CIDR)',
      allowedCidrsHelp:
        'Vale per l’auto-registrazione dal profilo (TOTP, FIDO2, MFA esterno): fuori da queste reti la registrazione risponde 403 e finisce nel registro. Vuoto = nessuna restrizione. I reset dell’helpdesk non sono limitati. Una rete per voce, senza bit di host (10.1.0.0/16, 2001:db8::/32).',
      allowedCidrsBad: 'Voce non valida: «{bad}» non è una rete in forma CIDR senza bit di host (10.1.0.0/16, 2001:db8::/32).'
    },
    ldap: {
      title: 'Gruppi rilasciati — servizio LDAP',
      releaseGroups: 'Gruppi rilasciabili via LDAP (memberOf)',
      releaseGroupsHelp:
        'Allowlist globale per il servizio LDAP: la memberOf di ogni consumer è filtrata a questi gruppi (verbatim). Vuoto = nessun gruppo. OIDC e RADIUS si configurano invece per-app.'
    },
    channels: {
      title: 'Capacità dei canali',
      help: 'Sola lettura: che cosa ogni canale sa portare (gate di registrazione, scelta del fattore, round interattivi, passwordless, prove). «eap» è l’autenticazione interna 802.1X.',
      colChannel: 'Canale',
      enrollmentGate: 'Gate di registrazione',
      picker: 'Scelta del fattore',
      interactiveRoundAnswer: 'Round interattivo (answer)',
      interactiveRoundWait: 'Round interattivo (wait)',
      passwordless: 'Passwordless',
      proofKinds: 'Prove trasportabili',
      proofs: {
        otp: 'OTP (codice digitato)',
        assertion: 'Assertion WebAuthn'
      },
      names: {
        portal: 'Portale (API nativa)',
        oidc: 'OIDC',
        saml: 'SAML',
        pi: 'privacyIDEA',
        radius: 'RADIUS',
        eap: '802.1X (EAP)',
        ldap: 'LDAP',
        proxy: 'Proxy identity-aware',
        pba: 'PBA (gate pre-boot)',
        cp: 'Credential Provider (Windows)'
      }
    },
    scope: {
      colEntry: 'Voce',
      colKind: 'Tipo',
      kindUser: 'Utente',
      kindGroup: 'Gruppo',
      kindRealm: 'Realm intero',
      userName: 'Nome utente (senza @realm)',
      groupName: 'Gruppo (come lo riporta la directory)',
      realm: 'Realm',
      realmEmpty: 'Nessun realm leggibile: le voci si aggiungono quando l’elenco dei realm è disponibile.',
      nativeNoGroups: 'Realm nativo: le voci di gruppo nominano un gruppo locale (Sistema › Gruppi locali).',
      hint: 'La voce viene scritta subito dopo la conferma della password; «@realm» copre tutti i soggetti del realm, senza wildcard.',
      listed: '«{entry}» è già in elenco.',
      empty: 'Nessuna voce.',
      add: 'Aggiungi',
      remove: 'Rimuovi',
      addTitle: 'Aggiungi la voce',
      removeTitle: 'Rimuovi la voce',
      addHint: 'Aggiunge «{entry}» a {path}. Richiede una nuova conferma della password.',
      removeHint: 'Rimuove «{entry}» da {path}. Richiede una nuova conferma della password.',
      alreadyPresent: 'Nessuna modifica: la voce era già presente (stessa forma normalizzata).',
      alreadyAbsent: 'Nessuna modifica: la voce era già assente.',
      unknownRealm: 'Il realm «{realm}» non esiste: la voce è stata rifiutata.',
      badName: 'Nome rifiutato: {detail}',
      badPath: '«{path}» non è una lista di ambito della policy: {detail}',
      editError: 'Impossibile modificare l’elenco.'
    },
    save: {
      title: 'Salva la policy',
      hint: 'La modifica della policy richiede una nuova conferma della password. Le liste di ambito non fanno parte del salvataggio: sono già state scritte voce per voce.',
      refused: 'Rifiutato dalla guardia di lockout: {detail}',
      error: 'Impossibile salvare la policy.'
    }
  },

  // The caller's own obligation, as the profile's MFA card (the gate page) and the Home
  // chip read the MfaStatus of phase 2 (design 2026-09-04 §1.5): what the named MFA
  // policies say the caller must HOLD (`owed`), what is still missing (`missing`) and
  // which policy asks for it (`provenance`).
  mfa: {
    and: 'e',
    chip: {
      unknown: 'Sconosciuto',
      active: 'MFA attiva',
      missing: 'MFA dovuta, fattore mancante',
      none: 'MFA non configurata'
    },
    gate: {
      missing:
        'Devi attivare {factors}: lo richiede la policy {policies}. Finché manca, la tua sessione raggiunge solo questa pagina e la registrazione dei fattori.',
      satisfied: 'Il tuo account deve avere {factors} (policy {policies}): tutto registrato.',
      breakGlass:
        'Account break-glass: nessun obbligo MFA per costruzione. Un fattore registrato viene comunque richiesto al login.'
    },
    removeFree: 'Stai per rimuovere il secondo fattore dal tuo account.',
    removeOwed:
      'Il fattore TOTP è dovuto dalla policy {policies}: senza di esso il tuo account non soddisfa più l’obbligo e la prossima sessione resta al gate di registrazione.',
    done: 'Fatto',

    // The profile's factor cards: the TOTP card (MfaCard) and its enrollment dialog
    // (MfaEnrollDialog), the recovery codes panel (RecoveryCodes) and the FIDO2 card
    // (Fido2Card) — the BioCard enrollment, the discoverable option, the duress
    // credential and its binding proof, and the browser-side WebAuthn failures.
    card: {
      title: 'Secondo fattore TOTP (app di autenticazione)',
      subheader: 'Aggiungi un secondo fattore al tuo accesso.',
      loadError: 'Impossibile leggere lo stato MFA.',
      configured: 'Configurato',
      notConfigured: 'Non configurato',
      expired: 'Scaduto',
      expiredHelp:
        'La configurazione TOTP avviata non è stata confermata in tempo: il codice QR non vale più. Ricomincia da capo.',
      restart: 'Ricomincia la configurazione',
      configuredAt: 'Configurato il: {at}',
      recoveryCodesRemaining: 'Codici di recupero rimasti: {count}',
      enroll: 'Configura TOTP',
      regenerate: 'Rigenera codici',
      removeTitle: 'Rimuovi TOTP',
      regenerateTitle: 'Rigenera codici di recupero',
      regenerateHint: 'La rigenerazione invalida tutti i codici precedenti.',
      regenerateConfirm: 'Rigenera',
      recoveryCodesTitle: 'Codici di recupero'
    },
    enroll: {
      startError: 'Impossibile avviare la configurazione TOTP.',
      codeRejected: 'Codice non valido. Riprova.',
      expired: 'La configurazione è scaduta prima della conferma: chiudi e ricomincia da capo (il QR cambia).',
      instructions:
        'Inquadra il codice QR con la tua app di autenticazione, oppure inserisci la chiave manualmente, poi digita il codice a 6 cifre per confermare.',
      secretLabel: 'Chiave (inserimento manuale):',
      parameters: '{algorithm} · {digits} cifre · {period}s',
      codeLabel: 'Codice di verifica',
      acknowledged: 'Ho salvato i miei codici di recupero.',
      confirm: 'Conferma'
    },
    recoveryCodes: {
      warning:
        'Conserva questi codici in un luogo sicuro: vengono mostrati una sola volta e sostituiscono il secondo fattore se perdi l’autenticatore.',
      copy: 'Copia',
      copied: 'Copiati',
      download: 'Scarica'
    },

    // The external factors the policies owe (`ext:<name>` in `owed`): the enrollment on
    // the bridge from the profile (backlog A.4, 2026-11-04) — Imago asks the bridge's
    // /enroll and shows what it answered, once.
    external: {
      owed: 'Fattore esterno richiesto dalla policy: {name}',
      enroll: 'Registra',
      enrollTitle: 'Registrazione su «{name}»',
      enrollHint:
        'Imago chiede al servizio esterno di avviare la tua registrazione: riceverai un link da aprire, un codice da inserire o un’istruzione da seguire. Quello che vedi qui è mostrato una sola volta.',
      started: 'Registrazione avviata sul servizio esterno «{name}».',
      openLink: 'Apri il link di registrazione',
      secret: 'Codice monouso (mostrato una sola volta):',
      message: 'Istruzioni del servizio:',
      expiresAt: 'Valido fino a: {at}',
      noCapability: 'Il servizio esterno non offre la registrazione dal portale: rivolgiti al supporto.',
      alreadyEnrolled: 'Risulti già registrato sul servizio esterno.',
      unavailable: 'Il servizio esterno non risponde o non è in servizio: riprova più tardi.',
      refused: 'Il servizio esterno ha rifiutato la registrazione: {detail}',
      error: 'Impossibile avviare la registrazione sul servizio esterno.'
    },
    fido2: {
      subheader: 'Registra la tua Imago BioCard o, se la policy lo ammette, un’altra chiave di sicurezza FIDO2.',
      enroll: 'Aggiungi card FIDO2',
      enrollDuress: 'Aggiungi credenziale anti-coercizione',
      inventoryHint:
        'La card deve essere una delle Imago BioCard aziendali (censita nell’inventario). Alla registrazione, appoggia o inserisci la card quando il browser lo richiede.',
      discoverable: 'Memorizza il nome utente sul dispositivo FIDO2',
      discoverableHelp:
        'Permette di accedere senza digitare il nome utente («Accedi con la tua Imago BioCard»). Richiede il PIN o la biometria del dispositivo e occupa uno slot al suo interno.',
      duressHint:
        'Credenziale anti-coercizione: registrala appoggiando il dito DURESS sulla BioCard; un accesso con quel dito riesce come sempre e fa partire l’allarme senza che chi ti costringe se ne accorga. Dopo la registrazione la dashboard chiede una verifica con lo stesso dito.',
      loadError: 'Impossibile leggere le card registrate.',
      none: 'Nessuna card FIDO2 registrata.',
      cardName: 'Card FIDO2',
      duressChip: 'anti-coercizione',
      proofVerified: 'legame verificato',
      proofPending: 'legame da verificare',
      registeredAt: 'Registrata il: {at}',
      lastUsedAt: 'Ultimo utilizzo: {at}',
      neverUsed: 'Mai utilizzata',
      proofOk: 'Legame verificato: il dito usato espone proprio questa credenziale.',
      proofError: 'Prova di legame non riuscita.',
      startError: 'Impossibile avviare la registrazione della card.',
      finishError: 'Impossibile registrare la card.',
      registeredTitle: 'Card registrata',
      registered: 'La card è stata registrata come secondo fattore.',
      registeredSerial: 'La card «{serial}» è stata registrata come secondo fattore.',
      removeTitle: 'Rimuovi card FIDO2',
      removeHint: 'Stai per rimuovere la card «{name}» dal tuo account.',

      // the browser-side WebAuthn failures (DOMException names) a user can actually hit
      webauthn: {
        notAllowed:
          'Operazione annullata: nessuna card è stata presentata in tempo, oppure la richiesta è stata rifiutata.',
        invalidState: 'Questa card risulta già registrata sul tuo account.',
        notSupported: 'Il browser o l’autenticatore non supporta questo tipo di credenziale.',
        security: 'Contesto non sicuro: la registrazione FIDO2 richiede una connessione HTTPS valida.',
        abort: 'Registrazione interrotta. Riprova.',
        generic:
          'Impossibile completare la registrazione della card. Verifica che la card sia collegata (o appoggiata sul lettore NFC) e riprova.'
      }
    }
  },

  // MFA (MfaView, MfaPolicyDialog, MfaRulesField, MfaScopeField, EffectiveMfaCard): the
  // named MFA policies of contract/mfa-policies.yaml (design 2026-09-04 §1.2–§1.5,
  // phase 2) — rules applied to a scope — and the effective view. The external MFA
  // section of the same page speaks through `externalMfas` below.
  mfaPolicies: {
    title: 'Policy di attuazione MFA',
    subheader: 'Chi deve AVERE quali fattori: regole (obbliga / esenta) applicate a utenti, gruppi o interi realm.',
    help: 'Un obbligo è di possesso: al login basta uno dei fattori posseduti. Le regole si sommano; un’esenzione vale solo se più specifica dell’obbligo.',
    helpDetail:
      '«Entrambi» vuol dire «entrambi registrati». Un’esenzione toglie solo il fattore che nomina e solo se più specifica dell’obbligo (utente > gruppo > realm; a pari specificità vince l’obbligo). Un fattore locale dovuto e mancante manda al gate di registrazione del portale e rifiuta gli altri canali; un fattore esterno dovuto è richiesto al login e, se non registrato, rifiuta ovunque. Il break-glass non deve nulla; un realm esente dall’MFA di Imago (mfaExempt) rende inerti le policy che lo coprono.',
    new: 'Nuova policy',
    empty:
      'Nessuna policy MFA: nessun utente deve avere un secondo fattore (a chi lo ha registrato viene comunque richiesto al login).',
    loadError: 'Impossibile caricare le policy MFA.',
    colName: 'Nome',
    colEnabled: 'Abilitata',
    colRules: 'Regole',
    colScope: 'Ambito',
    colUpdated: 'Aggiornata',
    enabled: 'Abilitata',
    disabled: 'Disabilitata',
    enable: 'Abilita',
    disable: 'Disabilita',
    enableTitle: 'Abilita la policy',
    disableTitle: 'Disabilita la policy',
    enableHint:
      'La policy «{policy}» torna a contribuire alla risoluzione dal prossimo login. Richiede la conferma della password.',
    disableHint:
      'La policy «{policy}» resta ma non contribuisce più alla risoluzione: chi doveva un fattore solo per lei non lo deve più dal prossimo login. Richiede la conferma della password.',
    edit: 'Modifica',
    delete: 'Elimina',
    deleteTitle: 'Elimina la policy',
    deleteHint:
      'La policy «{policy}» viene eliminata: i soggetti che obbligava devono, dalla prossima risoluzione, ciò che dicono le policy rimanenti. Richiede la conferma della password.',
    deleteRefused: 'Eliminazione rifiutata: {detail}',
    scopeCount: '{count} voci',
    moreScope: '+{count}',
    factors: {
      totp: 'TOTP (app di autenticazione)',
      fido2: 'FIDO2 (BioCard o chiave di sicurezza)',
      any: 'TOTP oppure FIDO2 (almeno uno)',
      both: 'TOTP e FIDO2 (entrambi)',
      external: 'MFA esterna «{name}»'
    },
    rules: {
      colRule: 'Regola',
      colReading: 'Significato',
      verbLabel: 'Azione',
      familyLabel: 'Fattore',
      verb: { enforce: 'Obbliga', omit: 'Esenta' },
      family: { local: 'Locale', ext: 'Esterno' },
      factor: 'Fattore',
      externalName: 'MFA esterna',
      add: 'Aggiungi regola',
      empty: 'Nessuna regola.',
      hint: '«Almeno uno» = uno dei due; «entrambi» = entrambi registrati (conservato com’è scritto). Un’esenzione senza obbligo da togliere è inerte, mai un errore. BioCard non è un fattore: il fattore è FIDO2.',
      extHint:
        'Il nome di un’MFA esterna configurata (sezione «Bridge MFA esterni» qui sotto, o create-external-mfa). Una risorsa disabilitata resta citabile, ma chi la deve fallisce chiuso al login finché non torna abilitata; il server rifiuta un nome che non esiste, dicendolo.',
      extNone:
        'Nessuna MFA esterna configurata: creala nella sezione «Bridge MFA esterni» (o con create-external-mfa) prima di citarla in una regola.',
      extDisabled: 'disabilitata',
      extCited: 'citato dalle policy',
      duplicate: 'La regola «{rule}» è già presente.',
      full: 'Una policy porta al massimo {max} regole.'
    },
    scope: {
      hint: 'Le voci vengono inviate con la policy; «@realm» copre tutti i soggetti del realm, senza wildcard. Il server verifica che ogni realm esista.',
      full: 'Una policy porta al massimo {max} voci di ambito.'
    },
    dialog: {
      nameRequired: 'Serve il nome.',
      createTitle: 'Nuova policy MFA',
      editTitle: 'Modifica policy MFA',
      name: 'Nome',
      nameHelp:
        'Minuscole, cifre e trattini (1–64), inizio e fine alfanumerici; unico senza distinzione di maiuscole. Immutabile dopo la creazione.',
      nameImmutable: 'Il nome è immutabile: per rinominare, elimina e ricrea.',
      nameInvalid: 'Nome non valido: minuscole, cifre e trattini, 1–64 caratteri, inizio e fine alfanumerici.',
      conflict: 'Esiste già una policy con questo nome (le maiuscole non contano).',
      enabled: 'Abilitata',
      enabledHelp: 'Una policy disabilitata viene conservata ma non risolta: non contribuisce a nessun obbligo.',
      rules: 'Regole',
      rulesHelp: 'Applicate insieme a ogni voce dell’ambito. Al salvataggio l’elenco sostituisce il precedente.',
      rulesRequired: 'Serve almeno una regola.',
      scope: 'Ambito',
      scopeHelp:
        'A chi si applicano le regole: utenti, gruppi (come li riporta la directory) o interi realm. Al salvataggio l’elenco sostituisce il precedente.',
      scopeRequired: 'Serve almeno una voce di ambito.',
      sudoNote:
        'La creazione o la modifica richiede la conferma della password e viene registrata in audit a tuo nome.',
      ruleRefused: 'Regola rifiutata ({field}): {detail}',
      scopeRealmRefused: 'Voce di ambito rifiutata ({field}): {detail}',
      scopeNameRefused: 'Voce di ambito rifiutata ({field}): {detail}',
      createError: 'Impossibile creare la policy.',
      updateError: 'Impossibile aggiornare la policy.'
    },
    effective: {
      title: 'MFA effettiva',
      subheader:
        '«Perché a mario@acme viene chiesto un fattore?» — cosa deve un soggetto sotto le policy abilitate, e da quale regola.',
      user: 'Nome utente (senza @realm)',
      userHelp: 'Senza «@realm»: il realm si sceglie accanto.',
      realm: 'Realm',
      resolve: 'Risolvi',
      owed: 'Fattori dovuti',
      owedNone: 'nessuno — la sola password basta (un fattore registrato viene comunque richiesto)',
      groups: 'Gruppi letti ora dal realm',
      groupsNone: 'nessuno (il soggetto non è in alcun gruppo, o la directory non ne riporta)',
      groupsUnknown: 'sconosciuti: si conoscono solo al login',
      groupsUnknownHelp:
        'Su un realm radius, oidc o saml i gruppi del soggetto si conoscono SOLO al login (Class/Filter-Id dell’Access-Accept, claim dell’IdP): questa vista ha confrontato le voci di gruppo con NESSUN gruppo, quindi una policy con ambito di gruppo può aggiungere al login obblighi che «Fattori dovuti» qui non mostra.',
      breakGlass: 'Break-glass',
      breakGlassHelp: 'La riga protetta del realm nativo non deve nulla per costruzione: nessuna policy si applica.',
      mfaExempt: 'Esente dall’MFA (il realm fa la sua)',
      mfaExemptHelp:
        'Il realm è esente dall’MFA di Imago («mfaExempt: true»): fa già la sua MFA, a questo soggetto non viene chiesto nessun fattore — né locale né esterno — e ogni policy che lo copre è inerte.',
      provenanceNone: 'Nessuna regola corrisponde a questo soggetto.',
      colFactor: 'Fattore',
      colEffective: 'Esito',
      colEnforcedBy: 'Obbligato da',
      colOmittedBy: 'Esentato da',
      outcome: { owed: 'Dovuto', omitted: 'Esentato', inertOmit: 'Esenzione inerte' },
      level: { '1': 'realm', '2': 'gruppo', '3': 'utente' },
      legend:
        'Specificità: {user} > {group} > {realm}; un’esenzione toglie il fattore solo se STRETTAMENTE più specifica di ogni obbligo su di esso (a parità vince l’obbligo). «Esenzione inerte»: un’esenzione senza obbligo da togliere, o che stringerebbe (omit:local:totp contro enforce:local:any).',
      notFound: 'Nessun utente «{user}» nel realm «{realm}» (o il realm non esiste).',
      unavailable: 'La directory del realm non risponde: {detail}',
      error: 'Impossibile risolvere l’MFA effettiva.'
    }
  },

  // MFA › Cerca utente (MfaSubjectCard): ONE subject by name — what it HOLDS (the
  // native TOTP, every FIDO2 credential with its Biocard, every external MFA with the
  // bridge's lookup where it can be asked) beside what it OWES (the effective block of
  // the same answer — the EffectiveMfaResult component), and the per-METHOD reset
  // (contract mfa-subjects.yaml, questionario A33, Luca 2026-09-07). An external method
  // is never resettable from Imago: the bridge API has no unenroll — the row offers the
  // enrollment link when the bridge handed one back. `enrollment.read` shows,
  // `enrollment.reset` acts (sudo, the §5.1 e-mail to the subject, one audit line).
  mfaSubjects: {
    title: 'Cerca utente',
    subheader:
      '«Cosa ha registrato mario@acme?» — i metodi MFA attivi di un utente (TOTP nativo, credenziali FIDO2, MFA esterne) accanto alla sua MFA effettiva, per azzerarne uno.',
    subject: 'Utente',
    subjectPlaceholder: 'mario@acme',
    subjectHelp:
      'utente@realm oppure realm\\utente (l’ultima «@» qualifica il realm; ACME\\mario = mario@acme). Un nome senza qualificatore è un utente del realm nativo «local».',
    subjectEmptyPart:
      'Parte vuota: dopo «@» (o prima di «\\») serve il realm, prima il nome utente. Un nome nudo vale per il realm «local».',
    subjectTooShort: 'Almeno {min} caratteri.',
    subjectTooLong: 'Al massimo {max} caratteri.',
    search: 'Cerca',
    noPermission:
      'La tua sessione non ha il permesso enrollment.read: i metodi registrati di un utente non si leggono da qui.',
    readOnly: 'Con il solo permesso enrollment.read i metodi si leggono ma non si azzerano (serve enrollment.reset).',
    malformed: 'Utente non valido: {detail}',
    notFound: 'Utente «{subject}» non trovato: {detail}',
    unavailable: 'La directory del realm non risponde: {detail}',
    loadError: 'Impossibile leggere i metodi MFA dell’utente.',
    resolved: 'Utente',
    realmKind: 'realm {kind}',
    heldNone:
      'Nessun secondo fattore custodito da Imago: l’utente entra con la sola password (o va al gate di registrazione, se una policy gli deve un fattore locale). Le MFA esterne possono comunque conoscerlo a monte.',
    colMethod: 'Metodo',
    colState: 'Stato',
    colFacts: 'Dettagli',
    since: 'dal {at}',
    created: 'registrata il {at}',
    lastUsed: 'ultimo uso {at}',
    neverUsed: 'mai usato',
    totp: {
      label: 'TOTP (app di autenticazione)',
      state: { enrolled: 'Registrato', pending: 'In sospeso', none: 'Non registrato' },
      pendingHelp: 'Segreto emesso ma nessun codice l’ha ancora confermato (QR a metà): l’azzeramento lo rimuove.',
      recoveryCodesLeft: '{count} codici di recupero rimasti',
      enrollmentId: 'id enrollment',
      reset: 'Azzera TOTP',
      resetTitle: 'Azzera il TOTP',
      resetHint:
        'Stai per azzerare il TOTP di «{subject}»: il segreto e i codici di recupero vengono rimossi (anche un segreto in sospeso); le credenziali FIDO2 restano. L’utente si registra di nuovo al prossimo accesso e riceve una e-mail di notifica, se ha un indirizzo. Richiede la conferma della password.'
    },
    fido2: {
      unlabeled: 'credenziale senza nome',
      credentialId: 'ID credenziale',
      registered: 'Registrata',
      personal: 'chiave personale (nessuna BioCard)',
      biocard: 'BioCard S/N {serial}',
      biocardStatus: { issued: 'In inventario', assigned: 'Assegnata', revoked: 'Revocata' },
      delete: 'Elimina',
      deleteTitle: 'Elimina la credenziale FIDO2',
      deleteHint:
        'Stai per eliminare la credenziale FIDO2 «{label}» di «{subject}»: le altre credenziali e il TOTP restano. L’utente riceve una e-mail di notifica, se ha un indirizzo. Richiede la conferma della password.',
      deleteHintBiocard:
        ' La credenziale è registrata sulla BioCard S/N {serial}: la riga d’inventario e l’assegnazione restano, la stessa card si può registrare di nuovo.'
    },
    external: {
      disabled: 'disabilitata',
      lookup: { supported: 'lookup disponibile', unsupported: 'lookup non disponibile' },
      lookupHelp: {
        supported:
          'Il probe ha osservato /lookup sul bridge: la registrazione del soggetto viene chiesta al bridge (se in salute).',
        unsupported:
          'Il bridge non espone /lookup (solo challenge/response, o mai provato): la registrazione a monte non si conosce da Imago; al login il round esterno è obbligatorio per chi lo deve.'
      },
      enrolled: { yes: 'Registrato sul bridge', no: 'Non registrato sul bridge', unknown: 'Sconosciuto' },
      unknownHelp: 'Il bridge non è stato interrogato, o la chiamata è fallita: {detail}',
      enroll: 'Registra',
      enrollHelp: 'La pagina di registrazione a monte indicata dal bridge (si apre in una nuova scheda).',
      notResettable:
        'Non azzerabile da Imago: la registrazione vive a monte del bridge e l’API del bridge non ha «unenroll». Si gestisce lì.'
    },
    resetAll: 'Azzera tutto',
    resetAllTitle: 'Azzera tutti i metodi',
    resetAllHint:
      'Stai per azzerare tutto ciò che Imago custodisce per «{subject}»: {held}. Le MFA esterne non vengono toccate (vivono sul bridge). L’utente si registra di nuovo al prossimo accesso e riceve una e-mail di notifica, se ha un indirizzo. Richiede la conferma della password.',
    heldTotp: 'il TOTP (segreto e codici di recupero)',
    heldFido2: '{count} credenziali FIDO2',
    resetRefused: 'Azzeramento rifiutato: {detail}',
    cleared: {
      title: 'Azzerato per «{subject}» ({method})',
      totp: 'TOTP rimosso (segreto e codici di recupero)',
      fido2: '{count} credenziali FIDO2 rimosse',
      biocardsStay: 'BioCard S/N {serials}: la riga d’inventario e l’assegnazione restano',
      nothing: 'nulla da rimuovere: l’utente non aveva questo metodo',
      notified: 'e-mail di notifica inviata all’utente',
      notNotified: 'nessuna e-mail inviata (nulla rimosso, nessun indirizzo, realm senza contatti, o invio fallito)'
    },
    effectiveTitle: 'MFA effettiva',
    effectiveHelp: 'Ciò che l’utente DEVE sotto le policy abilitate — la stessa risposta della scheda «MFA effettiva».'
  },

  // MFA esterni (ExternalMfaSection, ExternalMfaDialog, ExternalMfaProbeDialog): the
  // `/externalMfas` resource of contract/external-mfas.yaml (design §1.6, phase 3) — the
  // bridges Imago talks to, one `ext:<name>` factor each, cited by the policies. The
  // capabilities are OBSERVED by the probe; a disabled resource a policy still owes
  // makes the login fail closed (never a silent exemption).
  externalMfas: {
    title: 'Bridge MFA esterni',
    subheader:
      'I bridge che verificano un fattore esterno (SMS, push, privacyIDEA…): una risorsa per bridge, citata dalle policy come «ext:<nome>».',
    help: 'Un’MFA esterna è un bridge (endpoint + segreto condiviso) che adatta un servizio reale all’API challenge/response di Imago. Chi deve usarla lo dicono solo le policy («enforce:ext:sms» su un gruppo o un realm): qui non c’è ambito.',
    helpDetail:
      'Le capacità (lookup, enroll, cancel, multi-round) sono derivate dal probe, mai dichiarate a mano: finché un probe non riesce la risorsa non serve nessun login. Un probe periodico prova ogni bridge alla sua cadenza (default ogni 5 minuti); se l’ultimo probe riuscito è più vecchio di 20 minuti il bridge è stantio: disabilitato a livello effettivo anche se la configurazione dice «abilitato». Un bridge giù, in timeout, stantio o disabilitato mentre una policy lo deve fa fallire chiuso il login (503, senza colpo di lockout): disabilitare non esenta mai.',
    noPermission: 'La tua sessione non ha il permesso externalMfa.read: l’elenco dei bridge non è leggibile da qui.',
    new: 'Nuova MFA esterna',
    empty:
      'Nessuna MFA esterna configurata: una regola «ext:<nome>» non è citabile finché non ne crei una (qui o con create-external-mfa).',
    loadError: 'Impossibile caricare le MFA esterne.',
    colName: 'Nome',
    colEndpoint: 'Endpoint',
    colEnabled: 'Abilitata',
    colHealth: 'Salute',
    colCapabilities: 'Capacità osservate',
    colProbe: 'Ultimo probe',
    colInterval: 'Cadenza del probe',
    interval: 'ogni {seconds} s',
    intervalHelp:
      'Cadenza del probe periodico: il job prova il bridge quando l’ultimo probe è almeno così vecchio. Finestra di stantiezza fissa: {minutes} minuti.',
    probeScheduled: 'Probe immediato programmato per «{name}»: il verdetto arriva tra pochi secondi.',
    reload: 'Ricarica',
    timeout: 'timeout {ms} ms',
    enabled: 'Abilitata',
    disabled: 'Disabilitata',
    enable: 'Abilita',
    disable: 'Disabilita',
    enableTitle: 'Abilita l’MFA esterna',
    disableTitle: 'Disabilita l’MFA esterna',
    enableHint:
      'L’MFA esterna «{name}» torna a essere offerta al login dal prossimo accesso. Richiede la conferma della password.',
    disableHint:
      'L’MFA esterna «{name}» non viene più offerta: chi la deve per una policy NON entra (503 mfa_unavailable, senza colpo di lockout) finché non la riabiliti o non cambi la policy. Disabilitare non esenta mai. Richiede la conferma della password.',
    edit: 'Modifica',
    delete: 'Elimina',
    deleteTitle: 'Elimina l’MFA esterna',
    deleteHint:
      'L’MFA esterna «{name}» e il suo segreto nel keystore vengono eliminati; un round di login in corso contro il bridge termina al passo successivo. Richiede la conferma della password.',
    deleteCitedHint:
      'L’MFA esterna «{name}» è citata dalle policy {policies}: il server rifiuta l’eliminazione (409 in_use) finché una regola lo nomina. Togli prima la regola o la policy, poi riprova. Richiede la conferma della password.',
    deleteRefused: 'Eliminazione rifiutata: {detail}',
    neverProbed: 'mai provato: nessuna capacità nota, la risorsa non serve nessun login finché un probe non riesce',
    probedAt: 'osservate il {at}',
    capabilities: { lookup: 'lookup', enroll: 'enroll', cancel: 'cancel', multiRound: 'multi-round', wait: 'wait' },
    capabilityHelp: {
      lookup:
        'POST /lookup: il bridge dice se il soggetto è registrato. Con il lookup il fattore è offerto accanto a quelli locali e chi non è registrato viene rifiutato dicendolo; senza, il round esterno è obbligatorio per chi lo deve.',
      enroll: 'POST /enroll: il soggetto può registrarsi sul bridge dal proprio profilo (Imago chiede al bridge di avviare la registrazione e mostra una sola volta il link, il codice o le istruzioni che risponde).',
      cancel: 'POST /cancel: un round abbandonato viene rilasciato sul bridge.',
      multiRound: '/response può rispondere una NUOVA challenge (n giri).',
      wait: '/challenge può aprire un round WAIT ({kind: "wait"}): l’utente conferma altrove e Imago interroga /response senza testo.'
    },
    outcomes: {
      ok: 'Risponde',
      unauthorized: 'Segreto rifiutato',
      unreachable: 'Non raggiungibile',
      invalid: 'Risposta non valida'
    },
    health: {
      states: {
        ok: 'In salute',
        stale: 'Stantio',
        unreachable: 'Non raggiungibile',
        unauthorized: 'Segreto rifiutato',
        invalid: 'Risposta non valida',
        unprobed: 'Mai provato'
      },
      note: {
        ok: 'ultimo probe ok alle {at}: serve i login',
        stale: 'ultimo probe ok alle {at}, più vecchio di {minutes} minuti: disabilitato a livello effettivo',
        unprobed:
          'nessun probe dal {since}: non serve nessun login finché un probe non riesce (quello immediato è in arrivo)',
        grace:
          'ultimo probe fallito alle {since}, ultimo ok alle {at}: serve ancora i login finché la finestra di {minutes} minuti resta aperta',
        neverOk: 'nessun probe riuscito dal {since}: non serve nessun login finché un probe non riesce'
      },
      never: 'mai',
      since: 'stato dal {at}',
      lastOk: 'ultimo probe ok: {at}',
      lastOkNever: 'ultimo probe ok: mai',
      effectivelyDisabled: 'disabilitato a livello effettivo',
      effectivelyDisabledHelp:
        'L’interruttore dice «abilitato» ma la salute del bridge non serve nessun login: chi lo deve per una policy fallisce chiuso (503 mfa_unavailable) finché un probe non riesce.'
    },
    probe: {
      action: 'Prova',
      title: 'Prova MFA esterna',
      hint: 'Una sola GET /capabilities su {endpoint} col segreto memorizzato: l’esito viene registrato sulla risorsa e, se riesce, azzera l’orologio della stantiezza (lo stesso probe che il job ripete alla cadenza della risorsa). Non parte nessuna challenge (un canarino manderebbe un SMS o un push a una persona vera).',
      run: 'Prova',
      outcome: 'Esito',
      health: 'Salute dopo la prova',
      capabilities: 'Capacità osservate',
      error: 'Impossibile eseguire la prova.'
    },
    dialog: {
      createTitle: 'Nuova MFA esterna',
      editTitle: 'Modifica MFA esterna',
      name: 'Nome',
      nameHelp:
        'Il nome che le regole citano («enforce:ext:<nome>»): minuscole, cifre e trattini (1–64), inizio e fine alfanumerici; unico senza distinzione di maiuscole. Immutabile dopo la creazione.',
      nameImmutable: 'Il nome è immutabile: per rinominare, elimina e ricrea (e aggiorna le policy che lo citano).',
      nameInvalid: 'Nome non valido: minuscole, cifre e trattini, 1–64 caratteri, inizio e fine alfanumerici.',
      nameReserved: 'Nome riservato ai fattori locali (totp, fido2, any, both, local).',
      nameRequired: 'Serve il nome.',
      conflict: 'Esiste già un’MFA esterna con questo nome (le maiuscole non contano).',
      displayName: 'Etichetta nel picker',
      displayNameHelp: 'Mostrata così com’è a chi sceglie il fattore al login (max 64). Vuota = uguale al nome.',
      endpoint: 'Endpoint del bridge',
      endpointHelp:
        'URL base (schema + host [+ porta] [+ prefisso]); Imago aggiunge /api/imago-external-mfa/v1/…. Serve https; http solo su un host di loopback (127.0.0.0/8, ::1, localhost).',
      endpointRequired: 'Serve l’endpoint.',
      endpointRule: {
        malformed: 'Non è un URL.',
        scheme: 'Schema non ammesso: serve https (http solo su un host di loopback).',
        httpNotLoopback:
          'http è ammesso solo su un host di loopback (127.0.0.0/8, ::1, localhost): altrove serve https.',
        extras: 'Niente credenziali, query o frammento nell’URL.'
      },
      secret: 'Segreto condiviso (PSK)',
      secretHelp:
        'Presentato al bridge come Bearer su ogni chiamata; cifrato nel keystore, mai mostrato. Almeno {min} caratteri.',
      secretKeepHelp: 'Vuoto = invariato; scrivilo per ruotarlo. Non viene mai mostrato.',
      secretShort: 'Il segreto deve avere almeno {min} caratteri.',
      secretRequired: 'Serve il segreto (almeno {min} caratteri).',
      timeout: 'Timeout per richiesta (ms)',
      timeoutHelp:
        'Da {min} a {max} ms, per ogni chiamata al bridge. Scaduto: al login 503 mfa_unavailable, nel probe «non raggiungibile».',
      timeoutInvalid: 'Intero da {min} a {max}.',
      probeInterval: 'Intervallo di probe (s)',
      probeIntervalHelp:
        'Da {min} a {max} s (default {default}): ogni quanto il job prova il bridge. La finestra di stantiezza è fissa a {minutes} minuti.',
      probeIntervalAboveWindow:
        'Sopra {window} s il bridge risulta stantio (disabilitato a livello effettivo) tra un probe riuscito e il successivo: la risorsa serve solo subito dopo ogni probe. Ammesso, consapevolmente.',
      probeIntervalInvalid: 'Intero da {min} a {max}.',
      enabled: 'Abilitata',
      enabledHelp:
        'Disabilitata non viene né offerta né provata, e chi la deve per una policy fallisce chiuso al login: mai un’esenzione silenziosa. Riabilitarla lancia subito un probe.',
      clearsNote:
        'Stai cambiando {fields}: al salvataggio le capacità osservate e l’ultimo probe vengono azzerati (la risorsa non serve nessun login) e parte subito un probe.',
      sudoNote:
        'La creazione o la modifica richiede la conferma della password e viene registrata in audit a tuo nome. Alla creazione, e a ogni cambio di endpoint, segreto o timeout, le capacità osservate vengono azzerate e parte SUBITO un probe (una GET /capabilities al bridge, mai una challenge): finché non riesce la risorsa non serve nessun login.',
      fieldRefused: 'Campo rifiutato ({field}): {detail}',
      createError: 'Impossibile creare l’MFA esterna.',
      updateError: 'Impossibile aggiornare l’MFA esterna.'
    }
  },

  // Imago 4Legacy (design 2026-09-09, D1–D12; contract legacy-apps.yaml): the landing,
  // the catalog of legacy apps, the profile editor (grouped as the design reads), the
  // words of the closed enums, the delete note, the nginx preview and apply — the two
  // refusals explained (409 nginx_not_configured, 502 nginx_apply_failed).
  legacy: {
    landing: {
      title: 'Imago 4Legacy',
      body: 'L’utente apre l’app sul suo host pubblico (app1.imago.acme.lan), fa login su Imago coi metodi di Imago — FIDO2, TOTP, MFA esterna — e Imago lo fa entrare nell’app legacy in intranet consegnandole lo username reale e una password effimera.',
      how1: 'Il piano dati è nginx: un vhost per app, reso e applicato da Imago (auth_request su ogni richiesta, i sette header d’identità sempre del proxy).',
      how2: 'L’app verifica la password effimera sul backend effimero di Imago (LDAP o RADIUS): ogni app legacy genera il proprio consumer, riconosciuto dall’indirizzo sorgente dell’app.',
      how3: 'Il logout dal portale chiude tutte le sessioni proxy dell’utente (single logout); il logout fatto dentro l’app non chiude Imago.',
      deployHint:
        'Sull’appliance nginx, la regola sudoers e la directory imago.d sono già predisposti: resta da accendere IMAGO_4LEGACY_NGINX_MODE=apply in /etc/trantor-imago/httpd.env e da fornire certificato e DNS wildcard. L’app deve restare raggiungibile SOLO dal proxy e da Imago.',
      cta: 'Vai alle app legacy'
    },
    list: {
      subheader:
        'Le applicazioni web legacy dietro il proxy identity-aware: ognuna genera il proprio consumer effimero (LDAP o RADIUS) e il proprio vhost nginx.',
      create: 'Nuova app legacy',
      applySite: 'Applica nginx',
      empty: 'Nessuna app legacy registrata.',
      loadError: 'Impossibile caricare le app legacy.',
      colName: 'Nome',
      colPublicHost: 'Host pubblico',
      colUpstream: 'Upstream',
      colMode: 'Login',
      colState: 'Stato',
      colConsumer: 'Consumer effimero',
      enabled: 'Abilitata',
      disabled: 'Disabilitata',
      edit: 'Modifica',
      preview: 'Anteprima nginx',
      delete: 'Elimina',
      fallback: 'ripiego: {fallback}'
    },
    modes: {
      formReplay: 'Replay del form',
      injectPrefill: 'Prefill iniettato',
      httpBasic: 'HTTP Basic',
      trustedHeader: 'Trusted header'
    },
    modeHelp: {
      formReplay:
        'Imago compila il form di login dell’app lato server con la password effimera: la password non entra mai nel browser. Il default.',
      injectPrefill:
        'Uno script iniettato nella pagina di login compila utente e password effimera monouso e invia (per le SPA e i form costruiti in JavaScript).',
      httpBasic: 'nginx inietta Authorization: Basic con una credenziale legata alla sessione proxy, a ogni richiesta.',
      trustedHeader: 'L’app si fida di X-Forwarded-User e degli altri header d’identità: nessuna credenziale coniata.'
    },
    fallbacks: {
      injectPrefill: 'Prefill iniettato',
      none: 'Nessuno (l’app mostra il suo login)'
    },
    successWhen: {
      redirectOffLoginPage: 'La risposta reindirizza fuori dalla pagina di login',
      setCookie: 'La risposta imposta un cookie',
      formGone: 'Il form di login non compare più'
    },
    csps: {
      nonce: 'Riscrivi la CSP con un nonce per lo script',
      strip: 'Togli la CSP sulle pagine iniettate'
    },
    lifetimes: {
      session: 'Legata alla sessione proxy',
      oneShot: 'Monouso'
    },
    lifetimeDerived: 'Derivata dal modo: {lifetime}',
    lifetimeNone: 'Nessuna credenziale (trusted header)',
    alphabets: {
      alnum: 'Lettere e cifre',
      alnumSymbols: 'Lettere, cifre e simboli (!#$%&*+-=?@_)'
    },
    hostModes: {
      public: 'Host pubblico (nessuna riscrittura)',
      upstream: 'Host dell’upstream (con riscritture)'
    },
    backendKinds: {
      ldap: 'LDAP effimero (imago-ldapd)',
      radius: 'RADIUS effimero (imago-radiusd)'
    },
    dialog: {
      createTitle: 'Nuova app legacy',
      editTitle: 'Modifica app legacy',
      tabs: {
        general: 'Generale',
        backend: 'Backend e gruppi',
        headers: 'Header e logout'
      },
      sections: {
        network: 'Rete / Upstream',
        login: 'Login',
        headers: 'Header',
        logout: 'Logout',
        groups: 'Gruppi rilasciati',
        backend: 'Backend'
      },
      name: 'Nome',
      nameHelp:
        'Etichetta host minuscola (a-z, 0-9, trattini), immutabile: nomina il file del vhost, il consumer effimero e l’audience delle credenziali.',
      displayName: 'Nome visualizzato',
      enabled: 'Abilitata',
      enabledHelp:
        'Un’app disabilitata non rende alcun vhost (l’apply lo rimuove), rifiuta il gate e ogni credenziale; profilo e consumer restano.',
      publicHost: 'Host pubblico',
      publicHostHelp:
        'L’host che l’utente apre: server_name del vhost, coperto dal certificato wildcard e dal DNS wildcard.',
      upstreamUrl: 'URL dell’upstream',
      upstreamUrlHelp: 'L’origine dell’app come la raggiunge il proxy: schema, host e porta, senza percorso.',
      hostMode: 'Host verso l’app',
      hostModeHelp:
        'Pubblico: l’app deve accettare il suo nome pubblico, zero riscritture. Upstream: l’app vede il proprio host e il proxy riscrive redirect, cookie e corpo.',
      tlsVerify: 'Verifica il certificato dell’upstream',
      tlsCa: 'CA dell’upstream (PEM, opzionale)',
      tlsCaHelp: 'Il bundle delle CA che ancorano il certificato dell’upstream, quando lo store di sistema non le ha.',
      rewriteTitle: 'Riscritture (solo con host dell’upstream)',
      rewriteRedirects: 'Riscrivi i redirect (Location)',
      rewriteCookies: 'Togli il Domain dei cookie',
      rewriteBody: 'Tipi di contenuto riscritti nel corpo',
      rewriteBodyHelp:
        'I content type il cui corpo viene riscritto (sub_filter). Vuoto: nessuna riscrittura del corpo.',
      loginMode: 'Modo di login',
      fallback: 'Ripiego del replay',
      fallbackHelp:
        'Che cosa fa il replay quando fallisce (nessun form, POST rifiutato, criterio di successo non soddisfatto).',
      formTitle: 'Form di login dell’app',
      formHelp: 'Il descrittore che il motore di replay legge — e la pagina su cui atterra il prefill.',
      learn: 'Impara il form',
      learnHelp:
        'Imago legge la pagina di login attraverso l’upstream SALVATO e propone il blocco: nulla è salvato finché non premi Salva.',
      learnCreateHint: 'Disponibile dopo la creazione: l’apprendimento legge l’upstream salvato dell’app.',
      learnFound:
        'Form trovato: campo utente «{userField}», campo password «{passField}». Proposto il replay del form.',
      learnNoForm: 'Nessun form renderizzato dal server (costruito in JavaScript): proposto il prefill iniettato.',
      learnError: 'Impossibile leggere la pagina di login dell’app.',
      formUrl: 'Pagina di login',
      formUrlHelp: 'Il percorso della pagina di login (/login, /index.php?login).',
      formUserField: 'Campo utente',
      formUserFieldHelp: 'Il name dell’input; vuoto, il motore indovina.',
      formPassField: 'Campo password',
      formPassFieldHelp: 'Il name dell’input; vuoto, il motore indovina.',
      formSuccessWhen: 'Login riuscito quando',
      injectTitle: 'Prefill iniettato',
      injectHelp:
        'nginx accoda lo script di prefill alle pagine elencate (con un nonce CSP per richiesta): compila i selettori con utente e password effimera monouso e invia.',
      injectPages: 'Pagine iniettate',
      injectPagesHelp: 'I percorsi esatti che ricevono lo script; la pagina di login è sempre inclusa.',
      injectUserSelector: 'Selettore campo utente',
      injectPassSelector: 'Selettore campo password',
      injectSubmitSelector: 'Selettore pulsante di invio',
      injectAutoSubmit: 'Invia automaticamente',
      injectCsp: 'Content-Security-Policy',
      injectCspHelp:
        'Con «nonce» la CSP dell’app viene SOSTITUITA da una che ammette lo script: le app con script inline hanno bisogno di «strip».',
      credentialTitle: 'Credenziale effimera',
      credentialHelp:
        'Username reale, solo la password è effimera. Legata alla sessione per HTTP Basic (viaggia a ogni richiesta), monouso per replay e prefill (spesa al primo verify riuscito).',
      lifetime: 'Durata',
      lifetimeEffective: 'Durata effettiva: {lifetime}',
      ttlSeconds: 'TTL della credenziale monouso (s)',
      ttlSecondsHelp: 'Da 10 a 3600 secondi; una credenziale legata alla sessione vive quanto la sessione proxy.',
      passwordShape: 'Forma della password su misura',
      passwordShapeHelp:
        'Per un’app che limita il campo password. Spento: password generata da Imago (prefisso timec_ + 22 caratteri).',
      passwordLength: 'Lunghezza (16–64)',
      passwordAlphabet: 'Alfabeto',
      headersSet: 'Header verso l’app',
      headersSetHelp:
        'Uno per riga, Nome: valore (proxy_set_header). I sette header d’identità sono sempre del proxy: nominarli è rifiutato.',
      headersRemove: 'Header nascosti al client',
      headersRemoveHelp: 'Header di risposta nascosti (proxy_hide_header), es. X-Powered-By.',
      logoutUrl: 'Percorso di logout dell’app',
      logoutUrlHelp: 'Es. /logout, /front/logout.php. Vuoto: nessun logout dell’app chiamato.',
      onPortalLogout: 'Chiama il logout dell’app al logout dal portale',
      onPortalLogoutHelp:
        'Il logout Imago chiude comunque tutte le sessioni proxy; con questo la webui carica anche il logout dell’app, best-effort. Il logout fatto dentro l’app non chiude Imago.',
      releaseGroups: 'Gruppi rilasciati',
      releaseGroupsHelp:
        'La allowlist dei gruppi cui l’app ha diritto: rilasciati come X-Forwarded-Groups sul gate e come gruppi dell’identità sul verify effimero; copiati sul consumer generato.',
      backendKind: 'Backend effimero',
      backendKindHelp: 'Il backend su cui l’app verifica la password effimera.',
      backendSharedSecret: 'Segreto condiviso RADIUS',
      backendSharedSecretHelp:
        'Il segreto del NAS dell’app (kind radius): sigillato nel keystore del consumer generato, mai mostrato; vuoto in modifica = resta quello salvato.',
      sourceKind: 'Sorgente',
      sourceCidr: 'Intervallo sorgente',
      sourceIp: 'Indirizzo sorgente',
      sourceHelp:
        'L’indirizzo da cui l’app verifica la password effimera (il bind LDAP o la Access-Request RADIUS): da qui è accettata SOLO la credenziale effimera di quest’app, mai la password reale.',
      consumerNote:
        'Il consumer effimero «{consumer}» (LDAP o client RADIUS secondo il backend) viene generato in Connettori › Applicazioni, con questa sorgente e i gruppi rilasciati: vive e muore con l’app legacy, non si modifica né si elimina da solo.',
      notes: 'Note (opzionale)',
      notesHelp: 'Note libere dell’operatore.',
      sudoHint:
        'La scrittura di un’app legacy richiede la conferma della password. Il vhost non viene applicato qui: Anteprima nginx lo mostra, Applica nginx lo scrive e ricarica.',
      createError: 'Impossibile creare l’app legacy.',
      updateError: 'Impossibile aggiornare l’app legacy.'
    },
    form: {
      nameShape:
        'Il nome dev’essere un’etichetta host minuscola: a-z, 0-9 e trattini, non all’inizio né alla fine, al massimo 63 caratteri.',
      publicHostRequired: 'L’host pubblico è obbligatorio.',
      upstreamShape: 'L’URL dell’upstream dev’essere http(s)://host[:porta], senza percorso, query né credenziali.',
      backendSourceRequired:
        'La sorgente del backend (CIDR o indirizzo IP) è obbligatoria: è così che il backend effimero riconosce l’app.',
      headersIdentity:
        'Gli header d’identità (X-Forwarded-User, X-Forwarded-Preferred-Username, X-Forwarded-Groups, X-Imago-*, Authorization) sono sempre del proxy: non si impostano qui.',
      headersMalformed: 'Ogni header verso l’app va scritto come Nome: valore, uno per riga.',
      ttlRange: 'Il TTL della credenziale monouso va da 10 a 3600 secondi.',
      passwordLengthRange: 'La lunghezza della password va da 16 a 64 caratteri.',
      injectPagesRequired: 'Il prefill iniettato ha bisogno di almeno una pagina.'
    },
    delete: {
      title: 'Elimina app legacy',
      hint: 'Stai per eliminare l’app legacy «{name}». Con lei muoiono il consumer effimero generato «{consumer}», tutte le sessioni proxy attive e le credenziali effimere dell’app. Il vhost su disco sparisce al prossimo Applica nginx.'
    },
    nginx: {
      previewTitle: 'Anteprima nginx — {name}',
      previewHelp:
        'Il server block di questa app come lo scriverebbe l’apply (<nome>.conf): l’export per chi applica nginx a mano. Un’app disabilitata è resa con il commento che dice che non verrebbe applicata.',
      copy: 'Copia',
      copied: 'Copiato',
      apply: 'Applica nginx',
      loadError: 'Impossibile rendere il vhost.',
      applyTitle: 'Applica nginx — {name}',
      applyTitleSite: 'Applica nginx (tutto il sito)',
      applyHint:
        'Il sito è UN insieme di file (il vhost Imago più uno per app abilitata) e viene sempre scritto intero in IMAGO_4LEGACY_NGINX_DIR, poi validato con nginx -t e ricaricato con nginx -s reload tramite la regola sudoers.',
      applyConfirm: 'Applica',
      notConfigured:
        'Il driver nginx è spento su questo server (IMAGO_4LEGACY_NGINX_MODE=off, il default): nulla è stato scritto. Imposta IMAGO_4LEGACY_NGINX_MODE=apply (scrive, valida e ricarica) o dry-run (scrive soltanto) nell’ambiente di httpd, oppure applica a mano il vhost dell’anteprima.',
      applyFailed:
        'nginx ha rifiutato il sito reso: i file precedenti sono stati ripristinati, nulla è stato ricaricato. Lo stderr di nginx -t è qui sotto.',
      stderr: 'stderr di nginx',
      appliedReloaded: 'Sito scritto in {dir} ({count} file), validato con nginx -t e ricaricato (modo {mode}).',
      appliedWritten: 'Sito scritto in {dir} ({count} file) senza validazione né ricarica (modo {mode}).',
      files: 'File: {files}',
      apps: 'App rese: {apps}'
    }
  },
  language: {
    tooltip: 'Lingua'
  },
  fidoMetadata: {
    help: 'Il BLOB del FIDO Metadata Service (MDS3) e gli statement locali: un modello per AAGUID, con l’ultimo stato e il livello di certificazione. La policy FIDO2 › Metadata decide chi registra e chi accede.',
    disabled: 'MDS non configurato',
    failing: 'Ultimo aggiornamento fallito',
    healthy: 'Aggiornato',
    pending: 'Mai scaricato',
    url: 'URL del BLOB',
    blobNo: 'Numero del BLOB',
    nextUpdate: 'Prossimo aggiornamento (dal BLOB)',
    fetchedAt: 'Scaricato il',
    entries: 'Modelli dal BLOB',
    localEntries: 'Statement locali',
    refreshSeconds: 'Aggiornamento periodico',
    manualOnly: 'Solo manuale',
    everySeconds: 'Ogni {seconds} s',
    refresh: 'Aggiorna ora',
    refreshConfirm: 'Scarica e verifica il BLOB adesso: i modelli vengono sostituiti e il verificatore delle attestazioni ricaricato. Un BLOB non leggibile o non verificabile viene rifiutato e i modelli attuali restano.',
    refreshed: 'BLOB n. {blobNo} caricato: {entries} modelli.',
    refreshError: 'Aggiornamento fallito.',
    loadError: 'Impossibile leggere i metadata FIDO.',
    entriesTitle: 'Modelli di autenticatore',
    entriesHelp: 'Ordinati per descrizione; la ricerca è sulla descrizione.',
    search: 'Cerca per descrizione',
    only: 'Mostra',
    onlyAll: 'Tutti',
    onlyCertified: 'Solo certificati',
    onlyCompromised: 'Solo compromessi o revocati',
    noEntries: 'Nessun modello.',
    description: 'Modello',
    aaguid: 'AAGUID',
    status: 'Stato',
    level: 'Livello',
    source: 'Origine',
    sources: { mds: 'BLOB MDS', local: 'Statement locale' }
  },
  enumeration: {
    help: 'Ogni realm può avere UNA sorgente di enumerazione: la directory del realm o una directory LDAP propria, un provider SCIM 2.0, oppure gli utenti del realm nativo. La sincronizzazione produce il censimento (soggetti e gruppi con id stabili) che alimenta l’export SCIM e i grant e le policy per gruppo.',
    realm: 'Realm',
    noRealms: 'Nessun realm enumerabile.',
    realmNotEnumerable: 'Il realm richiesto non è enumerabile: mostrato il primo disponibile.',
    loadError: 'Impossibile leggere l’enumerazione.',
    sourceTitle: 'Sorgente di enumerazione',
    candidateTitle: 'Sorgente candidata (nell’ombra)',
    noCandidate:
      'Nessuna candidata: configura qui una sorgente di rimpiazzo, sincronizzala nell’ombra e poi fai il passaggio (cutover).',
    target: 'Sorgente da modificare',
    targetHelp:
      'La sorgente SERVITA alimenta il censimento; la CANDIDATA si sincronizza nell’ombra finché non decidi il passaggio (cutover).',
    targetServing: 'Servita',
    targetCandidate: 'Candidata',
    targetCandidateNew: 'Candidata (nuova)',
    cutover: 'Passaggio (cutover)',
    noSource: 'Nessuna sorgente configurata: scegli il tipo, imposta l’intervallo e crea.',
    sourceState: 'Stato: {state}',
    served: 'censimento servito',
    notServed: 'nessun run servito',
    kind: 'Tipo',
    kindLocked: 'Il tipo di una sorgente non cambia: ritirala e creane una nuova.',
    kindHelp: {
      ldap: 'Legge la directory del realm (URL, TLS, bind, base DN del realm) a pagine RFC 2696.',
      local: 'Legge gli utenti del realm nativo dalle tabelle di Imago (mai il break-glass).',
      scim: 'Legge un provider SCIM 2.0 a monte con un bearer proprio, a pagine RFC 7644; su qualsiasi realm esterno (l’enumerazione è ortogonale all’autenticazione).'
    },
    identity: {
      title: 'Identità di autenticazione',
      help: 'Lo spazio in cui gli id di autenticazione del realm sono confrontabili nel tempo: ogni accesso verificato lega il proprio id a un soggetto stabile (quello del censimento quando la sorgente lo permette).',
      none: 'Nessuno spazio attivo: nasce al primo accesso o al prossimo avvio.',
      active: 'Spazio attivo: {authority} — {links} identità legate, dal {when}',
      candidate: 'L’autorità è cambiata ({authority}): uno spazio candidato è aperto con {links} link. Gli accessi risolvono solo attraverso i link ri-chiavati (nessun soggetto nuovo) finché non fai il passaggio.',
      rekey: 'Ri-chiavatura',
      cutover: 'Passaggio dello spazio (cutover)',
      rekeyReport: 'Ri-chiavatura fatta: {entries} entry lette, {mapped} con entrambi gli id, {copied} link copiati, {orphans} link orfani, {sources} link di sorgente spostati.',
      unresolved: 'Righe legacy senza soggetto stabile: {enrollments} registrazioni MFA, {credentials} credenziali FIDO2, {biocards} BioCard (si risolvono al prossimo accesso del titolare).'
    },
    interval: 'Intervallo (s)',
    intervalHelp: '0 = solo sincronizzazioni manuali; altrimenti almeno 60 secondi.',
    ldap: {
      userFilter: 'Filtro utenti',
      groupFilter: 'Filtro gruppi',
      userNameAttribute: 'Attributo userName',
      groupNameAttribute: 'Attributo nome gruppo',
      memberAttribute: 'Attributo membri',
      pageSize: 'Entry per pagina',
      maxSubjects: 'Massimo soggetti',
      connectionMode: 'Connessione',
      connectionModeHelp: 'La directory del realm (URL, TLS, bind, base DN del realm) oppure una directory PROPRIA della sorgente.',
      ownRequired: 'Su un realm non LDAP la sorgente legge una directory propria: l’enumerazione è ortogonale all’autenticazione.',
      realmConnection: 'La connessione del realm',
      ownConnection: 'Una directory propria',
      urls: 'URL della directory',
      urlsHelp: 'Uno per riga (ldaps://host:636 o ldap://host), provati in ordine.',
      baseDn: 'Base DN',
      bindDn: 'Bind DN del servizio',
      bindDnHelp: 'Vuoto = bind anonimo.',
      bindPassword: 'Password del bind',
      bindPasswordHelp: 'Obbligatoria alla creazione con un bind DN; sigillata nel keystore, mai mostrata.',
      bindPasswordKeep: 'Vuota = resta quella sigillata.',
      tls: 'TLS',
      tlsVerify: 'ldaps:// con verifica del certificato',
      tlsStartTls: 'STARTTLS su ldap://',
      tlsNoVerify: 'Senza verifica del certificato (solo laboratorio)',
      caCertificate: 'CA della directory (PEM, vuoto = store di sistema)',
      uuidAttribute: 'Attributo dell’id immutabile',
      displayNameAttribute: 'Attributo nome visualizzato',
      mailAttribute: 'Attributo e-mail',
      phoneAttribute: 'Attributo telefono'
    },
    scim: {
      baseUrl: 'URL base del provider',
      baseUrlHelp: 'https://host/scim/v2 — http solo su un host di loopback; niente query né credenziali nell’URL. I redirect non sono mai seguiti.',
      bearerToken: 'Bearer token',
      bearerTokenHelp: 'Obbligatorio alla creazione; sigillato nel keystore, mai mostrato.',
      bearerTokenKeep: 'Vuoto = resta quello sigillato.',
      verifyCertificate: 'Verifica il certificato',
      verifyYes: 'Sì (consigliato)',
      verifyNo: 'No — solo loopback e laboratorio',
      caCertificate: 'CA del provider (PEM)',
      caCertificateHelp: 'Vuoto = lo store di sistema.',
      userFilter: 'Filtro SCIM utenti',
      groupFilter: 'Filtro SCIM gruppi',
      pageSize: 'Risorse per pagina (10–500)',
      maxSubjects: 'Massimo soggetti',
      timeoutSeconds: 'Timeout per richiesta (s)',
      pagination: 'Paginazione',
      paginationHelp: 'Auto = cursore RFC 9865 quando il provider lo annuncia, altrimenti indice.',
      paginationAuto: 'Automatica',
      paginationIndex: 'Indice (startIndex/count)',
      paginationCursor: 'Cursore (RFC 9865)',
      mappingHelp: 'Mapping degli attributi: per ogni campo i percorsi SCIM provati in ordine, separati da virgola (es. name.formatted, emails[type eq "work"].value, urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:employeeNumber). Vuoto = mapping fisso.',
      mapping: { userName: 'Nome utente', displayName: 'Nome visualizzato', mail: 'E-mail', phone: 'Telefono', active: 'Attivo', groupDisplayName: 'Nome del gruppo' },
      mappingDefault: { userName: 'userName', displayName: 'displayName, name.formatted, userName', mail: 'emails[primary eq true].value, emails.value', phone: 'phoneNumbers.value', active: 'active', groupDisplayName: 'displayName' }
    },
    create: 'Crea la sorgente',
    save: 'Salva',
    test: 'Prova',
    sync: 'Sincronizza ora',
    retire: 'Ritira',
    probeOk: 'La sorgente risponde',
    probeKo: 'La sorgente NON risponde',
    runsTitle: 'Sincronizzazioni',
    runsHelp: 'Le ultime sincronizzazioni, dalla più recente; «attivo» = il run da cui è servito il censimento.',
    noRuns: 'Nessuna sincronizzazione ancora.',
    run: { started: 'Avviata', state: 'Stato', trigger: 'Innesco', subjects: 'Soggetti', groups: 'Gruppi', error: 'Errore', active: 'attivo' },
    subjectsTitle: 'Soggetti',
    subjectsHelp: 'Il censimento servito: id stabile di Imago, nome utente, nome, stato, gruppi (anche transitivi).',
    subjectSearch: 'Cerca per nome utente',
    noSubjects: 'Nessun soggetto nel censimento.',
    subject: { userName: 'Nome utente', displayName: 'Nome', active: 'Attivo', groups: 'Gruppi', yes: 'Sì', no: 'No' },
    groupsTitle: 'Gruppi',
    groupsHelp: 'I gruppi del censimento con i membri diretti (soggetti e gruppi annidati).',
    noGroups: 'Nessun gruppo nel censimento.',
    group: { name: 'Nome', members: 'Membri' },
    dialog: {
      save: { title: 'Salva la sorgente di enumerazione', hint: 'La configurazione della sorgente del realm «{realm}» è protetta da sudo; una sincronizzazione parte subito dopo.', confirm: 'Salva' },
      retire: { title: 'Ritira la sorgente', hint: 'Il censimento del realm «{realm}» smette di essere servito (la storia e gli id stabili restano). Conferma con la password.', confirm: 'Ritira' },
      test: { title: 'Prova la sorgente', hint: 'Connessione, bind e una pagina per filtro sul realm «{realm}»: nessuna enumerazione completa.', confirm: 'Prova' },
      sync: { title: 'Sincronizza ora', hint: 'Avvia una sincronizzazione completa del realm «{realm}»; il run prosegue nel server.', confirm: 'Sincronizza' },
      rekey: { title: 'Ri-chiavatura delle identità', hint: 'Legge la directory del realm «{realm}» con l’attributo vecchio e quello nuovo e, in una transazione, copia i link nello spazio candidato, sposta i link delle sorgenti di enumerazione e riscrive le righe MFA/FIDO2/BioCard/duress. Ripetibile.', confirm: 'Ri-chiava' },
      nsCutover: { title: 'Passaggio dello spazio d’identità (cutover)', hint: 'Lo spazio candidato del realm «{realm}» diventa quello attraverso cui ogni login risolve; quello attivo si ritira con i suoi link come storia. Senza ri-chiavatura (candidato senza link) le identità ripartono da zero.', confirm: 'Passaggio' }
    }
  },
  scimExport: {
    title: 'Export SCIM di «{app}»',
    help: 'L’app legge utenti e gruppi del censimento di UN realm via SCIM 2.0 (/scim/v2, sola lettura) con un token proprio. Lo scope è tutto il realm o i gruppi scelti (e i loro utenti); nessun gruppo scelto = zero utenti.',
    loadError: 'Impossibile leggere l’export SCIM.',
    realm: 'Realm',
    realmHelp: 'Un realm con una sorgente di enumerazione servita.',
    realmLocked: 'Il realm di un export non cambia: ritira l’export e creane uno nuovo.',
    realmRequired: 'Scegli il realm.',
    enabled: 'Export abilitato',
    includeAll: 'Tutti gli utenti e i gruppi del realm',
    censusOk: 'censimento servito',
    censusMissing: 'nessun censimento servito: sincronizza l’enumerazione del realm',
    generation: 'Istantanea servita: {users} utenti ({inactive} disattivati), {groups} gruppi — costruita il {when}',
    noGeneration:
      'Nessuna istantanea servita: il protocollo risponde 503 finché il censimento del realm non è promosso',
    groupsHelp: 'I gruppi del censimento da pubblicare (con gli utenti che contengono, anche annidati).',
    noGroups: 'Nessun gruppo nel censimento del realm scelto.',
    emptyScope: 'Nessun gruppo scelto: l’export pubblica zero utenti finché non ne scegli uno o abiliti «tutti».',
    create: 'Crea l’export',
    save: 'Salva',
    retire: 'Ritira l’export',
    tokensTitle: 'Token',
    tokensHelp: 'Il bearer che il client SCIM presenta: mostrato UNA volta alla creazione, conservato solo come hash; più token vivi per la rotazione.',
    mintedOnce: 'Token creato: copialo ora, non sarà più mostrato.',
    tokenLabel: 'Etichetta',
    tokenLifetime: 'Durata (giorni)',
    mint: 'Crea token',
    revoke: 'Revoca',
    noTokens: 'Nessun token.',
    token: { label: 'Etichetta', created: 'Creato', expires: 'Scade', lastUsed: 'Ultimo uso', state: 'Stato', live: 'vivo', revoked: 'revocato' },
    dialog: {
      save: { title: 'Salva l’export SCIM', hint: 'La configurazione dell’export di «{app}» è protetta da sudo.', confirm: 'Salva' },
      retire: { title: 'Ritira l’export SCIM', hint: 'L’export di «{app}» e TUTTI i suoi token vengono eliminati: il client SCIM smette di leggere.', confirm: 'Ritira' },
      mint: { title: 'Crea un token', hint: 'Il token dell’export di «{app}» sarà mostrato una sola volta.', confirm: 'Crea' },
      revoke: { title: 'Revoca il token', hint: 'Il token «{label}» smette di valere subito; i metadati restano nel registro.' }
    }
  },
  approvals: {
    title: 'Richieste di accesso',
    help: 'Un’applicazione ha chiesto di accedere a nome tuo (OIDC CIBA): approva solo se sei tu ad averlo avviato e il messaggio coincide con quello che l’applicazione mostra.',
    pending: 'in attesa',
    bindingMessage: 'Messaggio di verifica',
    noBindingMessage: 'L’applicazione non ha allegato un messaggio di verifica: approva solo se stai usando quell’applicazione proprio ora.',
    details: 'Scope: {scope} · chiesto alle {requestedAt} · scade alle {expiresAt}',
    approve: 'Approva',
    deny: 'Rifiuta',
    approveTitle: 'Approva la richiesta di accesso',
    approveHint: 'Approvando, «{app}» riceve un accesso a nome tuo (messaggio: {bindingMessage}). Conferma con la tua password.',
    denyTitle: 'Rifiuta la richiesta di accesso',
    denyHint: '«{app}» riceverà un rifiuto (messaggio: {bindingMessage}). Conferma con la tua password.',
    loadError: 'Impossibile leggere le richieste di accesso.'
  },
  windowsCode: {
    title: 'Accesso Windows (RDP)',
    subheader:
      'Un codice monouso da digitare nel campo codice del Credential Provider Imago dentro la sessione RDP, al posto del secondo fattore: la chiave FIDO2 non arriva al desktop remoto.',
    none: 'Nessun computer: serve un computer con il Credential Provider Imago attivo e un abbinamento che ti ammetta.',
    computer: 'Computer',
    mint: 'Ottieni codice',
    sudoTitle: 'Codice per accesso Windows',
    sudoHint:
      'Il codice vale cinque minuti per il computer «{computer}» e viene mostrato una volta sola. Conferma la tua password.',
    issuedHint: 'Per «{computer}», scade tra {seconds} s. Digitalo nel campo codice del Credential Provider.',
    loadError: 'Impossibile leggere i tuoi computer.',
    mintError: 'Impossibile emettere il codice.'
  },
  duress: {
    pageHelp:
      'Un utente può registrare un suffisso segreto da digitare dopo la propria password quando è costretto: il login riesce come un login normale e l’allarme parte senza che lo veda (riga duressLogin nel registro, SIEM).',
    enabled: 'Anti-coercizione abilitata',
    enabledHelp:
      'Spento: il suffisso non viene mai consultato e la scheda nel profilo è nascosta. Acceso: gli utenti possono registrare il suffisso dal profilo e ogni login con password lo riconosce.',
    revokeSessions: 'Un login sotto coercizione chiude le sessioni precedenti',
    revokeSessionsHelp:
      'Le sessioni aperte prima della coercizione vengono chiuse subito; la sessione del login sotto coercizione resta viva, perché chi costringe deve vedere un successo.',
    saveTitle: 'Salva le impostazioni anti-coercizione',
    saveHint: 'Conferma con la tua password: la modifica della policy è protetta da sudo.',
    saved: 'Impostazioni salvate.',
    eventsTitle: 'Allarmi',
    eventsHelp: 'I login sotto coercizione registrati (evento duressLogin), dal più recente. L’utente non ne viene mai informato.',
    noEvents: 'Nessun allarme registrato.',
    colAt: 'Quando',
    colChannel: 'Canale',
    colUser: 'Utente',
    colRealm: 'Realm',
    colIp: 'Indirizzo',
    loadError: 'Impossibile caricare le impostazioni anti-coercizione.',
    card: {
      title: 'Suffisso anti-coercizione',
      help:
        'Un suffisso segreto da digitare dopo la tua password se sei costretto ad accedere: il login riesce normalmente e l’allarme parte senza che chi ti costringe se ne accorga.',
      enrolled: 'Suffisso registrato ({length} caratteri). Non viene mai mostrato.',
      notEnrolled: 'Nessun suffisso registrato.',
      set: 'Registra il suffisso',
      replace: 'Sostituisci il suffisso',
      clear: 'Rimuovi',
      setTitle: 'Registra il suffisso anti-coercizione',
      setHint:
        'Da 4 a 20 caratteri, senza spazi, non un carattere ripetuto. Scegline uno con cui nessuna tua password vera finisce: una password che finisce col suffisso viene giudicata sulla parte restante e rifiutata.',
      suffixLabel: 'Suffisso',
      suffixAgain: 'Ripeti il suffisso',
      sudoPassword: 'Conferma con la tua password',
      mismatch: 'I due suffissi non coincidono.',
      setError: 'Impossibile registrare il suffisso.',
      clearTitle: 'Rimuovi il suffisso anti-coercizione',
      clearHint: 'Senza suffisso un login sotto coercizione non può più essere segnalato.',
      loadError: 'Impossibile leggere lo stato del suffisso anti-coercizione.'
    }
  },
  about: {
    subheader: 'Il nodo che serve questa console.',
    version: 'Versione della piattaforma',
    contractVersion: 'Versione del contratto API',
    nodeName: 'Nome del nodo',
    startedAt: 'Processo avviato il',
    checksTitle: 'Controlli di salute',
    checks: { database: 'Database', redis: 'Redis', realms: 'Realm' },
    ok: 'ok',
    failing: 'in errore',
    loadError: 'Impossibile leggere le informazioni del nodo.'
  },
  events: {
    subheader:
      'Il registro ristretto a ciò che il sistema e gli amministratori hanno fatto: configurazioni, apply di nginx, sincronizzazioni, metadata FIDO, CA EAP, token SCIM, probe. I login e i fattori degli utenti restano in «Audit».'
  }
}

const enUS: typeof itIT = {
  login: {
    usernameHelper: 'user (“local” realm), user@realm or realm\\user',
    externalEnrollmentRequired:
      'Your account requires the external MFA “{name}”, on which you are not enrolled: enrol on the external portal first, then try again.',
    externalEnrollmentLink: 'Go to the enrollment',
    deviceCodeLink: 'I have a device code',
    stepUpRejected: 'The code was not accepted.',
    stepUpFido2Failed: 'Security-key verification cancelled or failed. Enter a code.',
    welcome: 'Welcome to Trantor Imago',
    heading: 'Sign in to your account.',
    headingOtp: 'Enter the code of your second factor.',
    username: 'Username',
    usernamePlaceholder: 'user, user@realm or realm\\user',
    continueButton: 'Continue',
    passkeyButton: 'Sign in with a passkey',
    changeUser: 'Change user',
    password: 'Password',
    submit: 'Sign in',
    securityKeyButton: 'Sign in with a security key',
    chooseFactor: 'This account has more than one verification method. Choose how to continue.',
    otpLabel: 'Code',
    otpHelpTotp: 'Enter a TOTP code or a recovery code.',
    otpHelpFactor: 'Enter the code of “{name}”.',
    verify: 'Verify',
    forgotPasswordLink: 'Forgot your password?',
    passkeyFailed: 'Passkey sign-in cancelled or failed. Enter your username.',
    securityKeyFailed: 'Security-key sign-in cancelled or failed. Use your password.',
    noSecurityKey: 'No security key available for this user. Use your password.',
    federationExpired: 'The federated session has expired, try again.',
    federationFailed: 'Federated sign-in failed.',
    factors: {
      totp: 'Authenticator app (TOTP)',
      fido2: 'FIDO2 card or token'
    },
    round: {
      headingCredential: 'The authentication server asks for one more step.',
      headingFactor: 'Answer the external MFA’s request.',
      headingWait: 'Confirm the external MFA’s request.',
      fromServer: 'Request from the server',
      fromFactor: 'Request from “{name}”',
      step: 'step {round}',
      answer: 'Answer',
      answerHelpCredential:
        'Answer as indicated above. Not a second factor: a step of the password verification, decided by the realm’s server.',
      answerHelpFactor: 'Answer as indicated above: the answer goes to “{name}”.',
      submit: 'Continue',
      openLink: 'Open the verification page',
      userCode: 'Code to enter',
      copyCode: 'Copy the code',
      copied: 'Copied',
      waiting: 'Waiting for the confirmation…',
      waitingCadence: 'Checking every {seconds} s.',
      waitingDeadline: 'The request expires at {at}.',
      expired: 'The request expired without a confirmation: nothing was judged, no lockout hit.',
      retry: 'Try again',
      rejectedCredential: 'The answer was not accepted by the realm’s server. Log in again from the password.',
      rejectedFactor:
        'The request was not accepted by “{name}” (answer refused, or round expired). Log in again from the password.',
      rejectedStepUp:
        'The request was not accepted by “{name}” (answer refused, or round expired). Choose again how to verify.'
    }
  },
  auth: {
    errors: {
      badCredentials: 'Invalid credentials.',
      rateLimited: 'Too many attempts. Try again later.',
      directoryUnavailable: 'Directory unavailable. Try again later.',
      unavailable: 'Service unavailable: the directory or the external MFA is not answering. Try again later.',
      mfaUnavailable:
        'The external MFA your account owes cannot be served right now ({detail}). This is not a credential failure: try again shortly.',
      mfaRequired:
        'Your account requires a second factor that cannot be verified from here (no usable factor on this channel, or an external factor not enrolled).',
      roundBudgetExhausted:
        'Too many external MFA rounds started for this login: answer the request already open or start over from the password.',
      generic: 'Could not complete the login.'
    },
    forgot: {
      title: 'Forgotten password',
      titleOtp: 'Lost authenticator',
      linkOtp: 'I lost my authenticator',
      username: 'Username',
      usernamePlaceholder: 'user (“local” realm), user@realm or realm\\user',
      usernameHelp:
        'Three forms: “user” (the “local” realm), “user@realm” or “realm\\user” — both qualified forms name the realm.',
      uniform:
        'If the account exists, you will receive an e-mail with a one-time code: enter it below together with the new password.',
      uniformOtp:
        'If the account exists, you will receive an e-mail with a one-time code: enter it below to remove the authenticator.',
      otpConsequence:
        'The current authenticator is removed: at the next login you will be asked to enroll a new one. If you still have a recovery code, use it in place of the second factor instead of this procedure.',
      haveCode: 'I already have a code',
      code: 'Received code',
      codePlaceholder: 'timrc_…',
      codeHelp: 'The one-time code received by e-mail: it lasts a few minutes and one attempt, right or wrong.',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      mismatch: 'The passwords do not match.',
      complete: 'Confirm',
      donePassword: 'Password reset. You can now log in with the new password.',
      doneOtp: 'Authenticator removed. At the next login you will enroll a new second factor.',
      codeRejected: 'Invalid, expired or already used code. Request a new code.',
      completeError: 'Could not complete the recovery.',
      back: 'Back',
      close: 'Close',
      submit: 'Send'
    }
  },
  nav: {
    home: 'Home',
    profile: 'Profile',
    connectors: 'Connectors',
    connectorsServices: 'Services',
    connectorsApplications: 'Applications',
    connectorsDot1x: '802.1X',
    realms: 'Realms',
    mfa: 'MFA',
    legacy: 'Imago 4Legacy',
    biocards: 'Imago BioCard',
    computers: 'Computers',
    system: 'System',
    policy: 'Policy',
    audit: 'Audit',
    events: 'System events',
    localUsers: 'Local users',
    localGroups: 'Local groups',
    enumeration: 'Enumeration',
    fidoMetadata: 'FIDO Metadata Service',
    rbacGrants: 'Roles & grants',
    duress: 'Anti-coercion',
    node: 'Node',
    about: 'About'
  },
  sectionSummary: {
    home: 'Your account and quick actions',
    profile: 'Your credentials and authentication factors',
    connectors: 'Services, applications and 802.1X exposed to users',
    realms: 'Native realms, LDAP/AD directories, federated IdPs and RADIUS servers',
    mfa: 'MFA enforcement policies and external factors',
    legacy: 'Legacy apps behind the proxy: Imago login, ephemeral password, generated nginx vhosts',
    biocards: 'Biometric smart card management',
    computers: 'Computers with PBA and Credential Provider: registration, enablements, pairings',
    policy: 'Access and security rules',
    audit: 'The full register: logins, factors, recoveries and configuration changes, on every channel',
    events: 'What the system and its administrators did: configuration, applies, syncs, probes',
    localUsers: 'Imago’s own users in the native “local” realm',
    localGroups: 'Groups of local users, recognised by grants, MFA policies and VLAN rules',
    enumeration: 'Each realm’s enumeration source and the census it yields: subjects and groups with stable ids',
    fidoMetadata: 'The FIDO authenticator models the server knows (the FIDO Metadata Service BLOB and local statements), with status and certification level',
    rbacGrants:
      'Who administers Imago: roles (system and custom) and grants to groups or users of the realms, with target and delegation',
    duress: 'Secret suffix typed under coercion: normal login, silent alarm',
    node: 'The management network, the update mirror and the 802.1X frontend of this node',
    about: 'Version, node and health checks'
  },
  common: {
    contractError: 'The server response does not match the contract: {field} = {value}',
    cancel: 'Cancel',
    create: 'Create',
    save: 'Save',
    delete: 'Delete',
    all: 'All',
    previous: 'Previous',
    next: 'Next',
    actions: 'Actions',
    close: 'Close',
    sudoPassword: 'Confirm with your password',
    remove: 'Remove',
    sudoWhy: 'Every write is confirmed with your own password and recorded in the audit under your name.',
    actionFailed: 'The operation failed.',
    howItWorks: 'How it works',
    problems: {
      immutableField: 'The field “{field}” is immutable: it cannot be changed after creation.',
      validation: 'Value not accepted — {fields}',
      validationField: '{field}: {message}',
      inUseCount: 'Deletion refused: the object is still in use ({count} references). {detail}',
      inUse: 'Deletion refused: the object is still in use. {detail}'
    }
  },
  chips: {
    hint: 'Enter to add an entry; pasting several lines adds one per line.'
  },
  noPermission: {
    title: 'Permission missing',
    body: 'Your session does not hold the permission to see this page. Roles resolve at login: if you were granted a role recently, log out and back in.',
    required: 'Required permission: {permission}'
  },
  audit: {
    subheader:
      'The audit register of Trantor Imago: logins, MFA, enrollments, recovery and configuration changes, on every channel.',
    filterChannel: 'Channel',
    filterEvent: 'Event',
    filterOutcome: 'Outcome',
    filterTargetKind: 'Object',
    filterUsername: 'User',
    filterUsernamePlaceholder: 'Search in the username…',
    filterRealm: 'Realm',
    filterRealmPlaceholder: 'Search in the realm name…',
    filterActor: 'Administrator (actor)',
    filterActorPlaceholder: 'Search in the administrator’s name…',
    filterSince: 'From',
    filterUntil: 'To',
    filterRangeHelp: 'Browser local time.',
    advanced: 'Advanced filters',
    filterExternalUuid: 'External UUID',
    filterExternalUuidPlaceholder: 'Exact objectGUID / entryUUID',
    filterClientIp: 'Client IP',
    filterClientIpPlaceholder: 'Exact address, e.g. 10.0.0.7',
    filterNode: 'Node',
    filterNodePlaceholder: 'Exact node name',
    filterActorRealm: 'Administrator’s realm',
    filterActorRealmPlaceholder: 'Exact realm name',
    filterTargetId: 'Object ID',
    filterTargetIdPlaceholder: 'Exact object identifier',
    filterTargetName: 'Object name',
    filterTargetNamePlaceholder: 'Search in the object name…',
    colAt: 'Date and time',
    colChannel: 'Channel',
    colEvent: 'Event',
    colOutcome: 'Outcome',
    colUsername: 'User',
    colRealm: 'Realm',
    colIp: 'IP',
    empty: 'No audit record.',
    emptyFiltered: 'No audit record for the selected filters.',
    loadError: 'Could not load the audit register.',
    node: 'Node',
    actor: 'Actor',
    sudo: 'sudo',
    target: 'Object',
    reason: 'Reason',
    declaredRealm: 'declared realm: “{realm}”',
    revokedCount: '{count} sessions revoked',
    detail: 'Detail',
    channels: {
      portal: 'Portal (native API)',
      oidc: 'OIDC',
      saml: 'SAML',
      pi: 'privacyIDEA',
      radius: 'RADIUS',
      ldap: 'LDAP',
      proxy: 'Identity-aware proxy',
      pba: 'PBA (pre-boot gate)',
      cp: 'Credential Provider (Windows)',
      eap: '802.1X (EAP)',
      system: 'System',
      scim: 'SCIM (app exports)'
    },
    events: {
      login: 'Login succeeded',
      loginFailed: 'Login refused',
      mfaChallenge: 'Second factor demanded',
      mfaAccepted: 'Second factor verified',
      mfaFailed: 'Second factor refused',
      lockoutTripped: 'Lockout tripped',
      sudoElevated: 'Sudo elevation granted',
      sudoFailed: 'Sudo elevation refused',
      sessionRevoked: 'Session revoked',
      enrollmentStarted: 'Enrollment started',
      enrollmentConfirmed: 'Enrollment confirmed',
      enrollmentCleared: 'Enrollment removed',
      recoveryStarted: 'Recovery started',
      recoveryCompleted: 'Recovery completed',
      recoveryFailed: 'Recovery refused',
      passwordChanged: 'Password changed by the user',
      passwordResetPerformed: 'Password set for the user (recovery or administrative reset)',
      configCreated: 'Configuration created',
      configUpdated: 'Configuration updated',
      configDeleted: 'Configuration deleted',
      externalMfaProbe: 'External MFA health changed',
      computerActivated: 'Computer activated',
      eapCaInitialized: 'Imago CA initialized',
      eapCertIssued: 'EAP-TLS certificate issued',
      eapCertRevoked: 'EAP-TLS certificate revoked',
      legacyAppCreated: 'Legacy app created (with its ephemeral consumer)',
      legacyAppUpdated: 'Legacy app updated',
      legacyAppDeleted: 'Legacy app deleted (with its consumer, proxy sessions and credentials)',
      legacyNginxApplied: '4Legacy nginx site written (and reloaded in apply mode)',
      legacyNginxFailed: '4Legacy nginx site refused (previous files restored)',
      legacyLogin: 'Login to a legacy app (proxy session minted)',
      legacyCredentialRefused: 'Ephemeral credential refused on the ephemeral verify (LDAP or RADIUS)',
      nodeApplied: 'Node configuration applied (network, mirror, 802.1X)',
      nodeApplyFailed: 'Node configuration refused or failed (the helper’s own words in the detail)',
      auditSwept: 'Audit register swept (retention)',
      totpEnrollmentExpired: 'Never-confirmed TOTP enrollment expired and deleted',
      enrollmentRefused: 'Factor enrollment refused: network not allowed (policy enrollment.allowedCidrs)',
      externalMfaEnrollmentStarted: 'Enrollment on an external MFA started from the profile (the bridge answered)',
      deviceCodeStarted: 'Device code issued (identity round opened)',
      deviceCodeVerified: 'Device code approved by the subject',
      deviceCodeRefused: 'Device code refused on the verification page',
      deviceCodeExpired: 'Device code expired with no approval',
      windowsCodeIssued: 'Windows one-time code issued',
      duressLogin: 'Login under duress (alarm)',
      samlAssertionIssued: 'SAML assertion issued to the service provider',
      backchannelRequested: 'Backchannel (CIBA) access request received',
      backchannelApproved: 'Backchannel request approved by the user',
      backchannelDenied: 'Backchannel request denied by the user',
      enumerationSynced: 'Enumeration synced (census promoted)',
      enumerationSyncFailed: 'Enumeration sync failed',
      scimTokenCreated: 'SCIM token created',
      scimTokenRevoked: 'SCIM token revoked',
      scimAccessDenied: 'SCIM access denied',
      subjectLinked: 'Identity linked to its Subject',
      fidoMetadataRefreshed: 'FIDO metadata refreshed',
      fidoMetadataRefreshFailed: 'FIDO metadata refresh failed'
    },
    outcomes: {
      succeeded: 'Succeeded',
      denied: 'Denied',
      rejected: 'Rejected',
      failed: 'Failed',
      accepted: 'Accepted'
    },
    missingFactors: 'Missing factors: {factors}',
    viaPolicies: 'Policies: {policies}',
    owedFactors: 'Owed factors: {factors}',
    onChannel: 'Channel: {channel}',
    externalMfa: 'External MFA: {name}',
    externalDisplayName: 'label: “{name}”',
    enrollmentUrl: 'enrollment: {url}',
    externalDetail: 'detail: {detail}',
    challengeMethod: 'Method',
    challengeRound: 'round {round}',
    secondFactor: 'Second factor',
    mfaNone: 'none (password alone: nothing owed)',
    mfaPending: 'pending: the session is at the enrollment gate',
    roundLabel: 'Interactive round',
    roundProducer: 'producer: {producer}',
    roundKind: 'kind: {kind}',
    roundNumber: 'round {round}',
    roundTransaction: 'round transaction: {id}',
    roundOpened: 'fresh rounds opened: {opened} of {budget}',
    roundCredentialBudget: 'credential steps: {rounds} of {budget}',
    roundRequiredHint:
      'code sent for an external method with no open round: a proof reaches the bridge only through its round',
    producers: {
      credential: 'credential (the realm’s RADIUS server)',
      factor: 'factor (the external MFA bridge)',
      identity: 'identity (the Imago broker’s device-code, federated realm)'
    },
    kinds: {
      answer: 'answer (typed answer)',
      wait: 'wait (confirmation elsewhere, poll)'
    },
    credentialRounds: 'Credential steps (RADIUS): {rounds}',
    realmKind: 'realm kind: {kind}',
    refusalError: 'detail: {error}',
    deviceCode: 'Device code',
    deviceRequested: 'subject asked for: {subject}',
    deviceApprovedAs: 'subject who answered: {subject}',
    deviceApprovedBy: 'approval: {how}',
    deviceApprovals: {
      login: 'login completed on the verification page',
      session: 'IdP session already authenticated as the subject (no login asked)'
    },
    deviceChannel: 'device channel: {channel}',
    deviceComputer: 'computer: {computer}',
    deviceExpiresAt: 'code deadline: {at}',
    deviceThrottlePerIp: 'per-IP window exhausted',
    deviceThrottlePerCode: 'per-code window exhausted',
    deviceRetryAfter: 'retry in {seconds} s',
    identityLabel: 'Identity',
    identities: {
      deviceCode: 'device code approved on the broker’s verification page (identity round)'
    },
    healthTransition: 'Health',
    probeTrigger: 'trigger: {trigger}',
    probeVerdict: 'probe verdict: {probe}',
    lastOkAt: 'last ok probe: {at}',
    lastOkNever: 'last ok probe: never',
    staleBridge: 'stale bridge: disabled at the effective level (the configuration still says “enabled”)',
    observationsCleared: 'capabilities and last probe cleared (endpoint, secret or timeout changed)',
    probeScheduled: 'immediate probe scheduled',
    probeDiscarded: 'verdict discarded: {reason}',
    factorReset: 'Reset',
    factorResetSubject: 'subject: {subject}',
    factorResetTotp: 'TOTP removed (secret and recovery codes)',
    factorResetFido2: '{count} FIDO2 credentials removed',
    factorResetBiocards: 'BioCard S/N {serials} (the inventory stays)',
    factorResetNothing: 'nothing removed',
    factorResetNotified: 'notification e-mail sent',
    factorResetNotNotified: 'no e-mail sent',
    factorMethods: {
      totp: 'TOTP',
      all: 'every method',
      fido2: 'FIDO2 credential',
      external: 'external MFA'
    },
    legacyLabel: 'Imago 4Legacy',
    legacyApp: 'app: {app}',
    legacyMode: 'login mode: {mode}',
    legacyCredential: 'credential: {credential}',
    legacyEngine: 'engine: {engine}',
    legacyReplay: 'form replay: {replay}',
    legacyFallback: 'fallback: {fallback}',
    legacyConsumer: 'ephemeral consumer: {consumer}',
    legacyFields: 'fields changed: {fields}',
    legacyNginxMode: 'nginx driver mode: {mode}',
    legacyNginxDir: 'directory: {dir}',
    legacyNginxFiles: 'files: {files}',
    legacyNginxApps: 'apps rendered: {apps}',
    legacyNginxReloaded: 'nginx validated and reloaded',
    legacyNginxStderr: 'nginx stderr',
    legacyProxySessions: '{count} proxy sessions ended',
    legacyLogoutApps: 'legacy apps: {apps}',
    legacyModes: {
      formReplay: 'form replay (server-side)',
      injectPrefill: 'injected prefill (script on the login page)',
      httpBasic: 'HTTP Basic',
      trustedHeader: 'trusted header (no credential)'
    },
    legacyCredentials: {
      session: 'bound to the proxy session',
      oneShot: 'one-shot',
      none: 'none'
    },
    legacyReplays: {
      ok: 'succeeded',
      failed: 'failed'
    },
    healthStates: {
      ok: 'healthy',
      stale: 'stale',
      unreachable: 'unreachable',
      unauthorized: 'secret refused',
      invalid: 'invalid answer',
      unprobed: 'unprobed'
    },
    probeTriggers: {
      periodic: 'periodic probe',
      immediate: 'immediate probe (create, edit or re-enable)',
      manual: 'manual probe',
      clock: 'clock: staleness window closed'
    },
    probeOutcomes: {
      ok: 'answers',
      unauthorized: 'secret refused',
      unreachable: 'unreachable',
      invalid: 'invalid answer'
    },
    targetKinds: {
      realm: 'Realm',
      localUser: 'Local user',
      localGroup: 'Local group',
      policy: 'Policy',
      roleMapping: 'Role mapping (historical)',
      rbacGrant: 'Role grant',
      rbacRole: 'Role',
      app: 'Application',
      computer: 'Computer',
      computerPairing: 'Computer pairing',
      enrollment: 'MFA enrollment',
      frontend: 'Service',
      biocard: 'BioCard',
      session: 'Session',
      eapCa: 'Imago CA',
      eapCert: 'EAP-TLS certificate',
      mfaPolicy: 'MFA policy',
      externalMfa: 'External MFA',
      legacyApp: 'Legacy app (Imago 4Legacy)'
    },
    eventReasons: {
      legacyCredentialRefused: {
        sourceNotEphemeral:
          'Ephemeral credential presented from an address that is no ephemeral consumer’s: refused without the realm chain (no lockout hit)',
        audienceMismatch: 'Credential minted for another legacy app (audience differs from the calling consumer)',
        expired: 'Ephemeral credential expired (past its TTL)',
        consumed: 'One-shot credential already spent',
        usernameMismatch: 'Bind as a username other than the one the credential was minted for',
        notEphemeralCredential:
          'Real password presented to an ephemeral consumer: never verified against the directory (only the ephemeral credential is admitted)',
        disabled: 'Legacy app disabled: every credential is refused'
      },
      legacyNginxFailed: {
        nginxTestFailed: '`nginx -t` refused the rendered site (stderr below)',
        nginxReloadFailed: '`nginx -s reload` failed (stderr below)',
        writeFailed: 'Writing the files into the nginx directory failed',
        renderFailed: 'A stored profile no longer renders (boot reconcile)'
      }
    },
    reasons: {
      badCredentials: 'Wrong credentials',
      rateLimited: 'Attempt ceiling exceeded (throttling)',
      unresolvedSubject: 'Subject unresolved: the username names no user in the realm',
      unknownSubject: 'Unknown subject',
      unknownRealm: 'Unknown realm',
      realmMissing: 'Realm missing: the channel requires an explicit realm',
      realmKindRefused: 'Realm kind not admitted on this channel',
      federatedRealm: 'Federated realm: Imago holds no password',
      mfaRequired: 'Second factor required',
      mfaRejected: 'Second factor refused',
      mfaObligedUnenrolled: 'MFA owed but an owed factor is not enrolled (channel with no enrollment gate)',
      noOwedFactorOnChannel: 'The factors held cannot travel on this channel while MFA is owed',
      externalEnrollmentRequired: 'External MFA owed but not enrolled on the bridge',
      externalUnavailable:
        'External MFA owed but not servable (bridge down, timeout, 5xx or resource disabled): closed refusal, no lockout hit',
      roundNotCarried:
        'Interactive round opened on a channel that cannot carry it: refused, nothing judged, no lockout hit',
      roundBudgetExhausted:
        'Round budget of this login exhausted (3 fresh factor rounds, 5 credential steps): refused, no lockout hit, the open round stays answerable',
      roundExpired:
        'Round closed unjudged: the bridge’s session expired or the wait round passed its deadline; no lockout hit',
      methodNotOffered: 'Picked method not among those offered',
      stepUpChallengeUnknownOrReplayed: 'Step-up FIDO2 challenge unknown or already spent',
      notEnrolled: 'No enrollment',
      stepUpRejected: 'Step-up refused',
      lockedOut: 'Account locked (lockout threshold reached)',
      disabled: 'User or realm disabled',
      revoked: 'Revoked',
      mustChangePassword: 'Mandatory password change pending',
      mustChangeForced: 'Password change forced by the administrator: sessions revoked',
      passwordReset: 'Password reset by the administrator: sessions revoked',
      passwordRecovered: 'Password recovered by the user: sessions revoked',
      localUserDisabled: 'Local user disabled: sessions revoked',
      localUserDeleted: 'Local user deleted: sessions revoked',
      adSsoRequired: 'Windows logon not configured on the realm (no AD domain)',
      vscNotProvisioned: 'Virtual Smart Card not provisioned',
      notInInventory: 'Card not in the BioCard inventory',
      assignedToOther: 'Card already assigned to another subject',
      pairingDenied: 'Pairing denied',
      noStableId: 'No stable identifier (UUID) for the subject',
      samlAcsFailed: 'SAML Response refused on the ACS',
      federationCallbackFailed: 'Federation callback failed',
      policyRefused: 'Refused by policy',
      directoryUnavailable: 'Directory unreachable',
      wrongCode: 'Unknown device code (never issued, or mistyped)',
      expired: 'Device code expired (300 s from issue)',
      alreadyUsed: 'Device code already spent (approved or burned)',
      throttled: 'Too many attempts on the device code (5 per minute per IP and per code)',
      subjectMismatch:
        'Login on the verification page as a subject other than the one the device asked for: code burned',
      denied: 'The subject denied on the verification page (“not me”)',
      deviceCodeDenied:
        'Device code denied or burned on the verification page, met on the device’s poll: uniform refusal, no lockout hit',
      deviceCodeUnavailable:
        'The broker could not serve the identity round (OIDC provider absent or failing): nothing judged, no lockout hit'
    }
  },
  apps: {
    list: {
      generated: 'generated by 4Legacy',
      generatedHelp:
        'Ephemeral consumer generated by the legacy app “{app}”: it lives and dies with it, never edited or deleted alone.',
      generatedActions: 'Managed from the legacy app that generated it (Imago 4Legacy)',
      title: 'Applications',
      subheader: 'The application catalog: every app integrates with one Trantor Imago service (channel).',
      create: 'New app',
      empty: 'No application registered.',
      loadError: 'Could not load the applications.',
      colName: 'Name',
      colKind: 'Kind',
      colFrontend: 'Frontend',
      colState: 'State',
      active: 'Active',
      inactive: 'Inactive',
      scimExport: 'SCIM export',
      edit: 'Edit',
      deleteTitle: 'Delete application',
      deleteHint:
        'You are about to delete the application “{name}”. Its credentials and its active OIDC sessions die with it.'
    },
    kinds: {
      oidcClient: 'OIDC client',
      radiusClient: 'RADIUS client',
      samlServiceProvider: 'SAML service provider',
      ldapConsumer: 'LDAP consumer'
    },
    dialog: {
      notes: 'Notes (optional)',
      notesHelp: 'Free-form operator notes: what the app is, who runs it.',
      enabled: 'Enabled',
      enabledHelp: 'A disabled app refuses every authentication flow; the catalog entry and its configuration survive.',
      ldapSourceKind: 'Source (optional)',
      ldapSourceIp: 'IP address',
      ldapSourceCidrLabel: 'Source range',
      ldapSourceIpLabel: 'Source address',
      ldapSourceHelp:
        'The address the consumer binds from on imago-ldapd, as the LDAP service sees it. Descriptive on a hand-written consumer; on an ephemeral one it is copied from the legacy app.',
      tabs: {
        general: 'General',
        protocol: 'Protocol',
        groups: 'Groups and claims'
      },
      nameRequired: 'The name is required.',
      redirectUrisRequired: 'At least one redirect URI is needed.',
      radiusSourceRequired: 'The NAS source (CIDR or IP address) is required.',
      radiusSecretRequired: 'The shared secret is required.',
      samlEntityIdRequired: 'The service provider’s Entity ID is required.',
      samlAcsUrlRequired: 'The ACS URL is required.',
      createTitle: 'New app',
      editTitle: 'Edit app',
      createError: 'Could not create the application.',
      updateError: 'Could not update the application.',
      kind: 'Kind',
      kindHelp: 'The kind determines the integrated frontend and is immutable after creation.',
      name: 'Name',
      namePlaceholder: 'customer-portal',
      nameHelp: 'Unique within the catalog.',
      created: {
        title: 'Application created',
        warning:
          'The client secret is shown only once and cannot be recovered: copy it now. If you lose it, delete and recreate the application.',
        clientId: 'Client ID',
        clientSecret: 'Client secret',
        close: 'Copied, close'
      },
      oidc: {
        clientId: 'Client ID',
        clientIdHelp: 'Minted at creation, stable for the whole life of the app (read-only).',
        redirectUris: 'Redirect URIs',
        redirectUrisHelp: 'One per line. https:// only (with the development allowance for http://localhost).',
        postLogoutRedirectUris: 'Post-logout redirect URIs (optional)',
        postLogoutRedirectUrisHelp: 'One per line, same rules as the redirect URIs.',
        grantTypes: 'Grant types',
        scopes: 'Scopes',
        scopesHelp: 'Enter to add a scope.'
      },
      radius: {
        source: 'Source',
        sourceIp: 'IP address',
        sourceCidrLabel: 'Source range',
        sourceIpLabel: 'Source address',
        sourceHelp: 'The NAS/concentrator is recognized by its source address.',
        sharedSecret: 'Shared secret',
        sharedSecretHelp: 'Written once and sealed in the keystore: it is never shown again.',
        newSharedSecret: 'New shared secret (leave blank to keep the current one)',
        vlanByGroup: 'Dynamic VLAN by group (optional)',
        vlanByGroupHelp: 'One rule per line: group => VLAN. The first rule whose group the user holds wins (RFC 3580).',
        vlanRules: 'Dynamic VLAN by subject (optional, evaluated first)',
        vlanRulesHelp:
          'One rule per line: user:<name>@<realm>, group:<group>@<realm> or @<realm> => VLAN. In order: the first match wins, then the by-group map.',
        vlanRulesInvalid:
          'Invalid line: {line} — expected <chip> => <vlan> with chip user:<name>@<realm>, group:<group>@<realm> or @<realm>'
      },
      saml: {
        note: 'Imago’s SAML IdP always signs the assertion and the Response; the released attributes are username, realm, displayName, email and the groups chosen below. Metadata and SSO URL are in the SAML frontend.',
        entityId: 'Entity ID',
        acsUrl: 'ACS URL',
        acsUrlHelp: 'Assertion Consumer Service URL (HTTP-POST binding).',
        nameIdFormat: 'NameID format',
        spCertificate: 'Service provider signing certificate (PEM, optional)',
        spCertificateHelp:
          'With a certificate, every AuthnRequest of the service provider must be signed (with this key); without one, requests travel unsigned and the registered ACS is the guarantee.',
        nameId: {
          unspecified: {
            label: 'unspecified — username@realm',
            help: 'The NameID is “username@realm”, stable and readable (default).'
          },
          emailAddress: {
            label: 'emailAddress — subject’s email',
            help: 'The NameID is the subject’s email: a user without an email is refused with a speaking error.'
          },
          persistent: {
            label: 'persistent — stable opaque identifier',
            help: 'An opaque identifier, stable per subject/SP pair and unaffected by renames.'
          },
          transient: {
            label: 'transient — per-session identifier',
            help: 'A different identifier on every assertion.'
          }
        }
      },
      ldap: {
        noteCreate:
          'Authenticates its users through the LDAP frontend (imago-ldapd), which delegates every verdict to the core. No secret is minted.',
        noteEdit: 'Authenticates through the LDAP frontend (imago-ldapd).',
        description: 'Description (optional)',
        descriptionEdit: 'Description',
        descriptionHelp: 'What this consumer is and how it binds.'
      },
      groups: {
        label: 'Groups to release',
        helpOidc: 'Groups released to the app (groups claim). The app must also request the “groups” scope.',
        helpOther: 'The user’s groups released to this app (only these, verbatim). Empty = no group.'
      }
    }
  },
  frontends: {
    list: {
      title: 'Services',
      subheader: 'The four protocol services of Trantor Imago: OIDC, LDAP, RADIUS and SAML.',
      loadError: 'Could not load the frontends.',
      colService: 'Service',
      colProtocol: 'Protocol',
      colState: 'State',
      colEnabled: 'Enabled',
      configure: 'Configure',
      enableTitle: 'Enable the service',
      disableTitle: 'Disable the service',
      enableHint:
        'You are about to enable the service “{name}”. The change takes effect at the next restart of the daemon (or build of the OIDC provider).',
      disableHint:
        'You are about to disable the service “{name}”. The change takes effect at the next restart of the daemon (or build of the OIDC provider).',
      enable: 'Enable',
      disable: 'Disable'
    },
    status: {
      up: 'Active',
      unknown: 'Unknown',
      notBuilt: 'Not built',
      down: 'Down'
    },
    dialog: {
      saveError: 'Could not save the service configuration.',
      tabs: {
        general: 'General',
        advanced: 'Advanced'
      },
      effect: {
        oidc: 'Changes take effect at the next rebuild of the OIDC provider in httpd.',
        ldap: 'Changes take effect at the next restart of the LDAP daemon (imago-ldapd).',
        radius: 'Changes take effect at the next restart of the RADIUS daemon (imago-radiusd).',
        saml: 'The switch takes effect immediately: with the service off, every SAML request receives a 404 and the metadata is not published.'
      },
      notBuilt:
        'Not available yet (phase 2): the SAML service is not part of this build, so it exposes no editable settings.',
      state: 'State:',
      build: 'Build:',
      buildPresent: 'present',
      buildMissing: 'not built',
      enabled: 'Enabled on this channel',
      issuer: 'Issuer',
      issuerHelp: 'Public origin of the provider (deploy-level, read-only).',
      entityId: 'IdP Entity ID',
      entityIdHelp: 'Derived from the public origin (deploy-level, read-only).',
      ssoUrl: 'SSO URL',
      ssoUrlHelp: 'HTTP-Redirect and HTTP-POST bindings on the same URL (read-only).',
      metadataUrl: 'Metadata URL',
      metadataUrlHelp: 'To import into the service provider: it carries the IdP’s signing certificate (read-only).',
      listenerAddress: 'Listening address',
      listenerAddressHelp: 'The daemon’s socket (deploy-level, from the environment — read-only).',
      servedRealms: 'Served realms',
      servedRealmsHelp:
        'None = every realm. A sign-in on a realm not served is refused like a non-existent realm (the audit tells them apart).',
      defaultRealm: 'Default realm of the service',
      defaultRealmHelp: 'The realm of a BARE username on this service; empty = the portal’s rule (local).',
      defaultRealmPortal: '— portal rule —',
      totpMode: 'TOTP mode in the combined credential',
      totpModeHelp:
        'suffix = the last 6 digits (as before), prefix = the first 6, none = the password is all password (whoever is required to use TOTP cannot sign in from here).',
      accessTokenTtl: 'Access token TTL (seconds)',
      idTokenTtl: 'ID token TTL (seconds)',
      authCodeTtl: 'Authorization code TTL (seconds)',
      ttlRangeToken: 'Between 60 and 86400.',
      ttlRangeCode: 'Between 30 and 3600.',
      scopes: 'Published scopes',
      scopesHelp: 'Enter to add a scope.',
      dedupTtl: 'Dedup window (seconds)',
      dedupTtlHelp: 'UDP anti-retransmission window: between 1 and 3600.',
      papOnly: 'PAP only:',
      papOnlyValue: 'active (const)'
    }
  },
  eap: {
    list: {
      title: 'Imago CA — EAP-TLS certificates',
      loadError: 'Could not load the EAP certificates.',
      caCert: 'CA certificate',
      crl: 'CRL',
      initCa: 'Initialize CA',
      issue: 'Issue certificate',
      colSerial: 'Serial',
      colSubject: 'Subject (CN)',
      colSan: 'SAN',
      colExpiry: 'Expiry',
      colState: 'State',
      revoked: 'revoked',
      valid: 'valid',
      revoke: 'Revoke',
      empty: 'No certificate issued.'
    },
    viewer: {
      caCertTitle: 'Imago CA certificate (trust anchor)',
      caTitle: 'Imago CA',
      crlTitle: 'CRL (revocation)',
      crlShortTitle: 'CRL',
      notInitialized: 'The CA is not initialized yet.'
    },
    init: {
      title: 'Initialize the Imago CA',
      hint: 'Generates the CA key (sealed in the keystore) and the root certificate. Idempotent.',
      confirm: 'Initialize'
    },
    issue: {
      title: 'Issue an EAP-TLS client certificate',
      commonName: 'Common Name',
      sanUpn: 'SAN UPN (user, e.g. user@domain)',
      sanDns: 'SAN DNS (machine)',
      days: 'Validity (days, default 365)',
      note: 'At least one SAN (UPN or DNS) is required. The private key is shown only once.',
      continue: 'Continue (sudo)',
      confirmTitle: 'Confirm issuance',
      confirmHint: 'Issue a certificate for {name}.',
      confirm: 'Issue'
    },
    issued: {
      title: 'Certificate issued — serial {serial}',
      warning: 'Save the private key NOW: it will not be shown again.',
      certPem: 'Certificate (PEM)',
      keyPem: 'Private key (PEM)'
    },
    revoke: {
      title: 'Revoke the certificate {serial}…',
      hint: 'The certificate will be refused by EAP authentication and will end up in the CRL.',
      confirm: 'Revoke'
    }
  },
  computers: {
    ipWhitelistPlaceholder: '10.0.0.5 or 192.168.0.0/24',
    list: {
      title: 'Computers',
      subheader:
        'The computers registered with Trantor Imago and their enablements (PBA, Credential Provider). The activation is completed by the PBA Manager or the CP Manager on the computer with the one-time token; here you register, enable and revoke them.',
      pairings: 'Login pairings',
      new: 'New computer',
      loadError: 'Could not load the computers.',
      empty: 'No computers registered.',
      name: 'Name',
      enablements: 'Enablements',
      directoryDn: 'Directory DN',
      notes: 'Notes',
      enablementRevoked: 'Revoked',
      enablementHint: 'Click: reissue token · X: revoke',
      edit: 'Edit',
      enable: 'Enable PBA / Credential Provider',
      status: {
        pending: 'pending activation',
        active: 'active',
        revoked: 'revoked'
      }
    },
    revokeDialog: {
      title: 'Revoke enablement',
      hint: 'You are about to revoke the “{kind}” enablement of “{name}”. PBA: refused at pre-boot. CP: the permanent token is invalidated.',
      confirm: 'Revoke'
    },
    deleteDialog: {
      title: 'Delete computer',
      hint: 'You are about to delete “{name}” and all its enablements. The agents’ permanent tokens stop being served.'
    },
    newDialog: {
      title: 'New computer',
      nameRequired: 'The name is required.',
      createError: 'Could not create the computer.',
      name: 'Name',
      namePlaceholder: 'ws-frontdesk-01',
      nameHelp: 'Immutable after creation.',
      directoryDn: 'Directory DN (optional)',
      directoryDnHelp: 'To resolve the computer’s groups over LDAP.',
      realm: 'Groups realm (optional)',
      realmHelp: 'The realm whose service bind resolves the computer’s groups over LDAP. Empty = the user’s.',
      realmUser: 'User’s realm',
      ipWhitelist: 'Allowed addresses (IP/CIDR, optional)',
      ipWhitelistHelp: 'Single IPs or CIDR ranges. Constrains the agent (PBA/CP) calls to these origins.',
      notes: 'Notes (optional)'
    },
    editDialog: {
      title: 'Edit computer',
      name: 'Name',
      nameNote: 'Immutable: it is stamped into every audit line.',
      realm: 'Groups realm',
      realmHelp:
        'The realm whose service bind resolves the computer’s groups over LDAP. “— none —” = the realm of the authenticating user.',
      realmNone: '— none —',
      realmLoadError: 'Could not load the realms: the choice stays as it is.',
      realmUnknown: 'The selected realm no longer exists: reload the page and try again.',
      directoryDn: 'Directory DN',
      directoryDnHelp: 'The DN of the computer object in the directory, to resolve its groups over LDAP. Empty = none.',
      ipWhitelist: 'Allowed addresses (IP/CIDR)',
      ipWhitelistHelp:
        'Single IPs or CIDR ranges. Constrains the agent (PBA/CP) calls to these origins. Empty = no constraint.',
      notes: 'Notes',
      notesHelp: 'Free-form operator notes.',
      sudoNote: 'Editing “{name}” requires the password confirmation.',
      updateError: 'Could not edit the computer.'
    },
    enableDialog: {
      titleEnable: 'Enable — “{name}”',
      titleReissue: 'Reissue token — “{name}”',
      enable: 'Enable',
      reissue: 'Reissue',
      kind: 'Enablement',
      enableNote: 'A one-time token (valid for 15 minutes) is generated, to be entered into the agent.',
      reissueNote: 'The old token is invalidated and a new one is generated.',
      actionError: 'Could not complete the operation.',
      tokenTitle: 'One-time token — {kind}',
      tokenWarning:
        'Copy this token now: it is shown only once. It goes into the agent (PBA ISO / Credential Provider) of “{name}”, which trades it for a permanent token. Imago keeps only its hash.',
      done: 'Done'
    },
    pairingsDialog: {
      title: 'PBA login pairings',
      subtitle:
        'Who may log in on which computers. One side is a computer or a group of computers, the other a user or a group of users. Groups always come from the directory.',
      loadError: 'Could not load the pairings.',
      createError: 'Could not create the pairing.',
      chooseComputer: 'Choose the computer.',
      computerGroupRequired: 'The DN of the computer group is required.',
      userRequired: 'The user (username@realm) is required.',
      userGroupRequired: 'The DN of the user group is required.',
      add: 'Add',
      computer: 'Computer',
      subject: 'Subject',
      empty: 'No pairings.',
      addSection: 'Add a pairing',
      computerSide: 'Computer side',
      kindComputer: 'computer',
      kindGroup: 'group',
      kindUser: 'user',
      noComputers: 'No computers registered',
      computerGroupDn: 'Computer group DN',
      subjectSide: 'Subject side',
      userGroupDn: 'User group DN',
      deleteTitle: 'Delete pairing',
      deleteHint: 'You are about to delete the pairing “{computer} → {subject}”.'
    }
  },
  biocards: {
    status: {
      issued: 'In inventory',
      assigned: 'Assigned',
      revoked: 'Revoked'
    },
    list: {
      title: 'BioCard',
      subheader: 'The inventory of the corporate Imago BioCards: a FIDO2 card is listed here before it may enroll.',
      new: 'New card',
      loadError: 'Could not load the cards.',
      status: 'Status',
      model: 'Model',
      empty: 'No cards in the inventory.',
      serial: 'Serial number',
      assignedTo: 'Assigned to',
      notes: 'Notes',
      revoke: 'Revoke',
      edit: 'Edit'
    },
    revokeDialog: {
      title: 'Revoke card',
      hint: 'You are about to revoke the card “{serial}”. A revoked card is refused at enrollment.',
      confirm: 'Revoke'
    },
    deleteDialog: {
      title: 'Delete card',
      hint: 'You are about to delete the card “{serial}” from the inventory. A credential already enrolled keeps working, but the card can no longer be paired with a new enrollment.'
    },
    newDialog: {
      title: 'New card',
      serialRequired: 'The serial number is required.',
      createError: 'Could not create the card.',
      serial: 'Serial number',
      serialHelp: 'The card’s unique physical key. Immutable after creation.',
      aaguid: 'AAGUID (optional)',
      aaguidHelp: 'The GUID of the authenticator model. Immutable after creation.',
      model: 'Model (optional)',
      notes: 'Notes (optional)'
    },
    editDialog: {
      title: 'Edit card',
      updateError: 'Could not update the card.',
      serial: 'Serial number',
      serialHelp: 'The card’s physical identity (read-only).',
      aaguid: 'AAGUID',
      aaguidHelp: 'Authenticator model (read-only).',
      status: 'Status',
      model: 'Model',
      notes: 'Notes'
    }
  },
  rbacGrants: {
    subheader:
      'Who administers Trantor Imago: each grant grants a role to the members of a group of a realm, or to a single user of the realm, on a target (the whole deployment or one realm), with or without the power to delegate.',
    help: 'A grant is immutable: to correct it, delete and recreate it. Roles apply at the next login.',
    helpDetail:
      'A grant on one realm holds only on that realm’s operations (local users, identity space, enumeration); “can delegate” lets the holder create grants within its own and revoke those it created. Open sessions keep the roles resolved at authentication.',
    tabMappings: 'Grants',
    tabRoles: 'Roles',
    new: 'New grant',
    colTarget: 'Target',
    colDelegate: 'Delegation',
    colCreatedBy: 'Created by',
    targetAll: 'all',
    targetRealm: 'realm {realm}',
    delegable: 'can delegate',
    filterRealm: 'Realm',
    filterGroup: 'Realm group',
    filterGroupPlaceholder: 'Search in the group name…',
    filterUser: 'User',
    filterUserPlaceholder: 'Search in the username…',
    filterRole: 'Role',
    colRealm: 'Realm',
    colSubject: 'Subject',
    colRole: 'Role',
    colCreatedAt: 'Created at',
    subjectGroup: 'group',
    subjectUser: 'user',
    empty:
      'No grants: today only the local break-glass admin administers. Create a grant to delegate a role to a group of a realm or to a single user.',
    emptyFiltered: 'No grant for the selected filters.',
    loadError: 'Could not load the grants.',
    delete: 'Delete',
    deleteTitle: 'Delete grant',
    deleteHint:
      'You are about to revoke the role “{role}” from the members of “{group}” in realm “{realm}”. Sessions already open keep the role until their next login.',
    deleteHintUser:
      'You are about to revoke the role “{role}” from the user “{user}” in realm “{realm}”. Sessions already open keep the role until their next login.',
    roles: {
      admin: 'Everything: every read and every write.',
      helpdesk:
        'Resets MFA factors; reads realms, local users and groups, applications, policy and MFA policies, grants, enumeration and census, FIDO metadata, audit. No configuration writes.',
      auditor: 'Every read, audit register included. No writes.'
    },
    dialog: {
      title: 'New grant',
      target: 'Target',
      targetAll: 'The whole deployment',
      targetRealm: 'One realm',
      targetRealmPick: 'Target realm',
      targetRealmHelp: 'The role holds only on the operations of this realm (the realm itself, its local users, its identity space, its enumeration).',
      canDelegate: 'Can delegate',
      canDelegateHelp: 'The holder may create grants within this one (same role or a role with fewer permissions, same or narrower realm) and revoke the ones it created.',
      realm: 'Realm',
      realmHelp: 'The realm the group or the user belongs to.',
      realmEmpty: 'No realm available: create one first.',
      subjectKind: 'Grant to',
      subjectGroup: 'Group',
      subjectUser: 'User',
      group: 'Realm group',
      groupHelp:
        'The group name or DN as the directory reports it (memberOf/inChain); on a native realm, the name of an existing local group. Case-insensitive match.',
      user: 'Username',
      userHelp:
        'The login name of one single user, as the directory reports it (the same name the per-user MFA obligation takes). Case-insensitive match.',
      role: 'Role',
      roleEmpty: 'No role available.',
      roleSystem: 'system',
      roleCustom: 'custom role',
      realmRequired: 'Pick the realm.',
      groupRequired: 'The realm group is required.',
      userRequired: 'The username is required.',
      roleRequired: 'Pick the role.',
      targetRealmRequired: 'Pick the target realm.',
      sudoNote: 'Creating a grant requires the password confirmation.',
      conflict: 'An identical grant already exists (same realm, same group, same role and same target).',
      conflictUser:
        'An identical grant already exists (same realm, same user — case-insensitively — same role and same target).',
      danglingRealm: 'The selected realm no longer exists: reload the page and try again.',
      danglingRole: 'The selected role no longer exists: reload the page and try again.',
      nativeNoGroups: 'Native realm: the group must exist in System › Local groups.',
      nativeGroupRefused: 'No local group with this name in the realm: create it first in System › Local groups.',
      createError: 'Could not create the grant.'
    }
  },
  rbacRoles: {
    title: 'Roles',
    subheader:
      'The roles a grant may confer: the three system roles (fixed in the code) and the custom ones, composed from the permission catalog.',
    help: 'A role is a set of permissions: system roles are immutable, a custom role holds the permissions you choose.',
    helpDetail:
      'The “imago-” prefix is reserved and the name cannot change. Sessions carrying a custom role feel an edit at their next request (this node at once, the others within 30 s); a deleted role grants nothing anymore.',
    new: 'New role',
    colName: 'Name',
    colKind: 'Kind',
    colDescription: 'Description',
    colPermissions: 'Permissions',
    kindSystem: 'system',
    kindCustom: 'custom',
    noPermissions: 'no permission',
    morePermissions: '+{count}',
    empty: 'No roles.',
    loadError: 'Could not load the roles.',
    catalogError: 'Could not load the permission catalog: creating and editing roles is suspended.',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete role',
    deleteHint:
      'You are about to delete the role “{role}”. Sessions carrying it will obtain nothing through it anymore. If a grant still names it, the deletion is refused.',
    deleteGranted: 'The role “{role}” has {count} grant(s): revoke the grants that name it first.',
    systemLocked: 'System roles are fixed in the code: they cannot be edited or deleted.',
    dialog: {
      createTitle: 'New role',
      editTitle: 'Edit role',
      name: 'Name',
      nameHelp:
        'Lowercase letters, digits and “-”, 1–64 characters; immutable after creation. The “imago-” prefix is reserved for the system roles.',
      nameInvalid:
        'Invalid name: lowercase letters, digits and “-”, 1–64 characters, starting and ending with a letter or a digit.',
      nameReserved: 'The “imago-” prefix is reserved for the system roles.',
      nameRequired: 'The name is required.',
      nameImmutable: 'The name cannot change: it is stamped in every grant, session and audit line.',
      description: 'Description',
      permissions: 'Permissions',
      permissionsHelp:
        'The catalog is the served contract’s own, grouped by resource: each permission lists (on hover) the operations it unlocks. A role with no permission is legitimate: it grants nothing.',
      selectedCount: '{count} selected',
      sudoNote: 'Creating or editing a role requires the password confirmation.',
      conflict: 'A role with this name already exists.',
      createError: 'Could not create the role.',
      updateError: 'Could not update the role.'
    }
  },
  localGroups: {
    subheader:
      'The groups of the native realm “{realm}”: named sets of its local users. A member carries the group’s name in its identity: role grants, MFA policies, VLAN rules and app allowlists recognise it like an AD group.',
    help: 'The group name is immutable and matched case-insensitively; groups are flat (a member is a user). A membership change applies from the member’s next login. Roles per group are granted in',
    helpGrantsLink: 'Roles & grants',
    new: 'New group',
    usersLink: 'Users of the realm',
    realmSelectorHelp: 'Groups are per realm: the same name in two native realms is two different groups.',
    realmLookupDenied:
      'Your session can read the local groups but not the realms (permission realm.read): without the list of native realms the page cannot tell which realm to show. Ask for the realm.read permission.',
    filterName: 'Group name',
    filterNamePlaceholder: 'Search in the name…',
    colName: 'Name',
    colDescription: 'Description',
    colMembers: 'Members',
    noMembers: 'no members',
    empty: 'No local groups: create one.',
    emptyFiltered: 'No group matches the filter.',
    loadError: 'Could not load the local groups.',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete local group',
    deleteHint:
      'You are about to delete “{group}”. Its memberships are removed; the role grants, MFA policies and rules that name the group stay inert and must be revoked by hand. Members see the change from their next login.',
    dialog: {
      title: 'New group in “{realm}”',
      name: 'Group name',
      nameHelp:
        'Letters, digits, “.”, “_”, “-”, starting alphanumeric, no whitespace. Immutable after creation; matched case-insensitively.',
      nameInvalid: 'Invalid name: letters, digits, “.”, “_”, “-”, starting alphanumeric, 1–255 characters, no whitespace.',
      nameRequired: 'The group name is required.',
      nameInvalidBrief: 'Invalid group name.',
      description: 'Description',
      descriptionHelp: 'Free text for the administrator; never matched against anything.',
      members: 'Members',
      membersHelp: 'The users of the realm that belong to the group: the full list, saving replaces it.',
      membersTruncated: 'The realm has more users than the picker lists: add the others from the CLI.',
      membersLoadError: 'Could not load the users of the realm (the localUser.read permission is needed).',
      memberUnknown: 'A member is not a user of the realm: {detail}',
      conflictName: 'The group “{group}” already exists in realm “{realm}” (the match is case-insensitive): choose another name.',
      createError: 'Could not create the group.'
    },
    editDialog: {
      title: 'Edit local group',
      nameHelp: 'The name is immutable: role grants, MFA policies, VLAN rules and app allowlists cite it.',
      updateError: 'Could not update the group.'
    }
  },
  localUsers: {
    subheader:
      'The users of the native realm “{realm}”: the accounts Imago owns itself, with an argon2id password. Otherwise no different from a user of an external realm: same login flows, MFA obligation, policy and audit.',
    help: 'The username is immutable and matched case-insensitively. In the “local” realm, “admin” is the break-glass: never deleted, disabled or renamed. Roles are granted per user or per local group in',
    helpRolesLink: 'Roles & grants',
    helpGroupsLink: 'Local groups',
    new: 'New user',
    filterUsername: 'Username',
    filterUsernamePlaceholder: 'Search in the username…',
    filterEnabled: 'State',
    enabledOnly: 'Enabled only',
    disabledOnly: 'Disabled only',
    filterDisplayName: 'Display name',
    filterDisplayNamePlaceholder: 'Search in the display name…',
    filterMail: 'E-mail',
    filterMailPlaceholder: 'Search in the e-mail…',
    filterProtected: 'Break-glass',
    protectedOnly: 'The break-glass only',
    unprotectedOnly: 'Without the break-glass',
    filterMustChange: 'Password',
    mustChangeOnly: 'Change at login only',
    mustChangeOffOnly: 'Final only',
    colUsername: 'Username',
    colDisplayName: 'Display name',
    colMail: 'E-mail',
    colEnabled: 'State',
    colProtected: 'Break-glass',
    colMustChange: 'Password',
    colCreatedAt: 'Created at',
    enabled: 'Enabled',
    disabled: 'Disabled',
    breakGlass: 'break-glass',
    mustChange: 'change at login',
    empty: 'No user in this realm: create the first one.',
    emptyFiltered: 'No user for the selected filters.',
    loadError: 'Could not load the local users.',
    realmMissing: 'No native realm found: the “local” realm is built in and should always exist.',
    realmSelector: 'Native realm',
    realmSelectorHelp: 'Users are per realm: the same username in two native realms is two different users.',
    realmDefault: 'default',
    realmDisabled: 'disabled',
    realmUnknown: 'The realm named in the link is not a native realm: showing “{realm}”.',
    realmLookupDenied:
      'Your session may read the local users but not the realms (permission realm.read): without the list of the native realms the page cannot tell which realm to show. Ask for the realm.read permission.',
    edit: 'Edit',
    reset: 'Reset password',
    delete: 'Delete',
    deleteProtected: 'The break-glass cannot be deleted: it is the last way in.',
    resetProtected: 'The break-glass password is reset only from the host, with imago-httpd-admin init-admin --reset.',
    deleteTitle: 'Delete local user',
    deleteHint:
      'You are about to delete “{user}”. Its MFA factors (TOTP and FIDO2 credentials) are deleted, its BioCards become available again. Role grants and pairings that name the login stay and must be revoked by hand. Its open sessions are revoked at once.',
    dialog: {
      title: 'New user in “{realm}”',
      username: 'Username',
      usernameHelp:
        'Letters, digits, “.”, “_”, “-”, starting alphanumeric, no whitespace. Immutable after creation; case-insensitive match.',
      usernameInvalid:
        'Invalid username: letters, digits, “.”, “_”, “-”, starting alphanumeric, 1–255 characters, no whitespace.',
      usernameRequired: 'The username is required.',
      usernameInvalidBrief: 'Invalid username.',
      password: 'Password',
      passwordConfirm: 'Confirm password',
      passwordMin: 'At least {min} characters (policy).',
      passwordMismatch: 'The passwords do not match.',
      passwordTooShort: 'The password is shorter than the policy minimum: {min} characters.',
      passwordRequired: 'The password is required.',
      displayName: 'Display name',
      displayNameHelp: 'What the user is greeted with; empty = the username.',
      mail: 'E-mail',
      mailHelp: 'For the recovery codes and the notifications; empty = no contact (recovery unavailable).',
      mustChange: 'Must change the password at first login',
      mustChangeOnHelp:
        'Bootstrap credential: the user replaces it at first login; until then RADIUS and LDAP refuse the login.',
      mustChangeOffHelp: 'The password is final: no change forced at first login.',
      enabled: 'Enabled',
      conflictUsername:
        'The username “{user}” is already taken in realm “{realm}” (the match is case-insensitive): choose another one.',
      createError: 'Could not create the user.'
    },
    editDialog: {
      title: 'Edit local user',
      usernameHelp:
        'The username is immutable: it is stamped in every session, role grant, MFA obligation and audit line.',
      enabledLocked: 'The break-glass cannot be disabled: it is the last way in.',
      protectedConflict: 'The break-glass cannot be disabled: it is the last way in.',
      disableRevokes:
        'Disabling the user revokes their open sessions at once (and the sudo elevations with them): every further call answers 401.',
      mustChangeRevokes:
        'Forcing the password change revokes the user’s open sessions at once: the obligation gates the next login.',
      updateError: 'Could not update the user.'
    },
    resetDialog: {
      title: 'Reset password',
      note: 'You are setting a temporary password for “{user}”: they will have to change it at the next login. Their open sessions are revoked at once.',
      password: 'New password',
      confirm: 'Reset',
      resetError: 'Could not reset the password.'
    }
  },
  realms: {
    manageUsers: 'Manage users',
    manageGroups: 'Manage groups',
    enumeration: 'Realm enumeration and census',
    edit: 'Edit',
    delete: 'Delete',
    deleteBuiltIn: 'The built-in “local” realm cannot be deleted: it is the default realm and holds the break-glass.',
    list: {
      subheader:
        'The authentication realms of Trantor Imago: native realms (users managed by Imago), LDAP/AD directories, federated OIDC and SAML IdPs and RADIUS servers.',
      new: 'New realm',
      empty: 'No realm configured.',
      loadError: 'Could not load the realms.',
      colName: 'Name',
      colBackend: 'Backend',
      colEndpoint: 'Endpoint',
      colBaseDn: 'Base DN',
      colStatus: 'Status',
      colDefault: 'Default',
      colActions: 'Actions',
      active: 'Enabled',
      disabled: 'Disabled',
      default: 'Default',
      probeBuiltIn: 'The “local” realm has no backend to test',
      probeByKind: {
        ldap: 'Test the LDAP directory: URLs, encryption, connection, service bind, user filter',
        oidc: 'Test the OIDC discovery of the issuer',
        saml: 'Check the SAML IdP material: SSO URL and signing certificate',
        radius: 'Test the RADIUS server: Status-Server or a synthetic Access-Request with a signed reply',
        local: 'Check the state of the native realm (no backend to reach)'
      }
    },
    kinds: {
      ldap: 'LDAP',
      oidc: 'OIDC',
      saml: 'SAML',
      local: 'Native',
      radius: 'RADIUS'
    },
    newDialog: {
      title: 'New realm',
      kindLocal: 'Native (Imago’s users)',
      kindLdap: 'LDAP / Active Directory',
      kindOidc: 'OIDC (federated IdP)',
      kindSaml: 'SAML (federated IdP)',
      kindRadius: 'RADIUS (external server)',
      localInfo:
        'A native realm relies on no directory: its users belong to Imago (argon2id passwords) and are managed in System › Local users. No Windows logon through the Credential Provider; the “local” realm stays the default.',
      sudoNote: 'Creating a realm requires the password confirmation.',
      createError: 'Could not create the realm.'
    },
    deleteDialog: {
      title: 'Delete realm',
      hintExternal:
        'You are about to delete the realm “{realm}”. Its role grants are removed; its MFA enrollments become orphaned, not deleted.',
      hintNative:
        'You are about to delete the native realm “{realm}”. It is deleted only when it holds no users (delete them first, one by one); its role grants are removed, pairings that name a login stay.',
      hasUsers: 'The realm “{realm}” still holds {count} users: delete them first, then retry.'
    },
    editDialog: {
      title: 'Edit native realm',
      name: 'Name',
      localNote: 'The “local” realm is built in: its name, backend, status and default cannot be changed.',
      nativeNote: 'Native realm: its name and backend cannot be changed; its status can.',
      enabled: 'Enabled',
      enabledHelp: 'A disabled realm refuses every login of its users, on every channel.',
      notes: 'Notes',
      notesHelp: 'Free-form notes about this realm.',
      sudoNote: 'The edit requires the password confirmation and is audited under your name.',
      updateError: 'Could not update the realm.'
    },
    editExternalDialog: {
      title: 'Edit realm',
      nameNote: 'The name and the backend cannot be changed; everything else can. Only the changed fields are sent.',
      notExternal: 'This realm is native: edit it from the native realm dialog.',
      loadError: 'Could not load the realm.',
      updateError: 'Could not update the realm.'
    },
    probeDialog: {
      title: 'Test realm: {realm}',
      hints: {
        ldap: 'A configuration test of the directory, never an authentication: URL shape, encryption, connection, service bind and user filter.',
        oidc: 'Fetches the issuer’s discovery document (/.well-known/openid-configuration) and checks that it declares the configured issuer.',
        saml: 'Checks the IdP material with no network: the SSO URL must be https:// and the signing certificate must parse and be within its validity.',
        radius:
          'Status-Server (RFC 5997) against the servers in order, then a synthetic Access-Request that must come back as a SIGNED Access-Reject: reachability and shared secret proven together. No answer = server unreachable OR wrong secret (a server holding another secret discards the request in silence), never “timeout”.',
        local: 'A native realm has no backend: the only check is its state (enabled).'
      },
      username: 'Test user (optional)',
      usernameHelp: 'Exercises the user filter; without it only the shape is checked.',
      outcome: 'Outcome:',
      reachable: 'Reachable',
      unreachable: 'Unreachable',
      coherent: 'Material coherent',
      incoherent: 'Material incomplete',
      nativeNothingToProbe: 'No backend to test',
      run: 'Test',
      error: 'Could not run the connection test.',
      checks: {
        urls: 'Configured URLs',
        encryption: 'Transport encryption',
        enabled: 'Realm enabled',
        connect: 'TCP/TLS connection',
        bind: 'Service bind',
        userFilter: 'User filter',
        servers: 'Configured RADIUS servers',
        statusServer: 'Status-Server (RFC 5997)',
        accessRequest: 'Synthetic Access-Request (signed Access-Reject)',
        messageAuthenticator: 'Message-Authenticator on the server’s replies',
        discovery: 'OIDC discovery',
        idpSsoUrl: 'IdP SSO URL',
        idpCertificate: 'IdP signing certificate'
      }
    },
    form: {
      tabs: {
        general: 'General',
        connection: 'Connection',
        access: 'MFA and Windows'
      },
      name: 'Name',
      namePlaceholder: 'corp',
      nameHelp: 'Lowercase, DNS-style (corp, example.com), immutable after creation.',
      nameShape: 'Lowercase letters, digits, hyphens and dots only, DNS-style (e.g. corp, example.com).',
      nameRequired: 'The name is required.',
      kind: 'Backend',
      kindHelp: 'Immutable after creation.',
      advanced: 'Advanced',
      tlsSection: 'TLS',
      attributesSection: 'Directory attributes',
      limitsSection: 'Limits',
      urls: 'Servers (URL)',
      urlsHelp: 'One per line (ldaps://host:636 or ldap://host), tried in failover order.',
      baseDn: 'Base DN',
      baseDnHelp: 'The subtree the user search starts from.',
      bindDn: 'Bind DN',
      bindDnHelp: 'Service account that runs the user search. Empty = anonymous search bind (refused by AD).',
      bindPassword: 'Bind password',
      bindPasswordHelp: 'Encrypted in the keystore, never shown.',
      bindPasswordKeepHelp: 'Empty = unchanged; type it to rotate it. It is never shown.',
      clearBindPassword: 'Remove the bind password',
      clearBindPasswordHelp: 'Deletes the secret from the keystore: the search falls back to an anonymous bind.',
      userFilter: 'User filter',
      userFilterHelp:
        '%u is replaced by the local part of the login (RFC 4515-escaped); it must select exactly one user. Non-AD directories: (&(objectClass=inetOrgPerson)(uid=%u)).',
      membershipMode: 'Group resolution',
      membershipInChain: 'inChain (nested groups, AD)',
      membershipMemberOf: 'memberOf (direct membership)',
      startTls: 'StartTLS',
      startTlsHelp:
        'Encrypts an ldap:// connection before any bind; ignored on ldaps://. Without ldaps:// or StartTLS the bind password travels in the clear.',
      verifyCertificate: 'Verify the certificate',
      verifyCertificateHelp: 'The directory’s certificate is verified in the TLS handshake.',
      verifyCertificateOffHelp: 'Off: an unverified TLS session authenticates nobody — the connection test says so.',
      caCertificate: 'CA (PEM)',
      caCertificateHelp: 'PEM CA bundle pinned for this directory. Empty = the system trust store.',
      uuidAttribute: 'UUID attribute',
      uuidAttributeHelp:
        'The user’s immutable UUID: objectGUID on AD, entryUUID on OpenLDAP. The key of the MFA enrollments and of every audit line.',
      displayNameAttribute: 'Display name attribute',
      displayNameAttributeHelp: 'Used to greet the user; falls back to the login name.',
      mailAttribute: 'Mail attribute',
      mailAttributeHelp: 'Where recovery codes and the enrollment/reset notifications are delivered.',
      phoneAttribute: 'Phone attribute',
      phoneAttributeHelp: 'Mobile number, kept for a future SMS recovery (not served yet).',
      memberOfAttribute: 'memberOf attribute',
      memberOfAttributeHelp: 'Read at login to photograph the groups when the resolution is memberOf.',
      directoryLockoutThreshold: 'Directory lockout threshold',
      directoryLockoutThresholdHelp:
        'The directory’s own account-lockout threshold. Imago’s threshold must stay strictly below it: Imago must trip first. Empty = not declared.',
      connectTimeoutSeconds: 'Connect timeout (s)',
      connectTimeoutSecondsHelp: 'Connect and request deadline per URL (1–60 s); on expiry the next URL is tried.',
      oidcInfo:
        'Federated login by redirect to the IdP: no password reaches Imago. The identity photograph is built from the id_token claims according to the mappings.',
      issuer: 'Issuer',
      issuerHelp: 'The OIDC discovery base (Imago reads /.well-known/openid-configuration).',
      clientId: 'Client ID',
      clientSecret: 'Client secret',
      clientSecretHelp: 'Absent = public client (PKCE only). Encrypted in the keystore, never shown.',
      clientSecretKeepHelp: 'Empty = unchanged; type it to rotate it. It is never shown.',
      clearClientSecret: 'Remove the client secret',
      clearClientSecretHelp: 'The client becomes public (PKCE only).',
      claimsSection: 'Scopes and claim mappings',
      scopes: 'Scopes',
      scopesHelp: 'The scopes requested at the IdP (openid is always included).',
      usernameClaim: 'Username claim',
      uuidClaim: 'UUID claim (stable)',
      uuidClaimHelp: 'Rename-proof claim mapped to the external uuid (default sub).',
      displayNameClaim: 'Display name claim',
      mailClaim: 'Mail claim',
      groupsClaim: 'Groups claim',
      groupsClaimHelp: 'An array of groups, used like an LDAP memberOf.',
      samlInfo:
        'Imago is the Service Provider: login by redirect to the IdP (AuthnRequest), the signed Response returns to Imago’s ACS and the identity photograph is built from the assertion’s attributes. The realm’s SP metadata is published by Imago.',
      idpEntityId: 'IdP entity ID',
      idpEntityIdHelp: 'The IdP’s SAML entityID.',
      idpSsoUrl: 'IdP SSO URL',
      idpSsoUrlHelp: 'The IdP’s SingleSignOnService (HTTP-Redirect binding).',
      idpCertificate: 'IdP signing certificate',
      idpCertificateHelp:
        'PEM or bare base64. Every assertion signature is verified against this certificate: the trust anchor of the whole flow.',
      samlMappingsSection: 'Attribute mappings',
      usernameAttribute: 'Username attribute',
      usernameAttributeHelp: 'Empty = the assertion’s NameID.',
      samlUuidAttribute: 'UUID attribute (stable)',
      samlUuidAttributeHelp: 'Empty = the NameID (rename-proof when the IdP issues a persistent NameID).',
      samlDisplayNameAttribute: 'Display name attribute',
      samlMailAttribute: 'Mail attribute',
      groupsAttribute: 'Groups attribute',
      groupsAttributeHelp: 'Multi-valued attribute carrying the groups, used like an LDAP memberOf.',
      urlsRequired: 'At least one directory URL is needed.',
      urlShape: 'Every URL must read ldaps://host[:port] or ldap://host[:port], with no path.',
      baseDnRequired: 'The base DN is required.',
      connectTimeoutRange: 'The connect timeout is an integer between 1 and 60 seconds.',
      lockoutThresholdShape: 'The directory lockout threshold is an integer ≥ 1, or empty.',
      issuerRequired: 'The issuer is required.',
      clientIdRequired: 'The client ID is required.',
      idpEntityIdRequired: 'The IdP entity ID is required.',
      idpSsoUrlRequired: 'The IdP SSO URL is required.',
      idpCertificateRequired: 'The IdP signing certificate is required: it is the trust anchor.',
      bindPasswordClearAndTyped:
        'You switched “Remove the bind password” on and also typed a new one: pick one of the two.',
      clientSecretClearAndTyped:
        'You switched “Remove the client secret” on and also typed a new one: pick one of the two.',
      radiusInfo:
        'Imago is the PAP client of an external RADIUS server: the password is verified by an Access-Request (RFC 2865, Message-Authenticator always). An Access-Challenge from the server is one more credential step (a PIN, a token), not a second factor: Imago’s factors follow AFTER the accept. The server has no directory: the identity is the username alone (a rename upstream is a new subject), groups come only from the accept, no enumeration; password changes belong to the RADIUS server.',
      radiusServers: 'RADIUS servers',
      radiusServersHelp:
        '1 to 4 servers as host[:port] (port 1812 when omitted), tried IN THE ORDER listed until one answers: ordered failover, never load balancing. The arrows change the order.',
      radiusServersEmpty: 'No server: add at least one.',
      radiusServer: 'Add server (host[:port])',
      radiusServerHelp: 'radius1.example.com, 10.0.0.5:1812 or [fd00::5]:1812 — Enter or “Add” appends it at the end.',
      radiusServerAdd: 'Add',
      radiusServerShape: 'Every server reads host[:port]: a DNS name, an IPv4 or a bracketed IPv6, an optional port.',
      radiusServersRequired: 'At least one RADIUS server is needed.',
      radiusServersMax: 'At most 4 RADIUS servers.',
      radiusSecret: 'Shared secret',
      radiusSecretHelp:
        'The secret shared with the servers (one for the whole list), at least 8 characters. Encrypted in the keystore, never shown.',
      radiusSecretKeepHelp:
        'Empty = unchanged; type it to rotate it (at least 8 characters). It is never shown and cannot be removed.',
      radiusSecretRequired: 'The shared secret is required.',
      radiusSecretShort:
        'The shared secret needs at least 8 characters: a shorter one makes the RFC 2865 obfuscation a toy.',
      radiusTimeoutMs: 'Timeout (ms)',
      radiusTimeoutMsHelp: 'The wait for one reply before a retransmission (or the next server): 500–10000 ms.',
      radiusTimeoutRange: 'The timeout is an integer between 500 and 10000 milliseconds.',
      radiusRetries: 'Retransmissions',
      radiusRetriesHelp:
        'Retransmissions of the same request per server after the first send (0–5), then the next server.',
      radiusRetriesRange: 'The retransmissions are an integer between 0 and 5.',
      radiusNasIdentifier: 'NAS-Identifier',
      radiusNasIdentifierHelp:
        'Sent on every request when non-empty (some servers key their policies on it). Empty = not sent.',
      radiusGroupsFrom: 'Groups from the accept',
      radiusGroupsFromHelp:
        'Where the subject’s groups are read on the Access-Accept; used like an LDAP memberOf (role grants, MFA policy scopes).',
      radiusGroupsNone: 'none (empty groups)',
      radiusGroupsClass: 'Class (every Class attribute, as text)',
      radiusGroupsFilterId: 'Filter-Id (every Filter-Id attribute)',
      radiusRequireMessageAuthenticator: 'Require a Message-Authenticator on replies',
      radiusRequireMessageAuthenticatorHelp:
        'A reply is believed only when it carries a valid Message-Authenticator (RFC 2869 §5.14) besides its Response Authenticator: the Blast-RADIUS posture (CVE-2024-3596). A server that does not sign its replies authenticates nobody, and the probe names the reason.',
      radiusRequireMessageAuthenticatorOff:
        'Off: you KNOWINGLY admit a RADIUS server that does not sign its replies (FreeRADIUS before 3.2.5, NPS without KB5040268): the Response Authenticator alone is forgeable (CVE-2024-3596). Keep that leg on a segregated segment or a RadSec tunnel, and turn the check back on once the server is upgraded.'
    },
    errors: {
      immutableField: 'The field “{field}” is immutable: it cannot be changed after creation.',
      notOnNative: 'Native realm: the fields {fields} do not apply (no directory, federation or AD SSO).',
      policyRefused: 'Refused by the lockout guard: {detail}',
      nameReserved: 'The name “local” is reserved for the built-in native realm, in any casing.',
      nameTaken: 'A realm with this name already exists.'
    },
    mfaExempt: {
      section: 'Imago’s MFA',
      label: 'Exempt from Imago’s MFA',
      help: 'The realm already does its own MFA: Imago asks its users no factor, neither local nor external; the MFA policies covering it stay inert.',
      hint: {
        oidc: 'Default on for an OIDC realm: the IdP is the gate. Switch it off if Imago must add its own second factor after the federated login (step-up).',
        saml: 'Default on for a SAML realm: the IdP is the gate. Switch it off if Imago must add its own second factor after the federated login (step-up).',
        ldap: 'Default off for an LDAP realm: the directory verifies the password alone. Switch it on if an MFA already fronts the directory.',
        radius:
          'Default off for a RADIUS realm: Imago adds its factors after the Access-Accept. Switch it on if the RADIUS server already does its own MFA (token challenge).'
      },
      chip: 'MFA exempt',
      chipTooltip: 'The realm does its own MFA: Imago asks its users no factor.'
    },
    adSso: {
      column: 'AD SSO',
      none: '—',
      byPassword: 'password',
      byCert: 'cert',
      section: 'AD SSO (Windows logon)',
      adDomain: 'AD domain',
      adDomainHelp:
        'Windows domain of the realm’s users (AD DNS domain / Kerberos realm, e.g. example.com). REQUIRED for any Windows logon (CP, SSO PBA→CP); empty = no Windows logon. It is the domain, not the realm’s name.',
      adDomainShape:
        'DNS domain or NetBIOS name: letters, digits and hyphens, labels joined by dots (e.g. example.com or ACME), no spaces.',
      certLogon: 'Certificate logon (VSC)',
      certLogonHelp:
        'Smartcard certificate on the TPM Virtual Smart Card, signed by the Imago CA (the DC must trust the Imago CA: a one-time manual setup). Off = password logon.',
      federatedHelp:
        'On a federated realm the Windows logon is by certificate only: with an AD domain the certificate logon is on.',
      certLogonNeedsDomain:
        'The certificate logon needs the AD domain: fill in “AD domain” or switch the certificate logon off.',
      federatedNeedsCertLogon:
        'A federated realm logs on to Windows only by certificate: switch the certificate logon on or clear the AD domain.',
      radiusHelp:
        'On a RADIUS realm the Windows logon is by certificate only: the password is the RADIUS server’s and never reaches LSA. With an AD domain the certificate logon is on; without one the Credential Provider refuses the realm and the PBA gate unlocks with no handoff.',
      radiusNeedsCertLogon:
        'A RADIUS realm logs on to Windows only by certificate (its password never reaches LSA): switch the certificate logon on or clear the AD domain.',
      sidNoteLdap:
        'Active Directory 2025+ in enforcement mode (KB5014754) requires the user’s SID in the certificate: Imago reads it from objectSid and embeds it in every certificate it signs.',
      sidNoteNonLdap:
        'To support Active Directory 2025+ in enforcement mode on a non-LDAP realm, the CA must read the SID from the realm’s LDAP enumeration source (ADR-13, not built yet): today a non-LDAP realm works only with a Samba DC or an Active Directory not in enforcement mode.'
    }
  },
  home: {
    welcome: 'Welcome, {name}',
    loadError: 'Could not reach the Imago backend. Try again later.',
    accountTitle: 'Your account',
    secondFactor: 'Second factor:',
    roles: 'Roles:',
    email: 'Email: {mail}',
    quickActionsTitle: 'Quick actions',
    manageProfile: 'Manage profile and MFA',
    manageProfileHelp: 'Set up your second factor (MFA), regenerate your recovery codes and change your password from your profile.'
  },
  node: {
    channel: {
      off: 'This node has no configuration channel (IMAGO_NODE_APPLY_MODE=off): the management network, the update mirror and 802.1X are configured only on the Imago Server appliance.',
      offDetail: 'The server says: {detail}',
      loadError: 'Could not read the node state.'
    },
    readOnly: 'Your session reads the node but cannot change it: the node.manage permission is needed.',
    problems: {
      validation: 'The node refused the form — {detail}',
      checkpointExpired: 'The confirmation came too late: {detail}',
      applyFailed: 'The node did not apply the change: {detail}'
    },
    network: {
      title: 'Management network',
      subheader:
        'The interface, addresses, DNS and static routes the node is reached through. Every apply lasts 90 seconds: without a confirmation the node reverts to its previous network by itself.',
      pending: 'Awaiting confirmation',
      notConfigured:
        'This node has no network written in its identity yet (it was installed before this channel): the first confirmed apply writes it. The form starts empty; the current state is below.',
      refusedTitle: 'The server refused the form',
      interface: 'Interface',
      interfaceHelp: 'The name as seen now (ens192) or the MAC address; several ports separated by commas form a bond.',
      vlan: 'VLAN',
      vlanHelp: 'Empty = untagged; otherwise an integer between {min} and {max}.',
      ip4Mode: 'IPv4',
      ip6Mode: 'IPv6',
      mode: { off: 'Not configured', dhcp: 'DHCP', auto: 'Automatic (SLAAC/DHCPv6)', static: 'Static' },
      address4: 'IPv4 address',
      address6: 'IPv6 address',
      prefix: 'Prefix',
      gw4: 'IPv4 gateway',
      gw6: 'IPv6 gateway',
      gwHelp: 'The default route lives here, never among the static routes.',
      dns: 'DNS servers',
      dnsHelp: 'Enter to add a server; an IPv4 entry needs IPv4 configured, an IPv6 entry needs IPv6.',
      routes: {
        title: 'Static routes',
        colDestination: 'Destination',
        colGateway: 'Gateway',
        colMetric: 'Metric',
        empty: 'No static routes.',
        add: 'Add route',
        hint: 'The destination in CIDR form (10.9.0.0/24, fd01::/64) and a gateway of the same family inside the interface’s network; an empty metric = the device’s (100 on ethernet). The default route is the gateway, not a route.',
        duplicate: 'The destination “{destination}” is already in the table.'
      },
      issues: {
        interfaceRequired: 'The interface is required.',
        vlanRange: 'The VLAN is an integer between 1 and 4094.',
        address4Required: 'The IPv4 address is required.',
        prefix4Range: 'The IPv4 prefix is an integer between 1 and 32.',
        address6Required: 'The IPv6 address is required.',
        prefix6Range: 'The IPv6 prefix is an integer between 1 and 128.',
        familyRequired: 'At least one family is needed: IPv4 or IPv6.',
        routeDestinationRequired: 'The destination is required.',
        routeGatewayRequired: 'The gateway is required.',
        routeMetricRange: 'The metric is an integer of 0 or more.'
      },
      apply: 'Apply',
      applyHelp:
        'Writes the profiles and opens a {seconds}-second checkpoint: the new network stays only if you confirm it.',
      applyDialog: {
        title: 'Apply the management network',
        hint: 'The node writes the profiles and keeps the previous network in a checkpoint for {seconds} seconds: unless you confirm before the deadline it reverts to it by itself. If the address changes, reopen the console at the new address and confirm from there (or with “imago confirm-network”).',
        confirm: 'Apply'
      },
      inFlight: {
        title: 'Network applied, awaiting confirmation',
        countdown: 'Without a confirmation the node reverts to its previous network in {seconds} s (at {deadline}).',
        help: 'If this page can no longer reach the server, the node reverts by itself: no wrong address locks you out. Confirm only if the console answers from the new network.',
        profiles: 'Profiles written: {profiles}',
        confirm: 'Confirm ({seconds} s)'
      },
      stillPending:
        'The deadline has passed but the node still reports a network awaiting confirmation: you may try to confirm, the node answers if the checkpoint is already gone.',
      confirmDialog: {
        title: 'Confirm the network',
        hint: 'The checkpoint is destroyed and the new network enters the node’s identity: from now on it is the one the node boots with.',
        confirm: 'Confirm'
      },
      confirmed: 'Network confirmed: it is now the one in the node’s identity.',
      reverted:
        'The node has reverted to its previous network: the confirmation did not arrive in time and the pending form was discarded. The form shows the confirmed network again.',
      live: {
        title: 'Current state',
        device: 'Device',
        addresses: 'Addresses',
        gateway4: 'IPv4 gateway',
        gateway6: 'IPv6 gateway',
        dns: 'DNS',
        routes: 'Routes',
        none: '—',
        noRoutes: 'No routes.'
      }
    },
    update: {
      title: 'Updates',
      subheader:
        'The running version, the mirror the node pulls the A/B bundles from and the outcome of the last check.',
      running: 'Running version',
      mirror: 'Update mirror',
      mirrorHelp: 'https://… with a host (the node adds the trailing slash); empty = no mirror.',
      mirrorNone: 'No mirror configured: the node does not check for updates.',
      save: {
        title: 'Save the mirror',
        hint: 'The node records the mirror in its identity and uses it from the next check on.',
        confirm: 'Save'
      },
      saved: 'Mirror saved.',
      check: 'Check now',
      checkDialog: {
        title: 'Check for updates',
        hint: 'The node reads the mirror’s index and records what it finds; no bundle is downloaded or installed.',
        confirm: 'Check'
      },
      checked: 'Check done.',
      availableTitle: 'Last check',
      neverChecked: 'No check run yet.',
      nothingNewer: 'No version newer than the running one.',
      facts: {
        checkedAt: 'Checked at',
        mirror: 'Mirror asked',
        running: 'Running at the check',
        available: 'Available',
        available_version: 'Available version',
        available_bundle: 'Bundle',
        available_sha256: 'SHA-256',
        available_size: 'Size',
        available_notes: 'Notes'
      }
    },
    eap: {
      title: '802.1X (imago-eapd)',
      subheader:
        'The EAP frontend of this node: its posture at boot, the service and the server certificate it presents.',
      enabled: 'Enabled at boot',
      disabled: 'Disabled at boot',
      active: 'Running',
      inactive: 'Stopped',
      certificate: 'Server certificate',
      certificateNone: 'No certificate installed.',
      subject: 'Subject',
      notAfter: 'Expires on',
      enableSwitch: 'Enabled',
      enableHelp:
        'Enabling with no certificate installed is refused by the node: paste or load the certificate and key below.',
      pemHelp:
        'Certificate and key together, or neither: the node verifies the pair with openssl before touching the files. The key stays in this page until you send it; it is never read back nor recorded.',
      pairRequired: 'Certificate and key come together: {missing} is missing.',
      certPem: 'Server certificate (PEM)',
      keyPem: 'Private key (PEM)',
      fromFile: 'From file',
      reveal: 'Show the key',
      hide: 'Hide the key',
      save: {
        title: 'Save 802.1X',
        hint: 'The posture and, if given, the certificate and key are written in the node’s identity; the service is started or stopped at once.',
        confirm: 'Save'
      },
      saved: '802.1X settings saved; the PEM fields were emptied.'
    }
  },
  mode: {
    tooltip: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System'
  },
  user: {
    profile: { menu: 'My profile' },
    logout: 'Log out'
  },
  profile: {
    changePassword: 'Change password',
    passwordFederated:
      'The realm “{realm}” is federated ({kind}): the password is held by the identity provider, not by Imago. Change it there.',
    loadError: 'Could not load the profile from the Imago backend.',
    sessionTitle: 'Session',
    sessionSubheader: 'The session you are using the console with.',
    forcedChange: {
      title: 'Password change required',
      hint: 'You must set a new password before continuing.'
    },
    changePasswordDialog: {
      title: 'Change password',
      current: 'Current password',
      next: 'New password',
      confirm: 'Confirm new password',
      mismatch: 'The passwords do not match.',
      federated:
        'Your realm is federated: the password is held by the identity provider, not by Imago — change it there.',
      error: 'Could not change the password.'
    }
  },
  policy: {
    subtitle: 'The global authentication, session and rate-limiting rules of Trantor Imago.',
    loadError: 'Could not load the policy.',
    saved: 'Policy updated.',
    readOnly: 'Your session can read the policy but not change it: the policy.manage permission is required.',
    range: 'Between {min} and {max}.',
    updatedAt: 'Last change: {when}',
    tabs: {
      general: 'Session & security',
      fido2: 'FIDO2',
      frontend: 'Services & channels'
    },
    fields: {
      session: {
        maxIdleMinutes: 'Session idle timeout (minutes)',
        maxAgeHours: 'Maximum session lifetime (hours)',
        maxConcurrent: 'Concurrent sessions per user (0 = unlimited)'
      },
      password: {
        minLength: 'Minimum password length'
      },
      lockout: {
        threshold: 'Lockout threshold (failed attempts)',
        durationMinutes: 'Lockout duration (minutes)'
      },
      throttle: {
        perIpPerMinute: 'Attempts per IP per minute',
        perUserPerMinute: 'Attempts per user per minute'
      },
      mfa: {
        totpPendingMinutes: 'Validity of an unconfirmed TOTP setup (minutes)'
      },
      audit: {
        retentionDays: 'Audit register retention (days)'
      }
    },
    fido2: {
      devicesTitle: 'FIDO2 — allowed devices',
      devicePolicy: 'FIDO2 device policy',
      devicePolicyHelp: 'Applies to both the FIDO2 registration and the authentication.',
      biocardOnly: 'Only the Imago BioCards listed in the inventory',
      anyDevice: 'Any FIDO2 device, for the listed subjects',
      devicesAllow: 'Who may use any FIDO2 device',
      devicesAllowHelp:
        'Users, groups or whole realms: everyone else stays bound to the BioCards. Every change is immediate and asks for your password.',
      devicesAllowInert:
        'Users, groups or whole realms — entries inert while the policy is “BioCard only” (biocardOnly): still removable from here without switching the policy. Every change is immediate and asks for your password.',
      passwordlessTitle: 'FIDO2 — passwordless login',
      passwordlessHelp:
        'Who may log in with the security key alone, no password, on the channels that support it (see the channel matrix). A passwordless login ALWAYS requires user verification (PIN/biometric). Default: nobody.',
      passwordlessEnabled: 'Enable passwordless login for the listed subjects',
      passwordlessAllow: 'Who may log in passwordless',
      passwordlessAllowHelp: 'Users, groups or whole realms. Every change is immediate and asks for your password.',
      passwordlessAllowClosed:
        'The switch is off: the list is kept but nobody logs in passwordless until you turn it on and save.',
      blacklistTitle: 'FIDO2 — self-registration forbidden',
      blacklist: 'Who may NOT self-register a FIDO2 credential',
      blacklistHelp:
        'The registration from the profile is refused to these subjects; the helpdesk can still register for them. Every change is immediate and asks for your password.',
      metadataTitle: 'FIDO2 — metadata and certification',
      metadataHelp:
        'Rules over the authenticator models the FIDO Metadata Service knows. A REVOKED or COMPROMISED model is refused always, at registration and at login: not a choice.',
      requireKnownAaguid: 'Register only authenticators with a known model (AAGUID in the metadata)',
      minCertificationLevel: 'Least FIDO certification level at registration',
      minCertificationLevelHelp: '“None” admits uncertified models too; an unknown model holds no level.',
      levelNone: 'None',
      allowedAaguids: 'Allowed AAGUIDs (one per line; empty = every model)',
      allowedAaguidsHelp: 'Applies at registration AND at login: a key of a model removed from the list stops authenticating.',
      allowedAaguidsBad: 'Invalid line: “{bad}” is not an AAGUID.',
      sourceUrl: 'FIDO Metadata Service BLOB url',
      sourceUrlHelp:
        'Empty = the environment’s url (IMAGO_FIDO_MDS_URL). When set it wins, from the next refresh on: https required, http only towards a loopback host.',
      sourceUrlBad: {
        form: 'An absolute url is required, with no credentials, query or fragment.',
        scheme: 'Scheme not admitted: https is required (http only towards a loopback host).',
        host: 'http is admitted only towards a loopback host (127.0.0.0/8, ::1, localhost): this host needs https.'
      },
      sourceUrlFromPolicy: 'from the policy',
      sourceUrlFromEnvironment: 'from the environment (IMAGO_FIDO_MDS_URL)'
    },
    enrollment: {
      title: 'Factor enrollment — allowed networks',
      allowedCidrs: 'Networks a factor may be enrolled from (CIDR)',
      allowedCidrsHelp:
        'Applies to the self-service enrollment from the profile (TOTP, FIDO2, external MFA): outside these networks the enrollment answers 403 and lands in the register. Empty = no restriction. The helpdesk’s resets are never restricted. One network per entry, no host bits (10.1.0.0/16, 2001:db8::/32).',
      allowedCidrsBad: 'Invalid entry: “{bad}” is not a network in CIDR form without host bits (10.1.0.0/16, 2001:db8::/32).'
    },
    ldap: {
      title: 'Group release — LDAP service',
      releaseGroups: 'Groups releasable over LDAP (memberOf)',
      releaseGroupsHelp:
        'Global allowlist for the LDAP service: every consumer’s memberOf is filtered to these groups (verbatim). Empty = no group. OIDC and RADIUS are configured per app instead.'
    },
    channels: {
      title: 'Channel capabilities',
      help: 'Read-only: what each channel can carry (enrollment gate, factor picker, interactive rounds, passwordless, proofs). “eap” is the 802.1X inner authentication.',
      colChannel: 'Channel',
      enrollmentGate: 'Enrollment gate',
      picker: 'Factor picker',
      interactiveRoundAnswer: 'Interactive round (answer)',
      interactiveRoundWait: 'Interactive round (wait)',
      passwordless: 'Passwordless',
      proofKinds: 'Transportable proofs',
      proofs: {
        otp: 'OTP (typed code)',
        assertion: 'WebAuthn assertion'
      },
      names: {
        portal: 'Portal (native API)',
        oidc: 'OIDC',
        saml: 'SAML',
        pi: 'privacyIDEA',
        radius: 'RADIUS',
        eap: '802.1X (EAP)',
        ldap: 'LDAP',
        proxy: 'Identity-aware proxy',
        pba: 'PBA (pre-boot gate)',
        cp: 'Credential Provider (Windows)'
      }
    },
    scope: {
      colEntry: 'Entry',
      colKind: 'Kind',
      kindUser: 'User',
      kindGroup: 'Group',
      kindRealm: 'Whole realm',
      userName: 'Username (without @realm)',
      groupName: 'Group (as the directory reports it)',
      realm: 'Realm',
      realmEmpty: 'No readable realm: entries can be added once the realm list is available.',
      nativeNoGroups: 'Native realm: group entries name a local group (System › Local groups).',
      hint: 'The entry is written right after the password confirmation; “@realm” covers every subject of the realm, no wildcard.',
      listed: '“{entry}” is already listed.',
      empty: 'No entries.',
      add: 'Add',
      remove: 'Remove',
      addTitle: 'Add the entry',
      removeTitle: 'Remove the entry',
      addHint: 'Adds “{entry}” to {path}. Asks for a fresh password confirmation.',
      removeHint: 'Removes “{entry}” from {path}. Asks for a fresh password confirmation.',
      alreadyPresent: 'No change: the entry was already present (same normalised form).',
      alreadyAbsent: 'No change: the entry was already absent.',
      unknownRealm: 'The realm “{realm}” does not exist: the entry was refused.',
      badName: 'Name refused: {detail}',
      badPath: '“{path}” is not a scope list of the policy: {detail}',
      editError: 'Could not change the list.'
    },
    save: {
      title: 'Save the policy',
      hint: 'Changing the policy asks for a fresh password confirmation. The scope lists are not part of the save: they were already written entry by entry.',
      refused: 'Refused by the lockout guard: {detail}',
      error: 'Could not save the policy.'
    }
  },

  mfa: {
    and: 'and',
    chip: {
      unknown: 'Unknown',
      active: 'MFA active',
      missing: 'MFA owed, factor missing',
      none: 'MFA not set up'
    },
    gate: {
      missing:
        'You must set up {factors}: policy {policies} requires it. Until then your session reaches only this page and the factor enrollment.',
      satisfied: 'Your account must hold {factors} (policy {policies}): all enrolled.',
      breakGlass:
        'Break-glass account: no MFA obligation by construction. An enrolled factor is still challenged at login.'
    },
    removeFree: 'You are about to remove the second factor from your account.',
    removeOwed:
      'The TOTP factor is owed by policy {policies}: without it your account no longer meets the obligation and the next session stays at the enrollment gate.',
    done: 'Done',
    card: {
      title: 'TOTP second factor (authenticator app)',
      subheader: 'Add a second factor to your sign-in.',
      loadError: 'Could not read the MFA status.',
      configured: 'Set up',
      notConfigured: 'Not set up',
      expired: 'Expired',
      expiredHelp: 'The TOTP setup you started was not confirmed in time: the QR code is no longer valid. Start again.',
      restart: 'Start the setup again',
      configuredAt: 'Set up on: {at}',
      recoveryCodesRemaining: 'Recovery codes left: {count}',
      enroll: 'Set up TOTP',
      regenerate: 'Regenerate codes',
      removeTitle: 'Remove TOTP',
      regenerateTitle: 'Regenerate recovery codes',
      regenerateHint: 'Regenerating invalidates all the previous codes.',
      regenerateConfirm: 'Regenerate',
      recoveryCodesTitle: 'Recovery codes'
    },
    enroll: {
      startError: 'Could not start the TOTP setup.',
      codeRejected: 'Invalid code. Try again.',
      expired: 'The setup expired before it was confirmed: close and start again (the QR changes).',
      instructions:
        'Scan the QR code with your authenticator app, or enter the key manually, then type the 6-digit code to confirm.',
      secretLabel: 'Key (manual entry):',
      parameters: '{algorithm} · {digits} digits · {period}s',
      codeLabel: 'Verification code',
      acknowledged: 'I have saved my recovery codes.',
      confirm: 'Confirm'
    },
    recoveryCodes: {
      warning:
        'Keep these codes in a safe place: they are shown only once and replace your second factor if you lose your authenticator.',
      copy: 'Copy',
      copied: 'Copied',
      download: 'Download'
    },

    // The external factors the policies owe (`ext:<name>` in `owed`): the enrollment on
    // the bridge from the profile (backlog A.4, 2026-11-04) — Imago asks the bridge's
    // /enroll and shows what it answered, once.
    external: {
      owed: 'External factor required by the policy: {name}',
      enroll: 'Enroll',
      enrollTitle: 'Enrollment on “{name}”',
      enrollHint:
        'Imago asks the external service to start your enrollment: you will get a link to open, a code to enter or an instruction to follow. What you see here is shown only once.',
      started: 'Enrollment started on the external service “{name}”.',
      openLink: 'Open the enrollment link',
      secret: 'One-time code (shown only once):',
      message: 'Instructions from the service:',
      expiresAt: 'Valid until: {at}',
      noCapability: 'The external service does not offer enrollment from the portal: contact the support desk.',
      alreadyEnrolled: 'You are already enrolled on the external service.',
      unavailable: 'The external service does not answer or is out of service: try again later.',
      refused: 'The external service refused the enrollment: {detail}',
      error: 'Could not start the enrollment on the external service.'
    },
    fido2: {
      subheader: 'Enroll your Imago BioCard or, when the policy allows it, another FIDO2 security key.',
      enroll: 'Add FIDO2 card',
      enrollDuress: 'Add duress credential',
      inventoryHint:
        'The card must be one of the corporate Imago BioCards (listed in the inventory). During the enrollment, tap or insert the card when the browser asks for it.',
      discoverable: 'Store the username on the FIDO2 device',
      discoverableHelp:
        'Lets you sign in without typing your username (“Sign in with your Imago BioCard”). Requires the device’s PIN or biometrics and takes one slot on it.',
      duressHint:
        'Duress credential: enroll it by placing your DURESS finger on the BioCard; a sign-in with that finger succeeds as usual and raises the alarm without the coercer noticing. After the enrollment the dashboard asks for a verification with the same finger.',
      loadError: 'Could not read the enrolled cards.',
      none: 'No FIDO2 card enrolled.',
      cardName: 'FIDO2 card',
      duressChip: 'duress',
      proofVerified: 'binding verified',
      proofPending: 'binding to verify',
      registeredAt: 'Enrolled on: {at}',
      lastUsedAt: 'Last used: {at}',
      neverUsed: 'Never used',
      proofOk: 'Binding verified: the finger used exposes exactly this credential.',
      proofError: 'Binding proof failed.',
      startError: 'Could not start the card enrollment.',
      finishError: 'Could not enroll the card.',
      registeredTitle: 'Card enrolled',
      registered: 'The card has been enrolled as your second factor.',
      registeredSerial: 'The card “{serial}” has been enrolled as your second factor.',
      removeTitle: 'Remove FIDO2 card',
      removeHint: 'You are about to remove the card “{name}” from your account.',
      webauthn: {
        notAllowed: 'Operation cancelled: no card was presented in time, or the request was refused.',
        invalidState: 'This card is already enrolled on your account.',
        notSupported: 'The browser or the authenticator does not support this kind of credential.',
        security: 'Insecure context: the FIDO2 enrollment requires a valid HTTPS connection.',
        abort: 'Enrollment interrupted. Try again.',
        generic:
          'Could not complete the card enrollment. Check that the card is connected (or resting on the NFC reader) and try again.'
      }
    }
  },
  mfaPolicies: {
    title: 'MFA enforcement policies',
    subheader: 'Who must HOLD which factors: rules (enforce / omit) applied to users, groups or whole realms.',
    help: 'An obligation is one of possession: at login one of the factors held suffices. Rules add up; an omit holds only when more specific than the enforce.',
    helpDetail:
      '“Both” means “both enrolled”. An omit lifts only the factor it names and only when more specific than the enforce (user > group > realm; at equal specificity the enforce wins). An owed local factor that is missing sends to the portal’s enrollment gate and refuses the other channels; an owed external factor is asked at login and, when not enrolled, refuses everywhere. The break-glass owes nothing; a realm exempt from Imago’s MFA (mfaExempt) makes the policies covering it inert.',
    new: 'New policy',
    empty: 'No MFA policy: nobody must hold a second factor (whoever enrolled one is still challenged at login).',
    loadError: 'Could not load the MFA policies.',
    colName: 'Name',
    colEnabled: 'Enabled',
    colRules: 'Rules',
    colScope: 'Scope',
    colUpdated: 'Updated',
    enabled: 'Enabled',
    disabled: 'Disabled',
    enable: 'Enable',
    disable: 'Disable',
    enableTitle: 'Enable the policy',
    disableTitle: 'Disable the policy',
    enableHint: 'Policy “{policy}” contributes to the resolution again from the next login. Asks for your password.',
    disableHint:
      'Policy “{policy}” is kept but no longer contributes to the resolution: whoever owed a factor through it alone owes it no more from the next login. Asks for your password.',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete the policy',
    deleteHint:
      'Policy “{policy}” is deleted: the subjects it obliged owe, from their next resolution, whatever the remaining policies say. Asks for your password.',
    deleteRefused: 'Deletion refused: {detail}',
    scopeCount: '{count} entries',
    moreScope: '+{count}',
    factors: {
      totp: 'TOTP (authenticator app)',
      fido2: 'FIDO2 (BioCard or security key)',
      any: 'TOTP or FIDO2 (at least one)',
      both: 'TOTP and FIDO2 (both)',
      external: 'External MFA “{name}”'
    },
    rules: {
      colRule: 'Rule',
      colReading: 'Meaning',
      verbLabel: 'Action',
      familyLabel: 'Factor',
      verb: { enforce: 'Enforce', omit: 'Omit' },
      family: { local: 'Local', ext: 'External' },
      factor: 'Factor',
      externalName: 'External MFA',
      add: 'Add rule',
      empty: 'No rule.',
      hint: '“At least one” = one of the two; “both” = both enrolled (stored as written). An omit with no enforce to lift is inert, never an error. BioCard is not a factor: the factor is FIDO2.',
      extHint:
        'The name of a configured external MFA (the “External MFA bridges” section below, or create-external-mfa). A disabled resource stays citable, but whoever owes it fails closed at login until it is enabled again; the server refuses a name that does not exist, saying so.',
      extNone:
        'No external MFA configured: create one in the “External MFA bridges” section (or with create-external-mfa) before citing it in a rule.',
      extDisabled: 'disabled',
      extCited: 'cited by the policies',
      duplicate: 'Rule “{rule}” is already listed.',
      full: 'A policy carries at most {max} rules.'
    },
    scope: {
      hint: 'The entries are sent with the policy; “@realm” covers every subject of the realm, no wildcard. The server checks every realm exists.',
      full: 'A policy carries at most {max} scope entries.'
    },
    dialog: {
      nameRequired: 'The name is needed.',
      createTitle: 'New MFA policy',
      editTitle: 'Edit MFA policy',
      name: 'Name',
      nameHelp:
        'Lowercase letters, digits and hyphens (1–64), starting and ending alphanumeric; unique case-insensitively. Immutable after creation.',
      nameImmutable: 'The name is immutable: to rename, delete and recreate.',
      nameInvalid:
        'Invalid name: lowercase letters, digits and hyphens, 1–64 characters, starting and ending alphanumeric.',
      conflict: 'A policy with this name already exists (case does not matter).',
      enabled: 'Enabled',
      enabledHelp: 'A disabled policy is kept but never resolved: it contributes to no obligation.',
      rules: 'Rules',
      rulesHelp: 'Applied together to every scope entry. On save the list replaces the previous one.',
      rulesRequired: 'At least one rule is needed.',
      scope: 'Scope',
      scopeHelp:
        'Who the rules apply to: users, groups (as the directory reports them) or whole realms. On save the list replaces the previous one.',
      scopeRequired: 'At least one scope entry is needed.',
      sudoNote: 'Creating or editing asks for your password and is audited under your name.',
      ruleRefused: 'Rule refused ({field}): {detail}',
      scopeRealmRefused: 'Scope entry refused ({field}): {detail}',
      scopeNameRefused: 'Scope entry refused ({field}): {detail}',
      createError: 'Could not create the policy.',
      updateError: 'Could not update the policy.'
    },
    effective: {
      title: 'Effective MFA',
      subheader:
        '“Why is mario@acme asked for a factor?” — what a subject owes under the enabled policies, and by which rule.',
      user: 'Username (without @realm)',
      userHelp: 'Without “@realm”: the realm is picked beside.',
      realm: 'Realm',
      resolve: 'Resolve',
      owed: 'Owed factors',
      owedNone: 'none — the password alone suffices (an enrolled factor is still challenged)',
      groups: 'Groups read from the realm now',
      groupsNone: 'none (the subject is in no group, or the directory reports none)',
      groupsUnknown: 'unknown: they are known only at login',
      groupsUnknownHelp:
        'On a radius, oidc or saml realm the subject’s groups are known ONLY at login (the Access-Accept’s Class/Filter-Id, the IdP’s claims): this view matched the group entries against NO groups, so a group-scoped policy may add obligations at login that “Owed factors” does not show here.',
      breakGlass: 'Break-glass',
      breakGlassHelp: 'The protected row of the native realm owes nothing by construction: no policy applies.',
      mfaExempt: 'MFA-exempt (the realm does its own)',
      mfaExemptHelp:
        'The realm is exempt from Imago’s MFA (“mfaExempt: true”): it already does its own MFA, this subject is asked no factor — neither local nor external — and every policy covering it is inert.',
      provenanceNone: 'No rule matches this subject.',
      colFactor: 'Factor',
      colEffective: 'Outcome',
      colEnforcedBy: 'Enforced by',
      colOmittedBy: 'Omitted by',
      outcome: { owed: 'Owed', omitted: 'Omitted', inertOmit: 'Inert omit' },
      level: { '1': 'realm', '2': 'group', '3': 'user' },
      legend:
        'Specificity: {user} > {group} > {realm}; an omit lifts the factor only when STRICTLY more specific than every enforce on it (at parity the enforce wins). “Inert omit”: an omit with no enforce to lift, or one that would tighten (omit:local:totp against enforce:local:any).',
      notFound: 'No user “{user}” in realm “{realm}” (or the realm does not exist).',
      unavailable: 'The realm’s directory does not answer: {detail}',
      error: 'Could not resolve the effective MFA.'
    }
  },
  mfaSubjects: {
    title: 'Look up a user',
    subheader:
      '“What has mario@acme enrolled?” — a user’s active MFA methods (native TOTP, FIDO2 credentials, external MFA) beside their effective MFA, to reset one of them.',
    subject: 'User',
    subjectPlaceholder: 'mario@acme',
    subjectHelp:
      'user@realm or realm\\user (the LAST “@” qualifies the realm; ACME\\mario = mario@acme). A bare name is a user of the native “local” realm.',
    subjectEmptyPart:
      'Empty part: after “@” (or before “\\”) the realm is needed, before it the username. A bare name means the “local” realm.',
    subjectTooShort: 'At least {min} characters.',
    subjectTooLong: 'At most {max} characters.',
    search: 'Look up',
    noPermission:
      'Your session lacks the enrollment.read permission: a user’s enrolled methods cannot be read from here.',
    readOnly: 'With enrollment.read alone the methods can be read but not reset (enrollment.reset is needed).',
    malformed: 'Invalid user: {detail}',
    notFound: 'User “{subject}” not found: {detail}',
    unavailable: 'The realm’s directory does not answer: {detail}',
    loadError: 'Could not read the user’s MFA methods.',
    resolved: 'User',
    realmKind: 'realm {kind}',
    heldNone:
      'No second factor held by Imago: the user logs in with the password alone (or goes to the enrollment gate, if a policy owes them a local factor). External MFAs may still know them upstream.',
    colMethod: 'Method',
    colState: 'State',
    colFacts: 'Facts',
    since: 'since {at}',
    created: 'registered on {at}',
    lastUsed: 'last used {at}',
    neverUsed: 'never used',
    totp: {
      label: 'TOTP (authenticator app)',
      state: { enrolled: 'Enrolled', pending: 'Pending', none: 'Not enrolled' },
      pendingHelp: 'A secret was minted but no code confirmed it yet (the half-scanned QR): the reset removes it too.',
      recoveryCodesLeft: '{count} recovery codes left',
      enrollmentId: 'enrollment id',
      reset: 'Reset TOTP',
      resetTitle: 'Reset the TOTP',
      resetHint:
        'You are about to reset the TOTP of “{subject}”: the secret and the recovery codes are removed (a pending secret too); the FIDO2 credentials stay. The user re-enrolls at the next login and receives a notification e-mail, when they have an address. Asks for your password.'
    },
    fido2: {
      unlabeled: 'unnamed credential',
      credentialId: 'credential ID',
      registered: 'Registered',
      personal: 'personal key (no BioCard)',
      biocard: 'BioCard S/N {serial}',
      biocardStatus: { issued: 'In stock', assigned: 'Assigned', revoked: 'Revoked' },
      delete: 'Delete',
      deleteTitle: 'Delete the FIDO2 credential',
      deleteHint:
        'You are about to delete the FIDO2 credential “{label}” of “{subject}”: the other credentials and the TOTP stay. The user receives a notification e-mail, when they have an address. Asks for your password.',
      deleteHintBiocard:
        ' The credential is registered against BioCard S/N {serial}: the inventory row and its assignment stay, the same card can be registered again.'
    },
    external: {
      disabled: 'disabled',
      lookup: { supported: 'lookup available', unsupported: 'lookup not available' },
      lookupHelp: {
        supported:
          'The probe observed /lookup on the bridge: the subject’s enrollment is asked of the bridge (when healthy).',
        unsupported:
          'The bridge exposes no /lookup (challenge/response only, or never probed): the upstream enrollment is not knowable from Imago; at login the external round is mandatory for whoever owes it.'
      },
      enrolled: { yes: 'Enrolled on the bridge', no: 'Not enrolled on the bridge', unknown: 'Unknown' },
      unknownHelp: 'The bridge was not asked, or the call failed: {detail}',
      enroll: 'Enroll',
      enrollHelp: 'The upstream enrollment page the bridge pointed at (opens in a new tab).',
      notResettable:
        'Not resettable from Imago: the enrollment lives upstream of the bridge and the bridge API has no “unenroll”. Manage it there.'
    },
    resetAll: 'Reset all',
    resetAllTitle: 'Reset every method',
    resetAllHint:
      'You are about to reset everything Imago holds for “{subject}”: {held}. External MFAs are not touched (they live on the bridge). The user re-enrolls at the next login and receives a notification e-mail, when they have an address. Asks for your password.',
    heldTotp: 'the TOTP (secret and recovery codes)',
    heldFido2: '{count} FIDO2 credentials',
    resetRefused: 'Reset refused: {detail}',
    cleared: {
      title: 'Reset for “{subject}” ({method})',
      totp: 'TOTP removed (secret and recovery codes)',
      fido2: '{count} FIDO2 credentials removed',
      biocardsStay: 'BioCard S/N {serials}: the inventory row and its assignment stay',
      nothing: 'nothing to remove: the user did not hold this method',
      notified: 'notification e-mail sent to the user',
      notNotified: 'no e-mail sent (nothing removed, no address, a realm without contacts, or the send failed)'
    },
    effectiveTitle: 'Effective MFA',
    effectiveHelp: 'What the user OWES under the enabled policies — the same answer as the “Effective MFA” card.'
  },
  externalMfas: {
    title: 'External MFA bridges',
    subheader:
      'The bridges that verify an external factor (SMS, push, privacyIDEA…): one resource per bridge, cited by the policies as “ext:<name>”.',
    help: 'An external MFA is a bridge (endpoint + shared secret) adapting a real service to Imago’s challenge/response API. Who must use it is said only by the policies (“enforce:ext:sms” on a group or a realm): there is no scope here.',
    helpDetail:
      'The capabilities (lookup, enroll, cancel, multi-round) are derived from the probe, never declared by hand: until a probe succeeds the resource serves no login. A periodic probe checks every bridge at its own cadence (every 5 minutes by default); when the last successful probe is older than 20 minutes the bridge is stale: disabled at the effective level even though the configuration says “enabled”. A bridge that is down, times out, is stale or is disabled while a policy still owes it makes the login fail closed (503, no lockout hit): disabling never exempts.',
    noPermission: 'Your session lacks the externalMfa.read permission: the list of bridges cannot be read from here.',
    new: 'New external MFA',
    empty:
      'No external MFA configured: an “ext:<name>” rule cannot be cited until you create one (here or with create-external-mfa).',
    loadError: 'Could not load the external MFAs.',
    colName: 'Name',
    colEndpoint: 'Endpoint',
    colEnabled: 'Enabled',
    colHealth: 'Health',
    colCapabilities: 'Observed capabilities',
    colProbe: 'Last probe',
    colInterval: 'Probe cadence',
    interval: 'every {seconds} s',
    intervalHelp:
      'Cadence of the periodic probe: the job probes the bridge when its last probe is at least this old. Fixed staleness window: {minutes} minutes.',
    probeScheduled: 'Immediate probe scheduled for “{name}”: the verdict lands in a few seconds.',
    reload: 'Reload',
    timeout: 'timeout {ms} ms',
    enabled: 'Enabled',
    disabled: 'Disabled',
    enable: 'Enable',
    disable: 'Disable',
    enableTitle: 'Enable the external MFA',
    disableTitle: 'Disable the external MFA',
    enableHint: 'External MFA “{name}” is offered at login again from the next access. Asks for your password.',
    disableHint:
      'External MFA “{name}” is no longer offered: whoever owes it through a policy does NOT get in (503 mfa_unavailable, no lockout hit) until you enable it again or change the policy. Disabling never exempts. Asks for your password.',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete the external MFA',
    deleteHint:
      'External MFA “{name}” and its keystore secret are deleted; a login round in flight against the bridge ends at its next step. Asks for your password.',
    deleteCitedHint:
      'External MFA “{name}” is cited by the policies {policies}: the server refuses the deletion (409 in_use) while a rule names it. Remove the rule or the policy first, then retry. Asks for your password.',
    deleteRefused: 'Deletion refused: {detail}',
    neverProbed: 'never probed: no capability known, the resource serves no login until a probe succeeds',
    probedAt: 'observed on {at}',
    capabilities: { lookup: 'lookup', enroll: 'enroll', cancel: 'cancel', multiRound: 'multi-round', wait: 'wait' },
    capabilityHelp: {
      lookup:
        'POST /lookup: the bridge says whether the subject is enrolled. With the lookup the factor is offered beside the local ones and an unenrolled subject is refused, saying so; without it the external round is mandatory for whoever owes it.',
      enroll: 'POST /enroll: the subject can enroll on the bridge from their own profile (Imago asks the bridge to start the enrollment and shows once the link, code or instructions it answers).',
      cancel: 'POST /cancel: an abandoned round is released on the bridge.',
      multiRound: '/response may answer a NEW challenge (n rounds).',
      wait: '/challenge may open a WAIT round ({kind: "wait"}): the user confirms elsewhere and Imago polls /response without a text.'
    },
    outcomes: {
      ok: 'Answers',
      unauthorized: 'Secret refused',
      unreachable: 'Unreachable',
      invalid: 'Invalid answer'
    },
    health: {
      states: {
        ok: 'Healthy',
        stale: 'Stale',
        unreachable: 'Unreachable',
        unauthorized: 'Secret refused',
        invalid: 'Invalid answer',
        unprobed: 'Unprobed'
      },
      note: {
        ok: 'last ok probe at {at}: serves logins',
        stale: 'last ok probe at {at}, older than {minutes} minutes: disabled at the effective level',
        unprobed: 'no probe since {since}: serves no login until a probe succeeds (the immediate one is on its way)',
        grace:
          'last probe failed at {since}, last ok at {at}: still serves logins while the {minutes}-minute window stays open',
        neverOk: 'no successful probe since {since}: serves no login until a probe succeeds'
      },
      never: 'never',
      since: 'state since {at}',
      lastOk: 'last ok probe: {at}',
      lastOkNever: 'last ok probe: never',
      effectivelyDisabled: 'disabled at the effective level',
      effectivelyDisabledHelp:
        'The switch says “enabled” but the bridge’s health serves no login: whoever owes it through a policy fails closed (503 mfa_unavailable) until a probe succeeds.'
    },
    probe: {
      action: 'Probe',
      title: 'Probe external MFA',
      hint: 'One GET /capabilities on {endpoint} with the stored secret: the verdict is recorded on the resource and, when ok, resets the staleness clock (the same probe the job repeats at the resource’s cadence). No challenge is sent (a canary would fire an SMS or a push at a real person).',
      run: 'Probe',
      outcome: 'Outcome',
      health: 'Health after the probe',
      capabilities: 'Observed capabilities',
      error: 'Could not run the probe.'
    },
    dialog: {
      createTitle: 'New external MFA',
      editTitle: 'Edit external MFA',
      name: 'Name',
      nameHelp:
        'The name the rules cite (“enforce:ext:<name>”): lowercase letters, digits and hyphens (1–64), starting and ending alphanumeric; unique case-insensitively. Immutable after creation.',
      nameImmutable: 'The name is immutable: to rename, delete and recreate (and update the policies citing it).',
      nameInvalid:
        'Invalid name: lowercase letters, digits and hyphens, 1–64 characters, starting and ending alphanumeric.',
      nameReserved: 'Name reserved to the local factors (totp, fido2, any, both, local).',
      nameRequired: 'The name is needed.',
      conflict: 'An external MFA with this name already exists (case does not matter).',
      displayName: 'Picker label',
      displayNameHelp: 'Shown verbatim to whoever picks the factor at login (max 64). Empty = same as the name.',
      endpoint: 'Bridge endpoint',
      endpointHelp:
        'Base URL (scheme + host [+ port] [+ prefix]); Imago appends /api/imago-external-mfa/v1/…. https is required; http only on a loopback host (127.0.0.0/8, ::1, localhost).',
      endpointRequired: 'The endpoint is needed.',
      endpointRule: {
        malformed: 'Not a URL.',
        scheme: 'Scheme not admitted: https is required (http only on a loopback host).',
        httpNotLoopback:
          'http is admitted only on a loopback host (127.0.0.0/8, ::1, localhost): elsewhere https is required.',
        extras: 'No credentials, query or fragment in the URL.'
      },
      secret: 'Shared secret (PSK)',
      secretHelp:
        'Presented to the bridge as Bearer on every call; encrypted in the keystore, never shown. At least {min} characters.',
      secretKeepHelp: 'Empty = unchanged; type it to rotate it. It is never shown.',
      secretShort: 'The secret must be at least {min} characters long.',
      secretRequired: 'The secret is needed (at least {min} characters).',
      timeout: 'Per-request timeout (ms)',
      timeoutHelp:
        'From {min} to {max} ms, for every call to the bridge. Expired: 503 mfa_unavailable at login, “unreachable” in the probe.',
      timeoutInvalid: 'An integer from {min} to {max}.',
      probeInterval: 'Probe interval (s)',
      probeIntervalHelp:
        'From {min} to {max} s (default {default}): how often the job probes the bridge. The staleness window is fixed at {minutes} minutes.',
      probeIntervalAboveWindow:
        'Above {window} s the bridge reads stale (disabled at the effective level) between one successful probe and the next: the resource serves only right after each probe. Admitted, knowingly.',
      probeIntervalInvalid: 'An integer from {min} to {max}.',
      enabled: 'Enabled',
      enabledHelp:
        'Disabled it is neither offered nor probed, and whoever owes it through a policy fails closed at login: never a silent exemption. Enabling it again starts a probe at once.',
      clearsNote:
        'You are changing {fields}: on save the observed capabilities and the last probe are cleared (the resource serves no login) and a probe starts at once.',
      sudoNote:
        'Creating or editing asks for your password and is audited under your name. On create, and on every change of the endpoint, the secret or the timeout, the observed capabilities are cleared and a probe starts AT ONCE (one GET /capabilities at the bridge, never a challenge): until it succeeds the resource serves no login.',
      fieldRefused: 'Field refused ({field}): {detail}',
      createError: 'Could not create the external MFA.',
      updateError: 'Could not update the external MFA.'
    }
  },
  legacy: {
    landing: {
      title: 'Imago 4Legacy',
      body: 'The user opens the app on its public host (app1.imago.acme.lan), logs in on Imago with Imago’s methods — FIDO2, TOTP, external MFA — and Imago logs them into the legacy app in the intranet by handing it the real username and an ephemeral password.',
      how1: 'The data plane is nginx: one vhost per app, rendered and applied by Imago (auth_request on every request, the seven identity headers always the proxy’s).',
      how2: 'The app verifies the ephemeral password on Imago’s ephemeral backend (LDAP or RADIUS): every legacy app generates its own consumer, recognised by the app’s source address.',
      how3: 'The portal logout ends every proxy session of the user (single logout); a logout done inside the app never ends Imago.',
      deployHint:
        'On the appliance nginx, the sudoers rule and the imago.d directory are already in place: what remains is to set IMAGO_4LEGACY_NGINX_MODE=apply in /etc/trantor-imago/httpd.env and to provide the wildcard certificate and DNS. The app must stay reachable ONLY from the proxy and Imago.',
      cta: 'Go to the legacy apps'
    },
    list: {
      subheader:
        'The legacy web applications behind the identity-aware proxy: each one generates its own ephemeral consumer (LDAP or RADIUS) and its own nginx vhost.',
      create: 'New legacy app',
      applySite: 'Apply nginx',
      empty: 'No legacy app registered.',
      loadError: 'Could not load the legacy apps.',
      colName: 'Name',
      colPublicHost: 'Public host',
      colUpstream: 'Upstream',
      colMode: 'Login',
      colState: 'State',
      colConsumer: 'Ephemeral consumer',
      enabled: 'Enabled',
      disabled: 'Disabled',
      edit: 'Edit',
      preview: 'nginx preview',
      delete: 'Delete',
      fallback: 'fallback: {fallback}'
    },
    modes: {
      formReplay: 'Form replay',
      injectPrefill: 'Injected prefill',
      httpBasic: 'HTTP Basic',
      trustedHeader: 'Trusted header'
    },
    modeHelp: {
      formReplay:
        'Imago fills the app’s login form server-side with the ephemeral password: it never enters the browser. The default.',
      injectPrefill:
        'A script injected into the login page fills the username and the one-shot ephemeral password and submits (SPAs and JS-built forms).',
      httpBasic: 'nginx injects Authorization: Basic with a credential bound to the proxy session, on every request.',
      trustedHeader: 'The app trusts X-Forwarded-User and the other identity headers: no credential is minted.'
    },
    fallbacks: {
      injectPrefill: 'Injected prefill',
      none: 'None (the app shows its own login)'
    },
    successWhen: {
      redirectOffLoginPage: 'The answer redirects off the login page',
      setCookie: 'The answer sets a cookie',
      formGone: 'The login form is gone'
    },
    csps: {
      nonce: 'Rewrite the CSP with a nonce for the script',
      strip: 'Strip the CSP on the injected pages'
    },
    lifetimes: {
      session: 'Bound to the proxy session',
      oneShot: 'One-shot'
    },
    lifetimeDerived: 'Derived from the mode: {lifetime}',
    lifetimeNone: 'No credential (trusted header)',
    alphabets: {
      alnum: 'Letters and digits',
      alnumSymbols: 'Letters, digits and symbols (!#$%&*+-=?@_)'
    },
    hostModes: {
      public: 'Public host (no rewrites)',
      upstream: 'Upstream host (with rewrites)'
    },
    backendKinds: {
      ldap: 'Ephemeral LDAP (imago-ldapd)',
      radius: 'Ephemeral RADIUS (imago-radiusd)'
    },
    dialog: {
      createTitle: 'New legacy app',
      editTitle: 'Edit legacy app',
      tabs: {
        general: 'General',
        backend: 'Backend and groups',
        headers: 'Headers and logout'
      },
      sections: {
        network: 'Network / Upstream',
        login: 'Login',
        headers: 'Headers',
        logout: 'Logout',
        groups: 'Released groups',
        backend: 'Backend'
      },
      name: 'Name',
      nameHelp:
        'A lowercase host label (a-z, 0-9, hyphens), immutable: it names the vhost file, the ephemeral consumer and the audience of the credentials.',
      displayName: 'Display name',
      enabled: 'Enabled',
      enabledHelp:
        'A disabled app renders no vhost (the apply removes it), refuses the gate and every credential; profile and consumer survive.',
      publicHost: 'Public host',
      publicHostHelp:
        'The host the user opens: the vhost’s server_name, covered by the wildcard certificate and the DNS wildcard.',
      upstreamUrl: 'Upstream URL',
      upstreamUrlHelp: 'The app’s origin as the proxy reaches it: scheme, host and port, no path.',
      hostMode: 'Host toward the app',
      hostModeHelp:
        'Public: the app must accept its public name, zero rewrites. Upstream: the app sees its own host and the proxy rewrites redirects, cookies and body.',
      tlsVerify: 'Verify the upstream’s certificate',
      tlsCa: 'Upstream CA (PEM, optional)',
      tlsCaHelp:
        'The bundle of the CAs anchoring the upstream’s certificate, when the system store does not hold them.',
      rewriteTitle: 'Rewrites (upstream host only)',
      rewriteRedirects: 'Rewrite redirects (Location)',
      rewriteCookies: 'Strip the cookies’ Domain',
      rewriteBody: 'Content types rewritten in the body',
      rewriteBodyHelp: 'The content types whose body is rewritten (sub_filter). Empty: no body rewrite.',
      loginMode: 'Login mode',
      fallback: 'Replay fallback',
      fallbackHelp: 'What the replay does when it fails (no form, the POST refused, the success heuristic not met).',
      formTitle: 'The app’s login form',
      formHelp: 'The descriptor the replay engine reads — and the page the prefill lands on.',
      learn: 'Learn the form',
      learnHelp:
        'Imago reads the login page through the SAVED upstream and proposes the block: nothing is saved until you press Save.',
      learnCreateHint: 'Available after the create: the learning reads the app’s saved upstream.',
      learnFound: 'Form found: username field “{userField}”, password field “{passField}”. Form replay proposed.',
      learnNoForm: 'No server-rendered form (built in JavaScript): injected prefill proposed.',
      learnError: 'Could not read the app’s login page.',
      formUrl: 'Login page',
      formUrlHelp: 'The path of the login page (/login, /index.php?login).',
      formUserField: 'Username field',
      formUserFieldHelp: 'The input’s name; blank, the engine guesses.',
      formPassField: 'Password field',
      formPassFieldHelp: 'The input’s name; blank, the engine guesses.',
      formSuccessWhen: 'Login succeeded when',
      injectTitle: 'Injected prefill',
      injectHelp:
        'nginx appends the prefill script to the pages listed (with a per-request CSP nonce): it fills the selectors with the username and the one-shot ephemeral password and submits.',
      injectPages: 'Injected pages',
      injectPagesHelp: 'The exact paths that get the script; the login page is always included.',
      injectUserSelector: 'Username selector',
      injectPassSelector: 'Password selector',
      injectSubmitSelector: 'Submit selector',
      injectAutoSubmit: 'Submit automatically',
      injectCsp: 'Content-Security-Policy',
      injectCspHelp:
        'With “nonce” the app’s CSP is REPLACED by one admitting the script: apps with inline scripts need “strip”.',
      credentialTitle: 'Ephemeral credential',
      credentialHelp:
        'Real username, only the password is ephemeral. Session-bound for HTTP Basic (it rides every request), one-shot for replay and prefill (spent on the first successful verify).',
      lifetime: 'Lifetime',
      lifetimeEffective: 'Effective lifetime: {lifetime}',
      ttlSeconds: 'One-shot credential TTL (s)',
      ttlSecondsHelp: '10 to 3600 seconds; a session-bound credential lives as long as the proxy session.',
      passwordShape: 'Custom password shape',
      passwordShapeHelp:
        'For an app that limits its password field. Off: a password generated by Imago (timec_ prefix + 22 characters).',
      passwordLength: 'Length (16–64)',
      passwordAlphabet: 'Alphabet',
      headersSet: 'Headers toward the app',
      headersSetHelp:
        'One per line, Name: value (proxy_set_header). The seven identity headers are always the proxy’s: naming one is refused.',
      headersRemove: 'Headers hidden from the client',
      headersRemoveHelp: 'Response headers hidden (proxy_hide_header), e.g. X-Powered-By.',
      logoutUrl: 'The app’s logout path',
      logoutUrlHelp: 'E.g. /logout, /front/logout.php. Blank: no app logout is called.',
      onPortalLogout: 'Call the app’s logout on the portal logout',
      onPortalLogoutHelp:
        'The Imago logout ends every proxy session anyway; with this the webui also loads the app’s logout, best-effort. A logout done inside the app never ends Imago.',
      releaseGroups: 'Released groups',
      releaseGroupsHelp:
        'The group allowlist the app is entitled to: released as X-Forwarded-Groups on the gate and as the identity’s groups on the ephemeral verify; copied onto the generated consumer.',
      backendKind: 'Ephemeral backend',
      backendKindHelp: 'The backend the app verifies the ephemeral password on.',
      backendSharedSecret: 'RADIUS shared secret',
      backendSharedSecretHelp:
        'The app’s NAS secret (kind radius): sealed into the generated consumer’s keystore, never shown; empty on edit = the stored one stays.',
      sourceKind: 'Source',
      sourceCidr: 'Source range',
      sourceIp: 'Source address',
      sourceHelp:
        'The address the app verifies the ephemeral password from (the LDAP bind or the RADIUS Access-Request): from here ONLY this app’s ephemeral credential is accepted, never the real password.',
      consumerNote:
        'The ephemeral consumer “{consumer}” (an LDAP consumer or a RADIUS client, per the backend) is generated in Connectors › Applications with this source and the released groups: it lives and dies with the legacy app, never edited or deleted alone.',
      notes: 'Notes (optional)',
      notesHelp: 'Free-form operator notes.',
      sudoHint:
        'Writing a legacy app asks for your password. The vhost is not applied here: nginx preview shows it, Apply nginx writes and reloads it.',
      createError: 'Could not create the legacy app.',
      updateError: 'Could not update the legacy app.'
    },
    form: {
      nameShape:
        'The name must be a lowercase host label: a-z, 0-9 and hyphens (not first or last), at most 63 characters.',
      publicHostRequired: 'The public host is required.',
      upstreamShape: 'The upstream URL must be http(s)://host[:port], with no path, query or credentials.',
      backendSourceRequired:
        'The backend source (CIDR or IP address) is required: it is how the ephemeral backend recognises the app.',
      headersIdentity:
        'The identity headers (X-Forwarded-User, X-Forwarded-Preferred-Username, X-Forwarded-Groups, X-Imago-*, Authorization) are always the proxy’s: not set here.',
      headersMalformed: 'Every header toward the app is written as Name: value, one per line.',
      ttlRange: 'The one-shot credential TTL ranges from 10 to 3600 seconds.',
      passwordLengthRange: 'The password length ranges from 16 to 64 characters.',
      injectPagesRequired: 'The injected prefill needs at least one page.'
    },
    delete: {
      title: 'Delete legacy app',
      hint: 'You are about to delete the legacy app “{name}”. Its generated ephemeral consumer “{consumer}”, every live proxy session and every ephemeral credential of the app go with it. The vhost on disk goes at the next Apply nginx.'
    },
    nginx: {
      previewTitle: 'nginx preview — {name}',
      previewHelp:
        'This app’s server block as the apply would write it (<name>.conf): the export for whoever applies nginx by hand. A disabled app renders with the comment saying it would not be applied.',
      copy: 'Copy',
      copied: 'Copied',
      apply: 'Apply nginx',
      loadError: 'Could not render the vhost.',
      applyTitle: 'Apply nginx — {name}',
      applyTitleSite: 'Apply nginx (whole site)',
      applyHint:
        'The site is ONE set of files (the Imago vhost plus one per enabled app) and is always written whole into IMAGO_4LEGACY_NGINX_DIR, then validated with nginx -t and reloaded with nginx -s reload through the sudoers rule.',
      applyConfirm: 'Apply',
      notConfigured:
        'The nginx driver is off on this server (IMAGO_4LEGACY_NGINX_MODE=off, the default): nothing was written. Set IMAGO_4LEGACY_NGINX_MODE=apply (write, validate, reload) or dry-run (write only) in httpd’s environment, or apply the previewed vhost by hand.',
      applyFailed:
        'nginx refused the rendered site: the previous files were restored, nothing was reloaded. The stderr of nginx -t is below.',
      stderr: 'nginx stderr',
      appliedReloaded: 'Site written into {dir} ({count} files), validated with nginx -t and reloaded (mode {mode}).',
      appliedWritten: 'Site written into {dir} ({count} files) without validation or reload (mode {mode}).',
      files: 'Files: {files}',
      apps: 'Apps rendered: {apps}'
    }
  },
  language: {
    tooltip: 'Language'
  },
  fidoMetadata: {
    help: 'The FIDO Metadata Service (MDS3) BLOB and the local statements: one model per AAGUID, with its latest status and certification level. The FIDO2 › Metadata policy decides who registers and who logs in.',
    disabled: 'MDS not configured',
    failing: 'Last refresh failed',
    healthy: 'Up to date',
    pending: 'Never fetched',
    url: 'BLOB url',
    blobNo: 'BLOB number',
    nextUpdate: 'Next update (from the BLOB)',
    fetchedAt: 'Fetched at',
    entries: 'Models from the BLOB',
    localEntries: 'Local statements',
    refreshSeconds: 'Periodic refresh',
    manualOnly: 'Manual only',
    everySeconds: 'Every {seconds} s',
    refresh: 'Refresh now',
    refreshConfirm: 'Fetch and verify the BLOB now: the models are replaced and the attestation verifier re-fed. A BLOB that cannot be read or verified is refused and the current models stay.',
    refreshed: 'BLOB no. {blobNo} loaded: {entries} models.',
    refreshError: 'Refresh failed.',
    loadError: 'Cannot read the FIDO metadata.',
    entriesTitle: 'Authenticator models',
    entriesHelp: 'By description; the search matches the description.',
    search: 'Search by description',
    only: 'Show',
    onlyAll: 'All',
    onlyCertified: 'Certified only',
    onlyCompromised: 'Compromised or revoked only',
    noEntries: 'No models.',
    description: 'Model',
    aaguid: 'AAGUID',
    status: 'Status',
    level: 'Level',
    source: 'Source',
    sources: { mds: 'MDS BLOB', local: 'Local statement' }
  },
  enumeration: {
    help: 'Each realm may have ONE enumeration source: the realm’s directory or an LDAP directory of its own, a SCIM 2.0 provider, or the native realm’s users. A sync yields the census (subjects and groups with stable ids) that feeds the SCIM export and the per-group grants and policies.',
    realm: 'Realm',
    noRealms: 'No enumerable realm.',
    realmNotEnumerable: 'The requested realm cannot be enumerated: showing the first available.',
    loadError: 'The enumeration could not be read.',
    sourceTitle: 'Enumeration source',
    candidateTitle: 'Candidate source (in the shadow)',
    noCandidate: 'No candidate: configure a replacement source here, sync it in the shadow, then cut over.',
    target: 'Source to edit',
    targetHelp: 'The SERVING source feeds the census; the CANDIDATE syncs in the shadow until you decide the cutover.',
    targetServing: 'Serving',
    targetCandidate: 'Candidate',
    targetCandidateNew: 'Candidate (new)',
    cutover: 'Cut over',
    noSource: 'No source configured: pick the kind, set the interval and create it.',
    sourceState: 'State: {state}',
    served: 'census served',
    notServed: 'no run served',
    kind: 'Kind',
    kindLocked: 'A source’s kind does not change: retire it and create a new one.',
    kindHelp: {
      ldap: 'Reads the realm’s directory (the realm’s URLs, TLS, bind, base DN) in RFC 2696 pages.',
      local: 'Reads the native realm’s users from Imago’s tables (never the break-glass).',
      scim: 'Reads an upstream SCIM 2.0 provider with a bearer of its own, in RFC 7644 pages; on any external realm (enumeration is orthogonal to authentication).'
    },
    identity: {
      title: 'Authentication identity',
      help: 'The space where the realm’s authentication-side ids are comparable over time: every verified login binds its id to a stable Subject (the census one when the source allows it).',
      none: 'No active space: it is born at the first login or the next boot.',
      active: 'Active space: {authority} — {links} identities linked, since {when}',
      candidate: 'The authority changed ({authority}): a candidate namespace is open with {links} links. Logins resolve only through the re-keyed links (no new Subject) until you cut over.',
      rekey: 'Re-key',
      cutover: 'Cut over the namespace',
      rekeyReport: 'Re-keyed: {entries} entries read, {mapped} with both ids, {copied} links copied, {orphans} orphan links, {sources} source links moved.',
      unresolved: 'Legacy rows without a Subject: {enrollments} MFA enrollments, {credentials} FIDO2 credentials, {biocards} BioCards (they resolve at the owner’s next login).'
    },
    interval: 'Interval (s)',
    intervalHelp: '0 = manual syncs only; otherwise at least 60 seconds.',
    ldap: {
      userFilter: 'User filter',
      groupFilter: 'Group filter',
      userNameAttribute: 'userName attribute',
      groupNameAttribute: 'Group name attribute',
      memberAttribute: 'Member attribute',
      pageSize: 'Entries per page',
      maxSubjects: 'Maximum subjects',
      connectionMode: 'Connection',
      connectionModeHelp: 'The realm’s directory (its URLs, TLS, bind, base DN) or a directory of the source’s OWN.',
      ownRequired: 'On a non-LDAP realm the source reads its own directory: enumeration is orthogonal to authentication.',
      realmConnection: 'The realm’s connection',
      ownConnection: 'A directory of its own',
      urls: 'Directory URLs',
      urlsHelp: 'One per line (ldaps://host:636 or ldap://host), tried in order.',
      baseDn: 'Base DN',
      bindDn: 'Service bind DN',
      bindDnHelp: 'Empty = anonymous bind.',
      bindPassword: 'Bind password',
      bindPasswordHelp: 'Required on creation with a bind DN; sealed in the keystore, never shown.',
      bindPasswordKeep: 'Empty = the sealed one stays.',
      tls: 'TLS',
      tlsVerify: 'ldaps:// with certificate verification',
      tlsStartTls: 'STARTTLS on ldap://',
      tlsNoVerify: 'Without certificate verification (labs only)',
      caCertificate: 'Directory CA (PEM, empty = system store)',
      uuidAttribute: 'Immutable id attribute',
      displayNameAttribute: 'Display name attribute',
      mailAttribute: 'E-mail attribute',
      phoneAttribute: 'Phone attribute'
    },
    scim: {
      baseUrl: 'Provider base URL',
      baseUrlHelp: 'https://host/scim/v2 — http only on a loopback host; no query, no credentials in the URL. Redirects are never followed.',
      bearerToken: 'Bearer token',
      bearerTokenHelp: 'Required on creation; sealed in the keystore, never shown.',
      bearerTokenKeep: 'Empty = the sealed one stays.',
      verifyCertificate: 'Verify the certificate',
      verifyYes: 'Yes (recommended)',
      verifyNo: 'No — loopback and labs only',
      caCertificate: 'Provider CA (PEM)',
      caCertificateHelp: 'Empty = the system store.',
      userFilter: 'SCIM user filter',
      groupFilter: 'SCIM group filter',
      pageSize: 'Resources per page (10–500)',
      maxSubjects: 'Maximum subjects',
      timeoutSeconds: 'Per-request timeout (s)',
      pagination: 'Pagination',
      paginationHelp: 'Auto = the RFC 9865 cursor when the provider announces it, the index otherwise.',
      paginationAuto: 'Automatic',
      paginationIndex: 'Index (startIndex/count)',
      paginationCursor: 'Cursor (RFC 9865)',
      mappingHelp: 'Attribute mapping: for each field the SCIM paths tried in order, separated by commas (e.g. name.formatted, emails[type eq "work"].value, urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:employeeNumber). Empty = the fixed mapping.',
      mapping: { userName: 'User name', displayName: 'Display name', mail: 'E-mail', phone: 'Phone', active: 'Active', groupDisplayName: 'Group name' },
      mappingDefault: { userName: 'userName', displayName: 'displayName, name.formatted, userName', mail: 'emails[primary eq true].value, emails.value', phone: 'phoneNumbers.value', active: 'active', groupDisplayName: 'displayName' }
    },
    create: 'Create the source',
    save: 'Save',
    test: 'Probe',
    sync: 'Sync now',
    retire: 'Retire',
    probeOk: 'The source answers',
    probeKo: 'The source does NOT answer',
    runsTitle: 'Syncs',
    runsHelp: 'The latest syncs, newest first; “active” = the run the census is served from.',
    noRuns: 'No sync yet.',
    run: { started: 'Started', state: 'State', trigger: 'Trigger', subjects: 'Subjects', groups: 'Groups', error: 'Error', active: 'active' },
    subjectsTitle: 'Subjects',
    subjectsHelp: 'The served census: stable Imago id, userName, name, state, groups (transitive too).',
    subjectSearch: 'Search by userName',
    noSubjects: 'No subject in the census.',
    subject: { userName: 'userName', displayName: 'Name', active: 'Active', groups: 'Groups', yes: 'Yes', no: 'No' },
    groupsTitle: 'Groups',
    groupsHelp: 'The census groups with their direct members (subjects and nested groups).',
    noGroups: 'No group in the census.',
    group: { name: 'Name', members: 'Members' },
    dialog: {
      save: { title: 'Save the enumeration source', hint: 'The source of realm “{realm}” is sudo-protected; a sync starts right after.', confirm: 'Save' },
      retire: { title: 'Retire the source', hint: 'The census of realm “{realm}” stops being served (the history and the stable ids stay). Confirm with your password.', confirm: 'Retire' },
      test: { title: 'Probe the source', hint: 'Connection, bind and one page per filter on realm “{realm}”: no full enumeration.', confirm: 'Probe' },
      sync: { title: 'Sync now', hint: 'Starts a full sync of realm “{realm}”; the run proceeds in the server.', confirm: 'Sync' },
      rekey: { title: 'Re-key the identities', hint: 'Reads the directory of realm “{realm}” with the old and the new attribute and, in one transaction, copies the links into the candidate namespace, moves the enumeration sources’ links and rewrites the MFA/FIDO2/BioCard/duress rows. Repeatable.', confirm: 'Re-key' },
      nsCutover: { title: 'Cut over the identity namespace', hint: 'The candidate namespace of realm “{realm}” becomes the one every login resolves through; the active one retires with its links as history. Without a re-keying (a candidate with no link) the identities start afresh.', confirm: 'Cut over' }
    }
  },
  scimExport: {
    title: 'SCIM export of “{app}”',
    help: 'The app reads the users and groups of ONE realm’s census over SCIM 2.0 (/scim/v2, read-only) with its own token. The scope is the whole realm or the chosen groups (and their users); no group chosen = zero users.',
    loadError: 'The SCIM export could not be read.',
    realm: 'Realm',
    realmHelp: 'A realm with a served enumeration source.',
    realmLocked: 'An export’s realm does not change: retire the export and create a new one.',
    realmRequired: 'Pick the realm.',
    enabled: 'Export enabled',
    includeAll: 'Every user and group of the realm',
    censusOk: 'census served',
    censusMissing: 'no served census: sync the realm’s enumeration',
    generation: 'Served snapshot: {users} users ({inactive} deactivated), {groups} groups — built on {when}',
    noGeneration: 'No served snapshot: the protocol answers 503 until the realm’s census is promoted',
    groupsHelp: 'The census groups to publish (with the users they hold, nested too).',
    noGroups: 'No group in the chosen realm’s census.',
    emptyScope: 'No group chosen: the export publishes zero users until you pick one or enable “every”.',
    create: 'Create the export',
    save: 'Save',
    retire: 'Retire the export',
    tokensTitle: 'Tokens',
    tokensHelp: 'The bearer the SCIM client presents: shown ONCE at creation, kept as a hash only; several live tokens for rotation.',
    mintedOnce: 'Token created: copy it now, it will not be shown again.',
    tokenLabel: 'Label',
    tokenLifetime: 'Lifetime (days)',
    mint: 'Create token',
    revoke: 'Revoke',
    noTokens: 'No token.',
    token: { label: 'Label', created: 'Created', expires: 'Expires', lastUsed: 'Last used', state: 'State', live: 'live', revoked: 'revoked' },
    dialog: {
      save: { title: 'Save the SCIM export', hint: 'The export of “{app}” is sudo-protected.', confirm: 'Save' },
      retire: { title: 'Retire the SCIM export', hint: 'The export of “{app}” and ALL its tokens are deleted: the SCIM client stops reading.', confirm: 'Retire' },
      mint: { title: 'Create a token', hint: 'The token of “{app}”’s export is shown exactly once.', confirm: 'Create' },
      revoke: { title: 'Revoke the token', hint: 'Token “{label}” stops working at once; its metadata stays in the register.' }
    }
  },
  approvals: {
    title: 'Access requests',
    help: 'An application asked to sign in on your behalf (OIDC CIBA): approve only if you started it and the message matches what the application shows.',
    pending: 'pending',
    bindingMessage: 'Verification message',
    noBindingMessage: 'The application attached no verification message: approve only if you are using that application right now.',
    details: 'Scope: {scope} · asked at {requestedAt} · expires at {expiresAt}',
    approve: 'Approve',
    deny: 'Deny',
    approveTitle: 'Approve the access request',
    approveHint: 'By approving, “{app}” gets an access on your behalf (message: {bindingMessage}). Confirm with your password.',
    denyTitle: 'Deny the access request',
    denyHint: '“{app}” will receive a refusal (message: {bindingMessage}). Confirm with your password.',
    loadError: 'The access requests could not be read.'
  },
  windowsCode: {
    title: 'Windows sign-in (RDP)',
    subheader:
      'A one-time code to type in the Imago Credential Provider’s code field inside the RDP session, in place of your second factor: a FIDO2 key does not reach the remote desktop.',
    none: 'No computer: you need a computer with the Imago Credential Provider enabled and a pairing that admits you.',
    computer: 'Computer',
    mint: 'Get a code',
    sudoTitle: 'Windows sign-in code',
    sudoHint: 'The code is valid five minutes for computer “{computer}” and is shown once. Confirm your password.',
    issuedHint: 'For “{computer}”, expires in {seconds} s. Type it in the Credential Provider’s code field.',
    loadError: 'Could not read your computers.',
    mintError: 'Could not mint the code.'
  },
  duress: {
    pageHelp:
      'A user may enrol a secret suffix to type after their own password when coerced: the login succeeds like any other and the alarm fires out of their sight (a duressLogin line in the register, SIEM).',
    enabled: 'Anti-coercion enabled',
    enabledHelp:
      'Off: the suffix is never consulted and the profile card is hidden. On: users may enrol the suffix from their profile and every password login recognises it.',
    revokeSessions: 'A login under coercion closes the previous sessions',
    revokeSessionsHelp:
      'The sessions opened before the coercion are closed at once; the session of the coerced login stays alive, because the coercer must see success.',
    saveTitle: 'Save the anti-coercion settings',
    saveHint: 'Confirm with your password: the policy change is sudo-protected.',
    saved: 'Settings saved.',
    eventsTitle: 'Alarms',
    eventsHelp: 'The coerced logins recorded (event duressLogin), newest first. The user is never told.',
    noEvents: 'No alarm recorded.',
    colAt: 'When',
    colChannel: 'Channel',
    colUser: 'User',
    colRealm: 'Realm',
    colIp: 'Address',
    loadError: 'The anti-coercion settings could not be loaded.',
    card: {
      title: 'Anti-coercion suffix',
      help:
        'A secret suffix to type after your password if you are forced to log in: the login succeeds normally and the alarm fires without the coercer noticing.',
      enrolled: 'Suffix enrolled ({length} characters). It is never shown.',
      notEnrolled: 'No suffix enrolled.',
      set: 'Enrol the suffix',
      replace: 'Replace the suffix',
      clear: 'Remove',
      setTitle: 'Enrol the anti-coercion suffix',
      setHint:
        '4 to 20 characters, no spaces, not one repeated character. Pick one that none of your real passwords ends with: a password ending with the suffix is judged on the remaining part and refused.',
      suffixLabel: 'Suffix',
      suffixAgain: 'Repeat the suffix',
      sudoPassword: 'Confirm with your password',
      mismatch: 'The two suffixes differ.',
      setError: 'The suffix could not be enrolled.',
      clearTitle: 'Remove the anti-coercion suffix',
      clearHint: 'Without a suffix a coerced login can no longer be signalled.',
      loadError: 'The anti-coercion suffix status could not be read.'
    }
  },
  about: {
    subheader: 'The node serving this console.',
    version: 'Platform version',
    contractVersion: 'API contract version',
    nodeName: 'Node name',
    startedAt: 'Process started at',
    checksTitle: 'Health checks',
    checks: { database: 'Database', redis: 'Redis', realms: 'Realms' },
    ok: 'ok',
    failing: 'failing',
    loadError: 'Could not read the node information.'
  },
  events: {
    subheader:
      'The register restricted to what the system and its administrators did: configuration, nginx apply, syncs, FIDO metadata, EAP CA, SCIM tokens, probes. User logins and factors stay in “Audit”.'
  }
}

export type Dictionary = typeof itIT

export const dictionaries: Record<Lang, Dictionary> = {
  'it-IT': itIT,
  'en-US': enUS
}
